// Existing User types
export interface User {
  id: string;
  email: string;
  name: string;
  company?: string;
  role: UserRole;
  /** Profile image (Firebase Storage URL); shown instead of initials across the app when set. */
  photoURL?: string | null;
  /** When role is `supplier`, links to `supplierOrgs` for RFQ portal scoping. */
  supplierOrgId?: string;
  /**
   * User-level timber / CNC work flags for awarded Production comments.
   * Not an employee designation — `parseEmployeeDesignations` drops unknown values.
   */
  supplierDesignations?: SupplierWorkFlag[];
  /**
   * Vendor portal access: Owner sees quotes/totals; Employee is ops-only (files, comments, schedule).
   * Missing on legacy supplier accounts is treated as Owner.
   */
  supplierPortalRole?: SupplierPortalRole;
  /**
   * Firebase UIDs of executives whose calendars this user may manage via Executive Calendar (EA workflow).
   * Mirror for UX; authorization also uses `calendarDelegates` on each executive's user doc (server-side).
   */
  calendarManagesExecutives?: string[];
  /** From `users/{uid}` in Firestore — used before CRM user list hydrates (e.g. EA approvals). */
  designations?: EmployeeDesignation[];
  /** Executive day briefing config (CEO/COO profile, viewers). */
  executiveBriefing?: ExecutiveBriefingConfig;
  /** Ids of `releaseAnnouncements` docs this user has dismissed (drives the "What's New" popup). */
  seenReleaseIds?: string[];
  /** Seasonal celebration event ids this user has already seen (e.g. `july4-2026`). */
  seenCelebrationIds?: string[];
  /** Ids of `employeeAwardGrants` docs this user has dismissed (achievement unlock popup). */
  seenAwardGrantIds?: string[];
  /** crmFeedback ids whose squash thank-you popup this user has dismissed. */
  seenBugSquashIds?: string[];
  /** When true, user logs hours via Contractor Timesheets (not W-2 implied project time). */
  is1099Contractor?: boolean;
  /** Immediate manager — written by the org chart reporting rules; Purchasing supervisor gate reads it. */
  reportsToUserId?: string;
  /** Org chart layer — Executive / Managerial / Staff (legacy office|shop read as general). */
  orgLevel?: import('./purchasing').OrgLevel;
  /** Org chart group — Sales / Office / Shop (legacy back_office|operations normalized on read). */
  orgDepartment?: import('./purchasing').OrgDepartment;
  /** Admin grant for the internal team chat at /team-chat. Missing means off. */
  teamChatEnabled?: boolean;
  /** Play a short chime for new unread bells. Missing means on. */
  bellSound?: boolean;
  /** Selected bell chime id. */
  bellSoundId?: string;
}

export type UserRole =
  | 'employee'
  /** W-2 shop floor — scoped CRM (Project Hub, Operations, Purchasing). */
  | 'employee_shop'
  /** W-2 office production — same scoped CRM as shop on day one. */
  | 'employee_office'
  | 'client'
  | 'admin'
  | 'designer'
  /** External project architect (partner). Internal design staff use `designer`. */
  | 'architect'
  | 'engineer'
  | 'staff'
  | 'supplier'
  | 'cpa'
  /** Invited trade-briefing subcontractor. Not CRM workspace staff. */
  | 'subcontractor';

// Notification preferences for external users (architects/engineers)
export type NotificationFrequency = 'realtime' | 'hourly' | 'daily';

export interface NotificationPrefs {
  slackDm: boolean;
  email: boolean;
  slackUserId?: string;
  /**
   * When true, auto-match must not overwrite `slackUserId`
   * (set by People → Team manual override).
   */
  slackUserIdManual?: boolean;
  /** SMS delivery preference (stored per user; backend may enable incrementally). */
  sms: boolean;
  /** Browser/mobile push (stored per user). */
  push: boolean;
  notificationFrequency: NotificationFrequency;
  onTaskAssigned: boolean;
  onTaskStatusChanged: boolean;
  onActionRequired: boolean;
  onComment: boolean;
  onMention: boolean;
  onTaskCompleted: boolean;
  /** Weekday digest when card charges need categorization. */
  onCardExpenseDigest: boolean;
  /** Personal Project Hub task reminders. */
  onTaskReminder: boolean;
  /** Play a short chime when a new unread bell arrives. */
  bellSound: boolean;
  /** Which synthesized chime to play. */
  bellSoundId: string;
}

export const DEFAULT_NOTIFICATION_PREFS: NotificationPrefs = {
  slackDm: false,
  email: true,
  sms: true,
  push: false,
  notificationFrequency: 'realtime',
  onTaskAssigned: true,
  onTaskStatusChanged: true,
  onActionRequired: true,
  onComment: true,
  onMention: true,
  onTaskCompleted: true,
  onCardExpenseDigest: true,
  onTaskReminder: true,
  bellSound: true,
  bellSoundId: 'desk',
};

// CRM User Profile (stored in Firestore)
export type EmployeeDesignation =
  | 'project_manager'
  | 'estimator'
  | 'executive_assistant'
  | 'sales_rep'
  | 'cfo'
  | 'coo'
  | 'office_manager';


/** User-level supplier work flags (do not store on `designations`). */
export type SupplierWorkFlag = 'timber_supplier' | 'cnc_supplier';

/** Vendor portal access within `role: 'supplier'`. */
export type SupplierPortalRole = 'owner' | 'employee';

export interface CRMUser {
  uid: string;
  email: string;
  displayName: string;
  role: UserRole;
  authProvider: string;
  photoURL?: string | null;
  /** Present when role is `supplier` (RFQ portal). */
  supplierOrgId?: string;
  /** Denormalized timberProjects ids for external architect portal hydration (Admin SDK only). */
  architectHubProjectIds?: string[];
  /** User-level timber / CNC flags for Production comment access after award. */
  supplierDesignations?: SupplierWorkFlag[];
  /** Vendor portal access level. Missing on legacy accounts is treated as Owner. */
  supplierPortalRole?: SupplierPortalRole;
  createdAt: Date;
  lastLogin: Date;
  isActive: boolean;
  company?: string;
  phone?: string;
  /** Free-text location shown on comment hover cards, e.g. "New York, USA". */
  location?: string;
  /** IANA timezone, e.g. "America/New_York". */
  timezone?: string;
  title?: string;
  notes?: string;
  /**
   * Designer $/hr for proposal joinery. Lives in `userCompensation/{uid}`, not on
   * `users/{uid}` (that collection is readable by all staff). Only hydrated for
   * finance viewers.
   */
  designerHourlyRate?: number;
  designations?: EmployeeDesignation[];
  notificationPrefs?: NotificationPrefs;
  /** UIDs allowed to manage this user’s calendar via Executive calendar (stored on executive profile). */
  calendarDelegates?: string[];
  /** Optional mirror: executive UIDs this EA helps (UX / menu); server still checks calendarDelegates. */
  calendarManagesExecutives?: string[];
  /** Executive Suite email funnel config for this user's mailbox (owner). See ExecInboxMailboxConfig. */
  execInbox?: ExecInboxMailboxConfig;
  /** Executive day briefing — profile and viewers (no inbox required for COO). */
  executiveBriefing?: ExecutiveBriefingConfig;
  /** Ids of `releaseAnnouncements` docs this user has dismissed (drives the "What's New" popup). */
  seenReleaseIds?: string[];
  /** Seasonal celebration event ids this user has already seen (e.g. `july4-2026`). */
  seenCelebrationIds?: string[];
  /** Ids of `employeeAwardGrants` docs this user has dismissed (achievement unlock popup). */
  seenAwardGrantIds?: string[];
  /** crmFeedback ids whose squash thank-you popup this user has dismissed. */
  seenBugSquashIds?: string[];
  /** When true, shown under Settings → Team → Test Users (cutover / QA logins). */
  isTestUser?: boolean;
  /** Shared machine login (Job Trailer, Cadwork PC) — not a person on the org chart. */
  isWorkstation?: boolean;
  /**
   * When true, this users doc is an AI agent identity (e.g. Caesar) and is eligible
   * for Operations Hub assignment / checklist dropdowns.
   */
  isAgent?: boolean;
  /** When true, user logs hours via Contractor Timesheets (not W-2 implied project time). */
  is1099Contractor?: boolean;
  /** Admin grant for the internal team chat at /team-chat. Missing means off. */
  teamChatEnabled?: boolean;
  /** Immediate manager — written by the org chart reporting rules; Purchasing supervisor gate reads it. */
  reportsToUserId?: string;
  /** Org chart layer — Executive / Managerial / Staff (legacy office|shop read as general). */
  orgLevel?: import('./purchasing').OrgLevel;
  /** Org chart group — Sales / Office / Shop (legacy back_office|operations normalized on read). */
  orgDepartment?: import('./purchasing').OrgDepartment;
}

/** One highlighted feature inside a release announcement ("What's New" popup). */
export interface ReleaseFeature {
  title: string;
  description: string;
  /** Optional explicit bullet items (preferred for display). */
  bullets?: string[];
  /** lucide-react icon name (e.g. "Search", "MessageSquare"); falls back to a sparkle icon. */
  icon?: string;
  /** Optional small screenshot (Firebase Storage or public URL) shown instead of the icon. */
  imageUrl?: string;
  /** Chip label for the affected CRM area, e.g. "Operations Hub". */
  area?: string;
}

/** Doc in `releaseAnnouncements` — published per deploy via scripts/publish-release-announcement.js. */
export interface ReleaseAnnouncement {
  id: string;
  /** Displayed on the version badge, e.g. "2.4.0". */
  version: string;
  title: string;
  /** Flip false to retract a release without deleting it. */
  active: boolean;
  publishedAt: Date;
  /** Roles that should see the popup; omit roles (e.g. architect/engineer/supplier) to exclude them. */
  audienceRoles: UserRole[];
  /** Optional designation filter (e.g. executive assistants) — matched in addition to roles. */
  audienceDesignations?: EmployeeDesignation[];
  /** Specific people (Firebase UIDs) who should see the popup in addition to roles/designations. */
  audienceUserIds?: string[];
  features: ReleaseFeature[];
}

/** Pending release generated on deploy — admin publishes from CRM UI. */
export type ReleaseDraftStatus = 'pending' | 'published' | 'skipped';

export interface ReleaseAnnouncementDraft {
  id: string;
  version: string;
  title: string;
  audienceRoles: UserRole[];
  audienceDesignations?: EmployeeDesignation[];
  audienceUserIds?: string[];
  features: ReleaseFeature[];
  status: ReleaseDraftStatus;
  createdAt: Date;
  deployCommit?: string;
  publishedReleaseId?: string;
}

/** Admin-managed award template in `employeeAwardDefinitions`. */
export interface EmployeeAwardDefinition {
  id?: string;
  name: string;
  description: string;
  iconUrl: string;
  iconStoragePath: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
}

/** Per-user award grant in `employeeAwardGrants`. */
export interface EmployeeAwardGrant {
  id?: string;
  userId: string;
  awardDefinitionId: string;
  /** Denormalized from definition at grant time. */
  awardName: string;
  awardDescription: string;
  iconUrl: string;
  grantedAt: Date;
  grantedBy: string;
  grantNote?: string;
}

/** Lead-scoped todo (Leads hub side panel) — stored in `leadTodos` */
export interface LeadTodo {
  id?: string;
  leadId: string;
  leadName: string;
  title: string;
  assigneeUserId: string;
  assigneeName?: string;
  createdByUserId: string;
  createdByName: string;
  completed: boolean;
  createdAt: Date;
  updatedAt: Date;
  completedAt?: Date | null;
}

/** Settings doc `settings/leadTodoAssignees` — who may receive lead todo assignments */
export interface LeadTodoAssigneesSettings {
  userIds: string[];
  updatedAt?: Date;
}

/** settings/arch_engineer_work_queue — per-user project priority (first id = highest). */
export interface ArchEngineerWorkQueueSettings {
  /** UID → ordered project document ids for that person’s hub work queue. */
  queuesByUserId?: Record<string, string[]>;
  /** @deprecated Single global list; use queuesByUserId. */
  projectIds?: string[];
  updatedAt?: Date;
  updatedByUserId?: string;
}

// Lead Comments
export interface CommentAttachment {
  id: string;
  name: string;
  url: string;
  type: string; // MIME type
  size: number; // bytes
}

export interface CommentMention {
  userId: string;
  userName: string;
  startIndex: number;
  endIndex: number;
}

/** Slack-style: emoji key → user IDs who added that reaction (each user at most once per emoji). */
export type CommentReactionsMap = Record<string, string[]>;

export interface LeadComment {
  id?: string;
  leadId: string;
  userId: string;
  userName: string;
  userInitials: string;
  content: string;
  mentions?: CommentMention[];
  attachments?: CommentAttachment[];
  /** Client-only reaction counts; updates do not trigger Slack/email (Firestore onCreate only). */
  reactions?: CommentReactionsMap;
  createdAt: Date;
  updatedAt?: Date;
}

/** Lead document (PDF) in dedicated documents area */
export interface ExecEmailSource {
  mailboxUserId: string;
  threadId: string;
  messageId: string;
  attachmentId: string;
  filename: string;
  pushedBy: string;
  pushedAt: Date;
}

/** Who authored a document annotation / markup / synopsis. */
export type AnnotationSource = 'user' | 'biddingAgent' | 'marketingAgent' | 'projectFilesAgent';

/** Metadata attached to an annotation written by the bidding agent, linking it back to a deliverable. */
export interface BiddingAgentAnnotationMeta {
  analysisId: string;
  reportSection?: 'qualification' | 'timber' | 'cost' | 'proposal';
  timberItemId?: string;
  confidence?: number;
  extractionLabel?: string;
}

/** Short agent-written summary of a document, surfaced on the Agent Documents Hub card. */
export interface LeadDocumentAgentSynopsis {
  text: string;
  analysisId: string;
  updatedAt: Date;
  source: 'biddingAgent' | 'marketingAgent';
  pageRefs?: Array<{ pageNumber: number; annotationId?: string }>;
}

export interface LeadDocument {
  id?: string;
  leadId: string;
  dealId?: string;
  name: string;
  url: string;
  storagePath?: string;  // For deletion
  mimeType?: string;     // e.g. 'application/pdf', 'image/jpeg'
  size: number;
  uploadedAt: Date;
  uploadedBy: string;
  emailSource?: ExecEmailSource;
  /** Agent-written 2–3 line summary of this document (bidding/marketing agent). */
  agentSynopsis?: LeadDocumentAgentSynopsis;
}

/** PDF annotation (comment pin) */
export interface PdfAnnotationReply {
  userId: string;
  userName: string;
  content: string;
  mentions?: CommentMention[];
  reactions?: CommentReactionsMap;
  createdAt: Date;
}

export interface PdfAnnotation {
  id?: string;
  documentId: string;
  documentUrl: string;
  leadId: string;
  taskId?: string;
  projectId?: string;
  pageNumber: number;
  x: number;
  y: number;
  content: string;
  userId: string;
  userName: string;
  mentions?: CommentMention[];
  replies?: PdfAnnotationReply[];
  /** User IDs who have viewed this thread (pins, popover, or lead feed); resets when a new reply is added */
  readBy?: string[];
  /** Emoji reactions on the root PDF comment (updates do not trigger Slack/email). */
  reactions?: CommentReactionsMap;
  /** Author class — `user` (default) or an agent. Agent pins are styled differently and skip Slack. */
  source?: AnnotationSource;
  /**
   * Drawing-pin visibility. Missing = working notes (LCTF only).
   * `architect` = decision pin the assigned architect can see and reply to.
   */
  audience?: 'internal' | 'architect';
  /** When `source` is an agent, links this pin back to the analysis deliverable. */
  agentMeta?: BiddingAgentAnnotationMeta;
  /** Cropped page screenshot around the pin — used in email notifications. */
  contextPreviewUrl?: string;
  /**
   * When set, this pin was created during a quiet PM plan-review session
   * (`planReviews/{planReviewId}`). Per-comment notifications are suppressed until the review completes.
   */
  planReviewId?: string;
  createdAt: Date;
}

/** Quiet PM plan-review session on a project drawing PDF — `planReviews/{id}`. */
export type PlanReviewStatus = 'active' | 'completed' | 'cancelled';

export interface PlanReview {
  id?: string;
  projectId: string;
  /** `projectFiles` doc id for the drawing version under review. */
  documentId: string;
  documentUrl: string;
  streamId?: string;
  fileName: string;
  status: PlanReviewStatus;
  startedAt: Date;
  startedBy: string;
  startedByName: string;
  completedAt?: Date;
  completedBy?: string;
  completedByName?: string;
  cancelledAt?: Date;
  cancelledBy?: string;
  cancelledByName?: string;
  /** Snapshot of annotation ids at complete (immutable slice). */
  annotationIds?: string[];
  markupStrokeCount?: number;
  /** Optional PM cover note included in the digest (plain-text fallback). */
  summaryNote?: string;
  /** Sanitized rich HTML cover note for email digest. */
  summaryNoteHtml?: string;
  /** Recipients chosen when completing the review. */
  notifyUserIds?: string[];
  createdAt: Date;
  updatedAt: Date;
}

/** Links a PDF measurement to a timber take-off item for a proposal structure. */
export interface PdfTakeoffLink {
  id?: string;
  documentId: string;
  documentUrl: string;
  dealId: string;
  leadId: string;
  measurementId: string;
  pageNumber: number;
  structureId: string;
  structureName: string;
  timberItemId?: string;
  name: string;
  species: string;
  grade: string;
  dimensions: string;
  quantity: number;
  moistureContent?: '' | 'green' | 'kiln_dried';
  measuredLength?: number;
  measuredLengthUnit?: 'ft' | 'in';
  unitCost?: number;
  notes?: string;
  userId: string;
  userName: string;
  createdAt: Date;
  updatedAt?: Date;
}

/** Draft structure created during PDF take-off before a proposal exists. */
export interface PdfTakeoffStructure {
  id?: string;
  dealId: string;
  leadId: string;
  /** Matches future ProposalNodeSet.id when pushed to proposal. */
  name: string;
  userId: string;
  userName: string;
  createdAt: Date;
  updatedAt?: Date;
}

/** PDF take-off pin — links a structure's timber take-off to a location on the PDF */
export interface PdfTakeoffPin {
  id?: string;
  documentId: string;
  documentUrl: string;
  leadId: string;
  dealId: string;
  structureId: string;
  structureName: string;
  pageNumber: number;
  x: number;
  y: number;
  /** Manual drawing organizer label — kept even without linked take-off items on the page. */
  organizational?: boolean;
  createdAt: Date;
}

/** Freehand / highlighter strokes on PDFs and images (normalized coordinates, same origin as comment pins). */
export type DocumentMarkupFreehandTool = 'pen' | 'highlighter';
export type DocumentEngineeringTool = 'callout' | 'line' | 'zigzag' | 'stamp';
export type DocumentMarkupShapeTool = 'cloud' | 'typewriter';
export type DocumentMarkupTool =
  | DocumentMarkupFreehandTool
  | DocumentEngineeringTool
  | DocumentMarkupShapeTool
  | 'eraser';

export interface DocumentMarkupPoint {
  /** 0–100, matches annotation pin coordinate space */
  x: number;
  y: number;
}

export interface DocumentMarkupStroke {
  id?: string;
  documentId: string;
  documentUrl: string;
  leadId: string;
  pageNumber: number;
  tool: DocumentMarkupTool;
  color: string;
  strokeWidth: number;
  points: DocumentMarkupPoint[];
  userId: string;
  userName: string;
  projectId?: string;
  /** Callout / stamp label. */
  text?: string;
  /** Callout box fill (e.g. cyan). */
  fill?: string;
  /** Callout text color; defaults to `color`. */
  textColor?: string;
  /** Fastener catalog id when `tool === 'stamp'`. */
  stampId?: string;
  /** Stamp size multiplier (1 = medium). */
  stampScale?: number;
  /** Stamp rotation in degrees. */
  stampRotation?: number;
  /** Author class — `user` (default) or an agent. */
  source?: AnnotationSource;
  /** When `source` is an agent, links this stroke back to the analysis deliverable. */
  agentMeta?: BiddingAgentAnnotationMeta;
  createdAt: Date;
}

/** Tabular cell/row/column color highlight for the CSV viewer (csvCellHighlights collection). */
export type CsvHighlightScope = 'cell' | 'row' | 'column';

export interface CsvCellHighlight {
  id?: string;
  documentId: string;
  documentUrl: string;
  leadId: string;
  projectId?: string;
  scope: CsvHighlightScope;
  /** 0-based row index in the parsed body (excluding header). Required for `cell` and `row` scopes. */
  row?: number;
  /** 0-based column index. Required for `cell` and `column` scopes. */
  col?: number;
  /** Header label for the column (used to recover anchor if column order changes between sessions). */
  colKey?: string;
  color: string;
  userId: string;
  userName: string;
  /** Author class — `user` (default) or an agent. */
  source?: AnnotationSource;
  /** When `source` is an agent, links this highlight back to the analysis deliverable. */
  agentMeta?: BiddingAgentAnnotationMeta;
  createdAt: Date;
}

/** Per-page calibration that translates PDF user units → real-world distance. */
export interface PdfMeasurementCalibration {
  id?: string;
  documentId: string;
  documentUrl: string;
  pageNumber: number;
  /** Source of the conversion factor. */
  mode: 'standard' | 'two-point';
  /** Set when `mode === 'standard'`. */
  standardScaleId?: string;
  /** Set when `mode === 'two-point'` — the two clicks the user used. */
  pointA?: { x: number; y: number };
  pointB?: { x: number; y: number };
  /** The real-world distance the user typed for the two-point clicks. */
  realDistance?: number;
  /** Unit of `realDistance` as the user entered it. */
  realDistanceUnit?: 'in' | 'ft' | 'mm' | 'cm' | 'm';
  /** Multiplier from PDF user-units → `baseUnit`. Resolved at save. */
  realPerPdfPoint: number;
  unitSystem: 'imperial' | 'metric';
  /** The natural unit for storage (e.g. ft for arch, mm for metric). */
  baseUnit: 'in' | 'ft' | 'mm' | 'cm' | 'm';
  /** How distances should be rendered in labels. */
  displayUnit: 'in' | 'ft' | 'ft-in' | 'mm' | 'cm' | 'm';
  /** Snapshot of page natural size at calibration time (safety check). */
  pageNaturalWidth: number;
  pageNaturalHeight: number;
  leadId?: string;
  projectId?: string;
  userId?: string;
  userName?: string;
  createdAt: Date;
  updatedAt?: Date;
}

/** A single measurement drawn by a user against a PDF page. */
export interface PdfMeasurement {
  id?: string;
  documentId: string;
  documentUrl: string;
  pageNumber: number;
  kind: 'linear' | 'polyline' | 'area' | 'angle';
  /** Normalized (0-100). 2 for linear, 2-N for polyline, 3-N for area, exactly 3 for angle ([rayA, vertex, rayB]). */
  points: { x: number; y: number }[];
  /** Optional label override; default is the formatted distance/area/angle. */
  label?: string;
  leadId?: string;
  projectId?: string;
  userId: string;
  userName: string;
  createdAt: Date;
  updatedAt?: Date;
}

/** Portal-scoped PDF annotation (client portal only — separate from staff `pdfAnnotations`). */
export interface PortalPdfAnnotation {
  id?: string;
  portalId: string;
  dealId: string;
  releaseId: string;
  bundleId?: string;
  documentId: string;
  documentUrl: string;
  pageNumber: number;
  x: number;
  y: number;
  content: string;
  authorUid: string;
  authorName: string;
  mentions?: CommentMention[];
  replies?: PdfAnnotationReply[];
  reactions?: CommentReactionsMap;
  createdAt: Date;
}

/** Portal-scoped freehand markup stroke. */
export interface PortalDocumentMarkupStroke {
  id?: string;
  portalId: string;
  dealId: string;
  releaseId: string;
  bundleId?: string;
  documentId: string;
  documentUrl: string;
  pageNumber: number;
  tool: DocumentMarkupTool;
  color: string;
  strokeWidth: number;
  points: DocumentMarkupPoint[];
  authorUid: string;
  authorName: string;
  createdAt: Date;
}

/** Portal-scoped measurement calibration. */
export interface PortalPdfMeasurementCalibration {
  id?: string;
  portalId: string;
  dealId: string;
  releaseId: string;
  documentId: string;
  documentUrl: string;
  pageNumber: number;
  mode: 'standard' | 'two-point' | 'auto-detected';
  standardScaleId?: string;
  pointA?: { x: number; y: number };
  pointB?: { x: number; y: number };
  realDistance?: number;
  realDistanceUnit?: 'in' | 'ft' | 'mm' | 'cm' | 'm';
  realPerPdfPoint: number;
  unitSystem: 'imperial' | 'metric';
  baseUnit: 'in' | 'ft' | 'mm' | 'cm' | 'm';
  displayUnit: 'in' | 'ft' | 'ft-in' | 'mm' | 'cm' | 'm';
  pageNaturalWidth: number;
  pageNaturalHeight: number;
  authorUid?: string;
  authorName?: string;
  createdAt: Date;
  updatedAt?: Date;
}

/** Portal-scoped measurement on a released PDF. */
export interface PortalPdfMeasurement {
  id?: string;
  portalId: string;
  dealId: string;
  releaseId: string;
  documentId: string;
  documentUrl: string;
  pageNumber: number;
  kind: 'linear' | 'polyline' | 'area' | 'angle';
  points: { x: number; y: number }[];
  label?: string;
  authorUid: string;
  authorName: string;
  createdAt: Date;
  updatedAt?: Date;
}

/** Time tracking entry for lead/proposal work (explicit or implied) */
export type LeadTimeEntryType = 'explicit' | 'implied';

export interface LeadTimeEntry {
  id?: string;
  leadId: string;
  userId: string;
  userName: string;
  type: LeadTimeEntryType;
  minutes: number;
  startTime: Date;
  endTime: Date;
  note?: string;
  proposalId?: string;
  dealId?: string;
  createdAt: Date;
}

// Map-related types
export interface Coordinates {
  lat: number;
  lng: number;
}

// Default coordinates for leads without a location (50 miles off coast of Norfolk, VA)
export const DEFAULT_LEAD_COORDINATES: Coordinates = {
  lat: 36.85,  // Norfolk latitude
  lng: -75.4   // 50 miles off the coast into the Atlantic
};

export interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

// Metrics Lite Types
export interface MetricsLiteVisitor {
  visitor_id: number;
  lead_info: {
    name?: string;
    email?: string;
    ip_address?: string;
    user_agent?: string;
    screen_width?: number;
    screen_height?: number;
    language?: string;
    timezone?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    referrer?: string;
    first_seen?: string;
    last_seen?: string;
  };
  statistics: {
    total_events?: number;
    pageviews?: number;
    goal_completions?: number;
    total_revenue?: number;
  };
  goals?: Array<{
    goal_name: string;
    event_type: string;
    completions: number;
    revenue: number;
  }>;
  recent_events?: Array<{
    id: number;
    type: string;
    is_goal: boolean;
    url: string;
    value: number;
    created_at: string;
  }>;
}

// UTM attribution data for a single touch point
export interface UtmTouch {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  timestamp?: Date;
  url?: string;
  touchType: 'first' | 'mid' | 'last';
}

export interface TrackingData {
  ipAddress?: string;
  userAgent?: string;
  screenWidth?: number;
  screenHeight?: number;
  language?: string;
  timezone?: string;
  // Legacy single UTM fields (for backward compatibility - uses first touch)
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  referrer?: string;
  firstSeen?: Date;
  lastSeen?: Date;
  // Multi-touch UTM attribution
  firstTouchUtm?: UtmTouch;
  lastTouchUtm?: UtmTouch;
  allUtmTouches?: UtmTouch[];
}

export interface VisitorStatistics {
  totalEvents?: number;
  pageviews?: number;
  goalCompletions?: number;
  totalRevenue?: number;
}

export interface VisitorGoal {
  goalName: string;
  eventType: string;
  completions: number;
  revenue: number;
}

export interface VisitorEvent {
  id: number;
  type: string;
  isGoal: boolean;
  url: string;
  value: number;
  createdAt: Date;
}

// ═══ Lead Enrichment Types ═══

export type PropertyType = 'sfr' | 'multi_family' | 'commercial' | 'vacant_land' | 'agricultural' | 'unknown';
export type OwnershipStatus = 'owner_occupied' | 'absentee' | 'investor' | 'unknown';
export type FloodZone = 'none' | 'moderate' | 'high' | 'coastal_high' | 'unknown';
export type EnrichmentConfidence = 'confirmed' | 'inferred' | 'estimated';
export type BuyerScoreBand = 'blazing' | 'hot' | 'warm' | 'cool' | 'cold';

export interface PropertyEnrichment {
  propertyType: PropertyType;
  lotSizeSqft?: number;
  lotSizeAcres?: number;
  buildingSqft?: number;
  bedrooms?: number;
  bathrooms?: number;
  yearBuilt?: number;
  constructionType?: string;
  assessedValue?: number;
  estimatedMarketValue?: number;
  zoningClassification?: string;
  landUseCode?: string;
  hasExistingStructure: boolean;
}

export interface OwnershipData {
  ownerName?: string;
  ownershipStatus: OwnershipStatus;
  purchaseDate?: Date;
  purchasePrice?: number;
  yearsOfOwnership?: number;
  estimatedEquity?: number;
  recentRefinance?: boolean;
  otherPropertiesOwned?: number;
  ownsVacantLand?: boolean;
  vacantLandDetails?: {
    address?: string;
    acreage?: number;
    purchaseDate?: Date;
    estimatedValue?: number;
  };
}

export interface BuildSiteAssessment {
  floodZone: FloodZone;
  aerialImageUrl?: string;
  soilType?: string;
  soilBearingCapacity?: string;
  topography?: string;
  parcelBoundaryGeoJson?: string;
  treeCover?: 'heavy' | 'moderate' | 'light' | 'clear';
  roadAccess?: boolean;
  utilityAccess?: boolean;
  isRural: boolean;
  isScenicArea: boolean;
}

export interface PermitRecord {
  permitNumber: string;
  type: string;
  description?: string;
  value?: number;
  dateIssued: Date;
  status: 'issued' | 'in_progress' | 'completed' | 'expired';
  contractor?: string;
}

export interface PermitData {
  permits: PermitRecord[];
  recentPermitCount: number;
  nearbyPermitActivity: number;
  hasBuildingPermitNoStructure: boolean;
  hasSepticOrWellPermit: boolean;
  lastPermitDate?: Date;
}

export interface TimberFrameBuyerSignal {
  id: string;
  label: string;
  category: 'readiness' | 'life_chapter' | 'aesthetic' | 'financial' | 'competitive';
  confidence: EnrichmentConfidence;
  description: string;
  scoreImpact: number;
}

export interface PortfolioEngagement {
  projectName: string;
  projectUrl?: string;
  viewCount: number;
  totalDwellTimeSeconds: number;
  lastViewed: Date;
}

export interface BehavioralIntent {
  portfolioProjectsViewed: PortfolioEngagement[];
  floorPlanPageViews: number;
  floorPlanDwellTimeSeconds: number;
  processPageViews: number;
  pricingPageViews: number;
  testimonialsPageViews: number;
  blogPostsRead: string[];
  returnVisitCount: number;
  daysBetweenFirstAndLastVisit: number;
  totalPagesViewed: number;
  averageSessionDepthPages: number;
  contactPageVisitsBeforeSubmission: number;
  galleryVsProcessRatio: number;
}

export interface HistoricalPattern {
  similarLeadCloseRate: number;
  sampleSize: number;
  averageDaysToClose: number;
  averageDealValue: number;
  commonLostReasons: string[];
  geographicWinRate?: number;
  sourceQualityRating: 'high' | 'medium' | 'low';
  portfolioCorrelation?: {
    projectName: string;
    closeRateMultiplier: number;
  };
}

export interface InferredFinancialCapacity {
  primaryHomeValue?: number;
  landValue?: number;
  otherRealEstateValue?: number;
  combinedAssets: number;
  hasMortgageOfRecord: boolean;
  capacityLevel: 'strong' | 'moderate' | 'unknown' | 'limited';
  cashBuyerIndicators: boolean;
  constructionLoanMentioned: boolean;
  flags: string[];
}

export interface TalkingPoint {
  text: string;
  category: 'property' | 'engagement' | 'financial' | 'historical' | 'action';
  priority: number;
  icon?: string;
}

export interface BuyerIntelligenceScore {
  total: number;
  band: BuyerScoreBand;
  breakdown: {
    buildSiteQualification: number;
    projectReadiness: number;
    lifeChapterFinancial: number;
    behavioralEngagement: number;
  };
  penalties: number;
  penaltyReasons: string[];
}

export interface AVMData {
  value?: number;
  high?: number;
  low?: number;
  confidence?: number;
  source?: string;
}

export interface SaleHistoryRecord {
  date?: string;
  amount?: number;
  type?: string;
}

export interface PersonEnrichment {
  fullName?: string;
  jobTitle?: string;
  jobCompany?: string;
  jobCompanySize?: string;
  jobCompanyIndustry?: string;
  inferredSalary?: string;
  linkedinUrl?: string;
  linkedinConnections?: number;
  facebookUrl?: string;
  twitterUrl?: string;
  homeAddress?: {
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    geo?: string;
  };
  ageRange?: string;
  gender?: string;
  education?: Array<{
    school?: string;
    degree?: string;
    major?: string;
  }>;
  likelihood?: number;
  socialProfiles?: Array<{
    network: string;
    url: string;
    username?: string;
  }>;
  isBusinessOwner?: boolean;
  businessName?: string;
  newsMentions?: number;
}

export interface LeadEnrichment {
  enrichedAt: Date;
  lastUpdated: Date;
  source: 'demo' | 'live' | 'partial';

  person?: PersonEnrichment;
  property?: PropertyEnrichment;
  ownership?: OwnershipData;
  buildSite?: BuildSiteAssessment;
  permits?: PermitData;
  avm?: AVMData;
  saleHistory?: SaleHistoryRecord[];

  buyerSignals: TimberFrameBuyerSignal[];
  behavioral?: BehavioralIntent;
  historicalPattern?: HistoricalPattern;
  financialCapacity?: InferredFinancialCapacity;

  score: BuyerIntelligenceScore;
  talkingPoints: TalkingPoint[];

  phoneIntel?: {
    isValid?: boolean;
    lineType?: string;
    carrier?: string;
    isPrepaid?: boolean;
    isCommercial?: boolean;
  };

  neighborhoodData?: {
    medianHouseholdIncome: number | null;
    medianHomeValue: number | null;
    bachelorsDegreeOrHigherPct: number | null;
    censusTract: string | null;
    source: string;
  };

  dataSources?: string[];
  _attomError?: string;
  _pdlError?: string;
  _fullContactError?: string;
  _trestleError?: string;
}

// ═══════════════════════════════════════════════════════════════════
// Lead Bidding Analysis (Mac-mini bidding agent)
// ═══════════════════════════════════════════════════════════════════

export type BiddingAnalysisStatus = 'complete' | 'partial' | 'failed';
export type BiddingQualificationRecommendation = 'go' | 'caution' | 'no_go';

export interface BiddingTimberItem {
  id: string;
  name: string;
  quantity: number;
  width: number;
  height: number;
  length: string;
  material?: string;
  confidence?: number;
  source?: string;
  /** Document this item was extracted from (lead document id or url). */
  documentId?: string;
  /** Page within the document where the item was found. */
  pageNumber?: number;
  /** Agent annotation pin that marks this item on the plan (`pdfAnnotations` id). */
  annotationId?: string;
  /** Agent markup strokes (highlights) tied to this item (`documentMarkup` ids). */
  markupStrokeIds?: string[];
  /** Proposal-shaped fields — same keys as TimberListItem / proposeTakeoffProposals. */
  species?: string;
  grade?: string;
  /** `HxWxL` with L in feet, e.g. `8x8x12`. Prefer this over width/height/length. */
  dimensions?: string;
  moistureContent?: '' | 'green' | 'kiln_dried';
  structureId?: string;
  structureName?: string;
  boardFeet?: number;
  unitCost?: number;
  total?: number;
}

/** One proposal node-set of timber lines. timberList uses TimberListItem keys. */
export interface BiddingTakeoffStructure {
  id: string;
  name: string;
  timberList: TimberListItem[];
}

export interface BiddingQualification {
  recommendation: BiddingQualificationRecommendation;
  budgetFitScore: number; // 0–100
  feasibilityFlags: string[];
  riskNotes: string[];
  summary?: string;
}

export interface BiddingCostBucketEstimate {
  design: number;
  production: number;
  materials: number;
  freight: number;
  install: number;
  overhead: number;
}

export interface BiddingCostEstimate {
  buckets: BiddingCostBucketEstimate;
  grandTotalLow: number;
  grandTotalHigh: number;
  assumptions: string[];
}

export interface BiddingProposalPrep {
  suggestedStructureCount: number;
  kitRecommendation: 'kit' | 'custom' | 'mixed' | 'unknown';
  nodeSetHints: string[];
  nextSteps: string[];
  dealId?: string;
}

export interface BiddingTimberExtraction {
  items: BiddingTimberItem[];
  documentCount: number;
  summary?: string;
  /** Proposal-ready take-off grouped like proposalBuilders.nodeSets[].materials.timberList. */
  structures?: BiddingTakeoffStructure[];
}

export interface LeadBiddingAnalysis {
  analyzedAt: Date;
  lastUpdated: Date;
  status: BiddingAnalysisStatus;
  qualification?: BiddingQualification;
  costEstimate?: BiddingCostEstimate;
  timberExtraction?: BiddingTimberExtraction;
  proposalPrep?: BiddingProposalPrep;
  dataSources?: string[];
  agentNotes?: string;
}

export interface LeadBiddingAnalysisRequestOptions {
  includeDocuments?: boolean;
  dealId?: string;
}

// ── Proposal Review (Mode 2) — agent critique stored at enhancedDeals/{dealId}.proposalReview ──

export type ProposalReviewStatus = 'complete' | 'partial' | 'failed';
export type ProposalRecipientRole = 'gc' | 'owner' | 'homeowner' | 'architect' | 'other';

export interface ProposalReviewRecipient {
  name?: string;
  role?: ProposalRecipientRole | string;
  /** 0–100 win likelihood for this recipient. */
  winProbability?: number;
  /** 0–1 model confidence in the win-prob. */
  confidence?: number;
  drivers?: string[];
  relationshipNotes?: string[];
}

export interface ProposalReviewEconomics {
  projectType?: 'commercial' | 'residential';
  buildingFootprintSqft?: number;
  pricePerSqft?: number;
  ownerCapexLow?: number;
  ownerCapexHigh?: number;
  /** Our subcontract value. */
  lctfPortionUsd?: number;
  roiNotes?: string;
  summary?: string;
}

export interface ProposalReviewImproveOdds {
  emailWording?: string[];
  provisions?: string[];
  positioning?: string[];
}

export interface ProposalReviewByNode {
  structure?: string;
  member?: string;
  quantity?: number;
  costUsd?: number;
  priceUsd?: number;
  marginPct?: number;
  riskNotes?: string;
}

export interface ProposalReviewInternalNumbers {
  proposalTotalUsd?: number;
  marginPct?: number;
  /** Year revenue impact of a win. */
  revenueIfWinUsd?: number;
  cashflowIfLossNotes?: string;
  byNode?: ProposalReviewByNode[];
}

export interface ProposalReviewConformance {
  deliversOnAsk?: boolean;
  gaps?: string[];
  extras?: string[];
}

export interface ProposalReview {
  status: ProposalReviewStatus;
  reviewedAt?: Date;
  lastUpdated?: Date;
  dealId?: string;
  leadId?: string;
  recipient?: ProposalReviewRecipient;
  economics?: ProposalReviewEconomics;
  improveOdds?: ProposalReviewImproveOdds;
  presentationQuestions?: string[];
  internalNumbers?: ProposalReviewInternalNumbers;
  conformance?: ProposalReviewConformance;
  summary?: string;
  agentNotes?: string;
  dataSources?: string[];
  analysisCost?: Record<string, unknown>;
}

/** Canonical take-off label captured for agent training. */
export interface BiddingTrainingLabel {
  name?: string;
  species?: string;
  grade?: string;
  dimensions?: string;
  quantity?: number;
  moistureContent?: string;
  measuredLength?: number;
  measuredLengthUnit?: 'ft' | 'in';
  unitCost?: number;
}

export type BiddingTrainingExampleType = 'humanTakeoff' | 'staffConfirmation' | 'staffCorrection';

/** A take-off training example (human label) in `biddingTrainingExamples`. */
export interface BiddingTrainingExample {
  id?: string;
  leadId: string;
  dealId?: string | null;
  documentId?: string | null;
  documentUrl?: string | null;
  pageNumber?: number | null;
  measurementId?: string | null;
  structureId?: string | null;
  structureName?: string | null;
  exampleType: BiddingTrainingExampleType;
  /** Confirmed/corrected label values. */
  label: BiddingTrainingLabel;
  /** Snapshot of the agent's extracted item when this is a confirmation/correction. */
  agentItem?: BiddingTimberItem;
  analysisId?: string;
  /** `biddingAgent` (export) or `crmStaff` (confirm/correct). */
  source: 'biddingAgent' | 'crmStaff';
  status?: 'confirmed' | 'corrected' | 'rejected';
  userId?: string;
  userName?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export type BiddingTakeoffProposalStatus = 'pending' | 'approved' | 'rejected';

/** Proposed take-off link payload (mirrors PdfTakeoffLink editable fields). */
export interface BiddingProposedLink {
  name?: string;
  species?: string;
  grade?: string;
  dimensions?: string;
  quantity?: number;
  moistureContent?: '' | 'green' | 'kiln_dried';
  measuredLength?: number;
  measuredLengthUnit?: 'ft' | 'in';
  unitCost?: number;
  structureId?: string;
  structureName?: string;
  measurementId?: string;
  timberItemId?: string;
  notes?: string;
}

/** Agent-proposed draft take-off link awaiting staff approval (`biddingTakeoffProposals`). */
export interface BiddingTakeoffProposal {
  id?: string;
  leadId: string;
  dealId?: string | null;
  documentId: string;
  documentUrl?: string;
  pageNumber: number;
  analysisId: string;
  status: BiddingTakeoffProposalStatus;
  proposedLink: BiddingProposedLink;
  confidence?: number;
  rationale?: string;
  annotationId?: string;
  source: 'biddingAgent';
  createdByUserId?: string;
  createdByUserName?: string;
  createdAt?: Date;
  reviewedAt?: Date;
  reviewedBy?: string;
  createdLinkId?: string;
}

// CRM Data Models
export interface Lead {
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  projectType: string;
  budget: number;
  timeline: string;
  location: string;
  address?: Address;
  coordinates?: Coordinates;
  geocoded?: boolean;
  source: LeadSource;
  status: LeadStatus;
  notes?: string;
  archived?: boolean; // Whether the lead is archived
  labels?: string[]; // Array of label strings (e.g., ["VIP", "Hot Lead", "Follow Up"])
  createdAt: Date;
  updatedAt: Date;
  assignedTo?: string; // employee ID
  addedBy?: string; // employee ID who added the lead
  convertedToContact?: string; // contact ID if converted
  convertedToDeal?: string; // deal ID if converted (first/primary deal)
  dealIds?: string[]; // all deal IDs for multi-project support
  // Metrics Lite integration
  metricsLiteId?: string; // visitor_id from Metrics Lite (UUID token from website or numeric id)
  /** Resolved integer Metrics Lite visitor id when metricsLiteId is a client UUID token. */
  metricsLiteNumericId?: number;
  // Communication tracking
  hasUnreadMessage?: boolean; // Whether there's an unread SMS/iMessage
  // Pipedrive reconciliation fields
  pipedrivePersonId?: string; // Person - ID from Pipedrive for reconciliation
  pipedriveLabel?: string; // Person - Label
  pipedriveOwner?: string; // Person - Owner
  pipedriveOpenDeals?: number; // Person - Open deals
  pipedriveWonDeals?: number; // Person - Won deals
  pipedriveLostDeals?: number; // Person - Lost deals
  pipedriveClosedDeals?: number; // Person - Closed deals
  pipedriveTotalActivities?: number; // Person - Total activities
  pipedriveDoneActivities?: number; // Person - Done activities
  pipedriveActivitiesToDo?: number; // Person - Activities to do
  pipedriveEmailCount?: number; // Person - Email messages count
  pipedriveLastEmailReceived?: Date; // Person - Last email received
  pipedriveLastEmailSent?: Date; // Person - Last email sent
  pipedriveNextActivityDate?: Date; // Person - Next activity date
  pipedriveLastActivityDate?: Date; // Person - Last activity date
  pipedriveCreated?: Date; // Person - Person created
  pipedriveUpdated?: Date; // Person - Update time
  pipedriveVisibleTo?: string; // Person - Visible to
  pipedriveProfilePicture?: string; // Person - Profile picture URL
  pipedriveLabels?: string; // Person - Labels
  trackingData?: TrackingData;
  statistics?: VisitorStatistics;
  goals?: VisitorGoal[];
  recentEvents?: VisitorEvent[];
  // Meta Ads (Facebook/Instagram) marketing data
  metaAdCampaign?: string;   // campaign_name from Meta Ads
  metaAdSet?: string;        // adset_name from Meta Ads
  metaAdName?: string;       // ad_name from Meta Ads

  // Bidding integration
  bidsCount?: number;        // Count of bids for this lead
  latestBidId?: string;      // ID of most recent bid

  // Lead enrichment
  enrichment?: LeadEnrichment;

  // Bidding agent analysis
  biddingAnalysis?: LeadBiddingAnalysis;
}

export interface Contact {
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  address?: Address;
  coordinates?: Coordinates;
  notes?: string;
  tags?: string[];
  createdAt: Date;
  updatedAt: Date;
  createdFrom?: string; // lead ID if converted from lead
  totalValue?: number; // sum of all deal values
  lastContact?: Date;
  nextFollowUp?: Date;
}

export interface Deal {
  id?: string;
  title: string;
  contactId: string;
  contactName: string;
  value: number;
  stage: DealStage;
  probability: number;
  expectedCloseDate?: Date;
  actualCloseDate?: Date;
  description?: string;
  products?: DealProduct[];
  address?: Address; // Store address for geocoding
  coordinates?: Coordinates;
  createdAt: Date;
  updatedAt: Date;
  assignedTo?: string; // employee ID
  createdFrom?: string; // lead ID if converted from lead
  proposalId?: string; // linked proposal
}

export interface Proposal {
  id?: string;
  dealId?: string;
  leadId?: string;
  contactId: string;
  title: string;
  content?: ProposalContent;
  sections?: ProposalSection[]; // New flexible sections format
  status: ProposalStatus;
  validUntil?: Date;
  totalAmount: number;
  createdAt: Date;
  updatedAt: Date;
  sentAt?: Date;
  viewedAt?: Date;
  acceptedAt?: Date;
  rejectedAt?: Date;
  rejectionReason?: string;
}



// Supporting types
export interface Address {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

export interface DealProduct {
  name: string;
  description?: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
}

export interface ProposalContent {
  introduction: string;
  scope: string;
  materials: MaterialItem[];
  timeline: string;
  pricing: PricingItem[];
  terms: string;
  customSections?: CustomSection[];
}

export interface ProposalSection {
  type: 'header' | 'pricing' | 'terms' | 'custom';
  title: string;
  content: string;
  order: number;
  items?: PricingItem[];
}

export interface MaterialItem {
  name: string;
  description?: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  totalPrice: number;
}

export interface PricingItem {
  name: string;
  description?: string;
  amount?: number;
  quantity?: number;
  unit?: string;
  unitPrice?: number;
  totalPrice?: number;
}

export interface CustomSection {
  title: string;
  content: string;
}

// Message types
export interface SMSMessage {
  id?: string;
  from: string;
  to: string;
  body: string;
  direction: 'inbound' | 'outbound';
  status: 'sent' | 'delivered' | 'failed' | 'pending';
  relatedTo: {
    entityType: 'lead' | 'contact';
    entityId: string;
  };
  createdAt?: Date;
}

export interface EmailMessage {
  id?: string;
  from: string;
  to: string[] | string;
  subject: string;
  body: string;
  htmlBody?: string;
  direction: 'inbound' | 'outbound';
  status: 'sent' | 'delivered' | 'failed' | 'pending';
  relatedTo: {
    entityType: 'lead' | 'contact' | 'deal';
    entityId: string;
  };
  createdAt?: Date;
}

// Enums
export type LeadSource = 'web-form' | 'facebook-form' | 'instagram-form' | 'ebook' | 'instagram-follow' | 'facebook-follow' | 'b2b-visit' | 'cold-call' | 'call-in' | 'referral' | 'unspecified';
export type LeadStatus = 'new' | 'contacted' | 'qualified' | 'lost' | 'nurture' | 'out-of-scope';
export type DealStage = 'prospecting' | 'qualification' | 'proposal' | 'negotiation' | 'closed-won' | 'closed-lost';
export type ProposalStatus = 'draft' | 'sent' | 'viewed' | 'accepted' | 'rejected' | 'expired';

// ==========================================
// ENGAGEMENT LETTER SYSTEM
// ==========================================

export interface EngagementLetter {
  id?: string;
  leadId: string;
  dealId?: string;                 // Optional link to deal
  slug: string;                    // URL slug: lastname-projectname
  accessPin: string;               // 4-digit PIN for public access
  letterType?: 'concept' | 'retainer'; // Type of engagement letter

  // Letter content
  title: string;
  clientName: string;
  clientEmail: string;
  projectDescription: string;
  designFee: number;               // Variable fee per letter
  deliverables: string[];          // e.g., ["3D model", "PDF drawings"]
  termsAndConditions: string;

  // Status tracking
  status: EngagementLetterStatus;
  createdAt: Date;
  updatedAt: Date;
  sentAt?: Date;
  viewedAt?: Date;
  signedAt?: Date;
  paidAt?: Date;

  // Signature data
  signature?: EngagementSignature;

  // Payment tracking
  paymentId?: string;              // QuickBooks payment ID when paid

  // Signed PDF
  signedPdfUrl?: string;           // Firebase Storage URL for signed PDF
}

export interface EngagementSignature {
  dataUrl: string;                 // Base64 signature image
  signedBy: string;                // Name as typed
  signedAt: Date;
  ipAddress?: string;
}

export type EngagementLetterStatus =
  | 'draft'
  | 'sent'
  | 'viewed'
  | 'signed'
  | 'paid'
  | 'expired'
  | 'voided';

// Default deliverables for engagement letters
export const DEFAULT_ENGAGEMENT_DELIVERABLES = [
  '3D timber frame model (SketchUp format)',
  'PDF drawings with dimensions',
  'Materials list with specifications',
];

// Predefined engagement letter types for quick creation
export interface EngagementLetterType {
  id: 'concept' | 'retainer';
  name: string;
  fee: number;
  deliverables: string[];
  description: string;
  paymentLinkName: string; // Name to match against quickbooksPaymentLinks
}

export const ENGAGEMENT_LETTER_TYPES: EngagementLetterType[] = [
  {
    id: 'concept',
    name: 'Concept Payment',
    fee: 500,
    deliverables: [
      'Initial 3D concept model',
      'Basic layout visualization',
      'Preliminary dimensions',
    ],
    description: 'Initial design concept for your timber frame project',
    paymentLinkName: 'Concept', // Matches payment link name containing "Concept"
  },
  {
    id: 'retainer',
    name: 'Concept Design Fee',
    fee: 1600,
    deliverables: [
      'Interactive 3D model viewable in any web browser',
      '2D draft plans with views of the structure and elevations',
    ],
    description: 'One-time concept design fee for your custom timber frame — 2D plans included',
    paymentLinkName: 'Retainer', // Matches payment link name containing "Retainer"
  },
];

// Default terms template for concept payments
export const DEFAULT_ENGAGEMENT_TERMS = `By signing this engagement letter, you agree to:

1. Pay the design fee for 3D timber frame modeling services
2. Receive deliverables within 10-14 business days of payment
3. This engagement is for design services only, not construction
4. Design revisions beyond the initial scope may incur additional fees
5. All designs remain the intellectual property of LC Timber Frames until final payment

This agreement constitutes a binding contract between you and LC Timber Frames for the design services described above.`;

// Terms for $1600 concept design fee engagement letters (matches the proposal format)
export const RETAINER_ENGAGEMENT_TERMS = `This Agreement, together with the following Attachments (if any), constitute the entire Agreement between the parties with respect to the subject matter hereof, and as of the date this Agreement is executed by both Parties, shall supersede any previous agreements or understandings, written or oral, between the Parties. All modifications to the applicable Compensation arrangement shall be in writing and signed by both Parties and shall not supersede the terms of this Agreement.

The Agreement shall commence on the date of the last signature.

The Concept Design Fee for our services is $1,600. This is a one-time fee that includes 2D draft plans. The timeline for this project shall be 2 weeks for initial deliverables.

Cancellation Terms - The design fee is required to secure your project in our timeline. This fee is non-refundable once the design process begins.`;

// Terms and conditions bullet points for retainer letters (legacy engagement page)
export const RETAINER_TERMS_CONDITIONS = [
  {
    title: 'Agreement',
    content: 'This Agreement constitutes the entire agreement between the parties and supersedes any previous agreements. The Agreement commences on the date of the last signature.',
  },
  {
    title: 'Design Fee & Timeline',
    content: 'The Concept Design Fee is a one-time payment of $1,600. 2D draft plans are included. The timeline for this project is 2 weeks for initial deliverables.',
  },
  {
    title: 'Cancellation',
    content: 'The design fee is required to secure your project in our timeline. This fee is non-refundable once the design process begins.',
  },
  {
    title: 'Revisions',
    content: 'One revision to the 3D model and 2D plans is included. Additional revisions are available at our standard rates.',
  },
  {
    title: 'Ownership',
    content: 'Preliminary concepts remain our IP until 100% payment; full deliverables transfer upon final payment. LCTF reserves the right to use design elements developed for this project in future projects.',
  },
  {
    title: 'Termination',
    content: "Either party may terminate with 14 days' written notice. Fees due for work completed to date.",
  },
  {
    title: 'Governing Law',
    content: 'This agreement is governed by the laws of the State of Tennessee. Any disputes will be resolved in the courts of Williamson County, Tennessee.',
  },
  {
    title: 'Confidentiality',
    content: 'We protect your project details as outlined in our standard NDA (attached if needed).',
  },
];

// Terms for the inline design fee checkout modal
export const DESIGN_FEE_TERMS = [
  {
    title: 'Agreement',
    content: 'This Agreement constitutes the entire agreement between the parties and supersedes any previous agreements. By completing payment, you authorize Leiper\'s Creek Timber Frames to begin work on your selected design services.',
  },
  {
    title: 'Design Services',
    content: 'Your design package includes the services selected above. The timeline for initial deliverables is approximately 2 weeks from payment. One round of revisions is included; additional revisions are available at our standard rates.',
  },
  {
    title: 'Payment & Cancellation',
    content: 'The design fee is required to secure your project in our schedule. This fee is non-refundable once the design process begins.',
  },
  {
    title: 'Intellectual Property',
    content: 'Preliminary concepts remain the property of LCTF until full payment is received. Upon payment, deliverables transfer to the client. LCTF reserves the right to use design elements in future projects.',
  },
  {
    title: 'Termination',
    content: "Either party may terminate with 14 days' written notice. Fees are due for any work completed to date.",
  },
  {
    title: 'Governing Law',
    content: 'This agreement is governed by the laws of the State of Tennessee. Any disputes will be resolved in the courts of Williamson County, Tennessee.',
  },
];

// ==========================================
// PIPELINE SYSTEM
// ==========================================

export interface Pipeline {
  id?: string;
  name: string;
  stages: PipelineStage[];
  isDefault: boolean;
  color: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface PipelineStage {
  id: string;
  name: string;
  order: number;
  probability: number;
  rottingDays?: number;
}

// Default pipeline configurations
export const DEFAULT_PIPELINES: Omit<Pipeline, 'id' | 'createdAt' | 'updatedAt'>[] = [
  {
    name: 'Timber Frames',
    stages: [
      { id: 'qualified', name: 'Qualified', order: 1, probability: 20, rottingDays: 7 },
      { id: 'design', name: 'Design', order: 2, probability: 55, rottingDays: 30 },
      { id: 'estimated', name: 'Estimated', order: 3, probability: 85, rottingDays: 30 },
    ],
    isDefault: true,
    color: '#2C3E2F',
  },
];

// Design sub-pipeline stages (tracked within the "Design" main stage)
export type DesignSubStage =
  | 'payment-requested'
  | 'in-design'
  | 'design-review'
  | 'revisions'
  | 'design-complete';

export type DesignType = 'concept' | 'retainer';

export const DESIGN_SUB_STAGES: { id: DesignSubStage; name: string; order: number }[] = [
  { id: 'payment-requested', name: 'Payment Requested', order: 1 },
  { id: 'in-design', name: 'In Design', order: 2 },
  { id: 'design-review', name: 'Design Review', order: 3 },
  { id: 'revisions', name: 'Revisions', order: 4 },
  { id: 'design-complete', name: 'Design Complete', order: 5 },
];

// ==========================================
// ENHANCED DEAL SYSTEM
// ==========================================

export type DealOutcome = 'won' | 'lost' | 'active';

export interface EnhancedDeal {
  id?: string;
  pipelineId: string;
  stageId: string;
  leadId?: string;
  contactId: string;
  contactName: string;
  company?: string;                 // Company from lead

  // ── Canonical project naming (single source of truth) ─────────────
  // `projectNumber` is immutable once assigned (format `YY-NNN`, e.g. `26-014`).
  // `clientLabel` + `descriptor` are editable; together with the number they
  // compose the canonical display name `"{projectNumber} {clientLabel} {descriptor}"`.
  // The legacy `title` field is now a mirror written by the rename trigger so
  // older read paths keep working during deprecation.
  projectNumber?: string;
  clientLabel?: string;
  descriptor?: string;

  title: string;
  value: number;
  outcome: DealOutcome;
  probability: number;

  // Stage Timing Analytics
  stageHistory: StageTransition[];
  currentStageEnteredAt: Date;

  // Visual
  colorBar: string;

  // Demographics for correlation analysis
  projectType: string;
  location: string;
  address?: Address;
  coordinates?: Coordinates;
  budget: number;
  source: LeadSource;

  // Timeline (e.g., "Spring 2025", "Fall 2026")
  timeline?: string;

  // Custom fields
  customFields?: CustomFieldValue[];

  // Metadata
  assignedTo?: string;
  createdAt: Date;
  updatedAt: Date;
  closedAt?: Date;
  /**
   * First time this deal became won. Write-once — never cleared by reactivate,
   * never overwritten on re-sign. Sales Activity prefers this over closedAt.
   */
  firstWonAt?: Date;
  lostReason?: string;

  // Expected close
  expectedCloseDate?: Date;

  // Notes
  description?: string;

  // Bidding integration
  bidsCount?: number;        // Count of bids for this deal
  latestBidId?: string;      // ID of most recent bid

  /** Pre-send bidding-agent critique of this deal's proposal (Mode 2). Written via upsertProposalReview. */
  proposalReview?: ProposalReview;

  // Design tracking (when stageId === 'design')
  designSubStage?: DesignSubStage;
  designType?: DesignType;           // 'concept' ($500) or 'retainer' ($1600)
  designFee?: number;                // Actual fee amount
  designNotes?: string;
  designPaymentDate?: Date;
  designSubStageHistory?: { subStage: DesignSubStage; enteredAt: Date }[];
  /** @deprecated Legacy uid. Designers use `assignedDesigner`; invited architects use `assignedArchitectUids`. */
  assignedArchitect?: string;
  /** External architecture firm / organization (free text, even when no CRM login). */
  assignedArchitectName?: string;
  /** Invited external architect user uids granted Project Hub access on this deal. */
  assignedArchitectUids?: string[];
  /** In-house designer (CRM user uid). */
  assignedDesigner?: string;
  assignedDesignerName?: string;
  projectName?: string;                // distinguishes multiple projects for same lead (e.g., "Pavilion", "Carport")

  sniperZone?: boolean;                // manually flagged for sniper containment zone

  // QuickBooks linkage
  quickbooksCustomerId?: string;
  /**
   * QuickBooks Sub-customer (Job) ID for this project, child of the
   * top-level Customer. Per-project AR rolls up to the parent Customer
   * automatically, while invoices, payments, and reports can be filtered
   * to the project. Populated lazily by the createQuickBooksInvoice
   * Cloud Function the first time an invoice posts.
   */
  quickbooksJobId?: string;
  quickbooksInvoiceId?: string;
  quickbooksInvoiceNum?: string;

  // Project P&L finalization (PM action). When set, the project is removed from
  // the active P&L list and shown in the Completed Projects archive (read-only,
  // re-openable). `pmFinalizedBy` mirrors the auth user uid that finalized.
  pmFinalizedAt?: Date;
  pmFinalizedBy?: string;

  /**
   * P&L-only shell for old QuickBooks ascription. Won + finalized, no Hub
   * project, no onboarding. Hidden from convert-to-project lists.
   */
  plHistoricalShell?: boolean;
}

export interface StageTransition {
  fromStage: string | null;
  toStage: string;
  transitionedAt: Date;
  durationMs: number;
  transitionedBy: string;
}

export interface CustomFieldValue {
  fieldId: string;
  fieldName: string;
  fieldType: 'text' | 'number' | 'date' | 'select' | 'multiselect' | 'currency';
  value: any;
}

export interface CustomFieldDefinition {
  id?: string;
  name: string;
  type: 'text' | 'number' | 'date' | 'select' | 'multiselect' | 'currency';
  options?: string[];
  entityType: 'lead' | 'deal' | 'project';
  required: boolean;
  createdAt: Date;
}

// ==========================================
// ACTIVITY SYSTEM
// ==========================================

export type ActivityType = 'call' | 'text' | 'email' | 'meeting' | 'note' | 'site-visit';
export type ActivityStatus = 'scheduled' | 'completed' | 'cancelled' | 'overdue';
export type CallOutcome = 'answered' | 'no_answer' | 'voicemail' | 'busy' | 'wrong_number' | 'disconnected';

// Call tracking configuration
export interface CallCadenceConfig {
  // Time windows for call attempts (in hours from previous attempt)
  intervals: number[]; // e.g., [1, 2, 24, 72] = 1hr, 2hr, 1day, 3days
  maxAttempts: number; // e.g., 4
  workingHoursStart: number; // e.g., 9 (9 AM)
  workingHoursEnd: number; // e.g., 17 (5 PM)
  workingDays: number[]; // e.g., [1,2,3,4,5] for Mon-Fri
}

// Call tracking metrics for a lead
export interface LeadCallMetrics {
  totalCalls: number;
  answeredCalls: number;
  unansweredCalls: number;
  lastCallDate?: Date;
  lastCallOutcome?: CallOutcome;
  nextCallDue?: Date;
  callAttemptNumber: number; // Which attempt we're on (1-4)
  shouldMarkLost: boolean;
  callHistory: {
    date: Date;
    outcome: CallOutcome;
    notes?: string;
  }[];
}

export interface Activity {
  id?: string;
  type: ActivityType;
  title: string;
  description?: string;

  // Linked entity
  entityType: 'lead' | 'deal' | 'project';
  entityId: string;
  entityName?: string;

  // Scheduling
  scheduledAt?: Date;
  completedAt?: Date;
  dueDate?: Date;
  duration?: number; // minutes

  // Assignment
  createdBy: string;
  assignedTo?: string;

  // Status
  status: ActivityStatus;
  outcome?: string;

  // Google Calendar sync
  googleEventId?: string;      // ID of synced Google Calendar event
  googleCalendarId?: string;   // Which calendar it was synced to
  syncToGoogle?: boolean;      // User preference for syncing this activity

  // Actor tracking (employee vs client)
  actorType?: 'employee' | 'client' | 'stakeholder';
  actorName?: string;

  createdAt: Date;
  updatedAt: Date;
}

// ==========================================
// ANALYTICS TYPES
// ==========================================

export interface StageMetrics {
  stageId: string;
  stageName: string;
  count: number;
  totalValue: number;
  avgTimeInStage: number;
  conversionToNext: number;
}

export interface DealAnalytics {
  // Funnel metrics
  totalLeads: number;
  totalDeals: number;
  conversionRate: number;

  // Stage metrics
  stageMetrics: StageMetrics[];

  // Outcome metrics
  wonDeals: number;
  lostDeals: number;
  winRate: number;
  avgDealValue: number;
  avgTimeToClose: number;

  // Demographic correlations
  byLocation: LocationMetrics[];
  byProjectType: ProjectTypeMetrics[];
  bySource: SourceMetrics[];
  byBudgetRange: BudgetRangeMetrics[];
}

export interface LocationMetrics {
  state: string;
  city?: string;
  dealCount: number;
  winRate: number;
  avgValue: number;
  avgTimeToClose: number;
}

export interface ProjectTypeMetrics {
  projectType: string;
  dealCount: number;
  winRate: number;
  avgValue: number;
  avgTimeToClose: number;
}

export interface SourceMetrics {
  source: LeadSource;
  dealCount: number;
  winRate: number;
  avgValue: number;
  conversionRate: number;
}

export interface BudgetRangeMetrics {
  range: string;
  minBudget: number;
  maxBudget: number;
  dealCount: number;
  winRate: number;
  avgTimeToClose: number;
}

// ==========================================
// REPORTING SYSTEM
// ==========================================

export type ReportDataSource =
  | 'leads'
  | 'deals'
  | 'activities'
  | 'projects'
  | 'pipeline-analytics'
  | 'conversion-metrics'
  | 'revenue-forecast'
  | 'utm-campaigns'
  | 'utm-sources';

export type DateRangePreset =
  | 'today'
  | 'yesterday'
  | 'this-week'
  | 'last-week'
  | 'this-month'
  | 'last-month'
  | 'this-quarter'
  | 'last-quarter'
  | 'this-year'
  | 'last-year'
  | 'all-time';

export type ReportColumnType = 'string' | 'number' | 'currency' | 'date' | 'percentage' | 'duration';
export type ReportAggregation = 'sum' | 'avg' | 'count' | 'min' | 'max';
export type ReportFilterOperator = 'equals' | 'not-equals' | 'contains' | 'greater-than' | 'less-than' | 'between' | 'in';

export interface ReportColumn {
  field: string;
  label: string;
  type: ReportColumnType;
  aggregation?: ReportAggregation;
  format?: string;
  width?: number;
  visible: boolean;
}

export interface ReportFilter {
  field: string;
  operator: ReportFilterOperator;
  value: any;
}

export interface ReportConfig {
  id?: string;
  name: string;
  description?: string;
  dataSource: ReportDataSource;
  columns: ReportColumn[];
  filters: ReportFilter[];
  sortBy?: { column: string; direction: 'asc' | 'desc' }[];
  groupBy?: string[];
  dateRange?: { start: Date; end: Date } | DateRangePreset;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
  isTemplate: boolean;
}

export interface ReportSchedule {
  frequency: 'daily' | 'weekly' | 'monthly';
  dayOfWeek?: number;
  dayOfMonth?: number;
  time: string;
  timezone: string;
}

export interface SavedReport {
  id?: string;
  configId: string;
  name: string;
  schedule?: ReportSchedule;
  recipients?: string[];
  lastRunAt?: Date;
  createdBy: string;
  createdAt: Date;
}

// ==========================================
// PROJECT MANAGEMENT SYSTEM
// ==========================================

export type ProjectStatus = 'planning' | 'design' | 'production' | 'installation' | 'completed' | 'on-hold';
export type ProjectDepartment =
  | 'preconstruction'
  | 'design'
  | 'procurement'
  | 'production'
  | 'installation';
export type TaskStatus = 'pending' | 'in-progress' | 'review' | 'completed' | 'blocked' | 'on-hold';
export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';
export type MilestoneStatus = 'pending' | 'completed';

// Project frame types
export type FrameType = 'custom' | 'kit';

// Kit frame stages (simplified pipeline)
export type KitFrameStage = 'procurement' | 'production' | 'shipment' | 'delivery';

// Custom frame departments (existing)
export const CUSTOM_FRAME_DEPARTMENTS: ProjectDepartment[] = [
  'preconstruction',
  'design',
  'procurement',
  'production',
  'installation',
];

/** Human-readable labels for task department pickers */
export const DEPARTMENT_DISPLAY_LABELS: Record<ProjectDepartment, string> = {
  preconstruction: 'Preconstruction',
  design: 'Design',
  procurement: 'Procurement',
  production: 'Production',
  installation: 'Installation',
};

/** Department colors for Gantt, board badges, etc. — must include every ProjectDepartment */
export const DEPARTMENT_SWATCH_COLORS: Record<ProjectDepartment, string> = {
  preconstruction: '#64748B',
  design: '#9F7AEA',
  procurement: '#4299E1',
  production: '#ED8936',
  installation: '#38B2AC',
};

// Kit frame stages
export const KIT_FRAME_STAGES: KitFrameStage[] = ['procurement', 'production', 'shipment', 'delivery'];

export type ProjectPhase = 'estimating' | 'preconstruction' | 'contracted';

export const PROJECT_PHASE_LABELS: Record<ProjectPhase, string> = {
  estimating: 'Estimating',
  preconstruction: 'Preconstruction',
  contracted: 'Under Contract',
};

/** True when the project is still in the pre-contract funnel (estimating or preconstruction). */
export function isEarlyFunnelProjectPhase(phase?: ProjectPhase | null): boolean {
  return phase === 'estimating' || phase === 'preconstruction';
}

/** Staff can create trade briefings in estimating, preconstruction, and under contract. */
export function canCreateTradeBriefingsForPhase(phase?: ProjectPhase | null): boolean {
  return phase == null || phase === 'estimating' || phase === 'preconstruction' || phase === 'contracted';
}

export interface TimberProject {
  id?: string;
  dealId: string;
  clientEmail: string;
  clientName: string;
  name: string;
  description?: string;

  status: ProjectStatus;
  /** Saved when a project is closed out so Reopen can restore the prior status. */
  statusBeforeClose?: ProjectStatus;
  frameType: FrameType;
  phase?: ProjectPhase; // estimating | preconstruction | contracted (defaults to 'contracted')
  /** Set when the project first becomes Under Contract (merge from precon, portal signing sync, or new won project). */
  contractedAt?: Date;

  startDate: Date;
  targetEndDate: Date;
  actualEndDate?: Date;

  value: number;
  location: string;
  address?: Address;
  coordinates?: Coordinates;
  projectType: string;

  projectManager?: string;
  assignedTo?: string[];
  /**
   * Hidden QA access. Grants architect/engineer hub visibility without
   * showing the user on the roster, tasks, designer field, or notifications.
   */
  qaViewerUids?: string[];
  /** Invited external architects who can open this project's hub (synced from the deal). */
  architectUids?: string[];
  clientIds?: string[];

  /** Preconstruction only: when true, time counts toward billable precon totals on the all-projects report. */
  ongoingBillable?: boolean;

  templateId?: string;
  defaultView?: 'table' | 'board' | 'gantt';

  // Timeline parameters carried from portal on signing
  timelineParams?: Array<{
    id: string;
    label: string;
    durationWeeks: number;
    bufferWeeks: number;
    subPhases?: Array<{
      id: string;
      label: string;
      durationWeeks: number;
    }>;
  }>;

  /** Latest standup note (denormalized from projectStandupNotes for All Projects list). */
  standupNote?: string;
  standupNoteAudioUrl?: string;
  standupNoteUpdatedAt?: Date;
  standupNoteUpdatedBy?: string;
  standupNoteUpdatedByName?: string;

  /** How this project will be fabricated (hand-cut in-house vs external CNC). */
  fabricationMethod?: FabricationMethod;
  /** Saved hand-cut vs CNC comparison inputs for this project. */
  handCutVsCncState?: ProjectHandCutVsCncState;

  /** When set, shown at the top of every trade briefing portal for this project. */
  tradeBriefingConfidentialBanner?: string | null;

  createdAt: Date;
  updatedAt: Date;
}

/** Append-only standup status history for Monday/Wednesday project reviews. */
export interface ProjectStandupNote {
  id?: string;
  projectId: string;
  text: string;
  audioUrl?: string;
  audioStoragePath?: string;
  createdAt: Date;
  createdBy: string;
  createdByName: string;
}

/** Architect → leadership status reports (separate from PM standup feed). */
export interface ArchitectStandupNote {
  id?: string;
  projectId: string;
  text: string;
  audioUrl?: string;
  audioStoragePath?: string;
  createdAt: Date;
  createdBy: string;
  createdByName: string;
}

/** Action item extracted from a Fathom meeting and linked to a project (PM-only tab). */
export interface ProjectMeetingActionItem {
  id: string;
  text: string;
  assigneeName?: string;
  assigneeUserId?: string;
  dueDate?: string;
  status?: 'open' | 'done';
}

/** Bot badge for a project meeting: staff-only vs outside parties. */
export type ProjectMeetingAudience = 'internal' | 'external';

/** Calendar meeting on a project timeline (written by projectMeetingsAgentApi). */
export type ProjectMeetingSource = 'fathom' | 'granola' | 'none';

export interface ProjectMeeting {
  id?: string;
  calendarEventId: string;
  /** One meeting can link to several projects. */
  projectIds: string[];
  dealId?: string;
  title: string;
  start: Date;
  end: Date;
  attendees: string[];
  source: ProjectMeetingSource;
  recordingUrl?: string;
  /** Plain-English short notes; may be empty. */
  summary: string;
  createdBy: string;
  createdAt?: Date;
  updatedAt: Date;
}

/** Communication entry on a lead (call / meeting / email / note). Collection: leadComms. */
export type LeadCommType = 'call' | 'meeting' | 'email' | 'note';
export type LeadCommAudience = 'internal' | 'external';

export interface LeadComm {
  id?: string;
  leadId: string;
  /** Stamped when the lead converts to a deal (in-place; no copy). */
  dealId?: string;
  /** Stamped when the deal converts to a project so Project Hub can query the same rows. */
  projectId?: string;
  type: LeadCommType;
  occurredAt: Date;
  participants: string[];
  title: string;
  /** Markdown-friendly body / summary. */
  summary: string;
  /** Fathom, Granola, or other source URL. */
  sourceLink?: string;
  /** Source system for dedupe with externalId (fathom, granola, manual, …). */
  source: string;
  /** External id for idempotent agent upserts (e.g. Fathom recording id). */
  externalId?: string;
  audience: LeadCommAudience;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

/** Fathom / Granola-derived meeting summary scoped to a timber project (written by agent API). */
export interface ProjectMeetingNote {
  id?: string;
  fathomMeetingId: string;
  projectId: string;
  dealId?: string;
  title: string;
  meetingAt: Date;
  summary: string;
  actionItems: ProjectMeetingActionItem[];
  participants?: string[];
  /** Set by the meeting-notes bot from invitees and location. Absent when inconclusive. */
  audience?: ProjectMeetingAudience;
  audienceRationale?: string;
  location?: string;
  recordingUrl?: string;
  transcriptUrl?: string;
  matchRationale?: string;
  matchConfidence?: number;
  source: 'fathom' | 'granola' | 'lctf_field';
  /** Optional link to an Operations Hub task (agent attach / create-from-meeting). */
  opsTaskId?: string;
  /** Field-note capture idempotency key when source is lctf_field. */
  fieldNoteJobId?: string;
  createdAt: Date;
  updatedAt: Date;
}

/** Activity level from the daily marketing competitor scan. */
export type MarketingActivityLevel = 'low' | 'medium' | 'high';

/** Per-competitor row in a daily marketing briefing. */
export interface MarketingCompetitorDetail {
  name: string;
  has_updates: boolean;
  [key: string]: unknown;
}

/** Thin index row for the Marketing Hub date picker. */
export interface MarketingReportListItem {
  report_date: string;
  overall_activity_level: MarketingActivityLevel;
  competitors_with_updates: number;
  headline: string;
}

/** Daily marketing intelligence report (written by marketing agent API). */
export interface MarketingReport {
  id?: string;
  report_date: string;
  monitoring_period: string;
  focus_competitors: string[];
  overall_activity_level: MarketingActivityLevel;
  daily_highlights: string[];
  seo_keywords_analysis?: Record<string, unknown>;
  google_business_profile_monitoring?: Record<string, unknown>;
  new_kits_released?: Record<string, unknown>[];
  marketing_tone_style_feel?: Record<string, unknown>;
  partnerships_observed?: Record<string, unknown>[];
  competitor_details: MarketingCompetitorDetail[];
  key_market_trends?: string;
  recommendations_for_leipers_creek: string[];
  data_notes?: string;
  source: 'marketing-agent';
  createdAt: Date;
  updatedAt: Date;
}

/** One opportunity signal in a radar report. */
export interface MarketingOpportunityItem {
  title: string;
  category?: string;
  rationale?: string;
  urgency?: string;
  suggested_action?: string;
  skip_reason?: string;
  [key: string]: unknown;
}

/** Thin index row for Opportunity Radar date picker. */
export interface MarketingOpportunityReportListItem {
  id: string;
  report_date: string;
  opportunity_count: number;
  headline: string;
  createdAt?: Date;
  updatedAt?: Date;
}

/** Daily opportunity radar (Company Brain → marketing agent API). */
export interface MarketingOpportunityReport {
  id?: string;
  report_date: string;
  opportunities: MarketingOpportunityItem[];
  otherSignals: MarketingOpportunityItem[];
  summary?: string;
  highlights: string[];
  source: 'marketing-agent';
  createdAt: Date;
  updatedAt: Date;
}

export type ProjectTimeEntryType = 'explicit' | 'implied';

export interface ProjectTimeEntry {
  id?: string;
  projectId: string;
  taskId?: string;
  userId: string;
  userName: string;
  type?: ProjectTimeEntryType;
  minutes: number;
  date: Date;
  startTime?: Date;
  endTime?: Date;
  note?: string;
  phase?: ProjectPhase;
  createdAt: Date;
}

/** Idle-aware CRM dwell session (page + optional job/lead). Not written while project/lead tracking is already flushing. */
export type CrmActivitySessionType = 'implied';
export type CrmActivityEntityType = 'project' | 'lead' | 'deal' | 'proposal';

/** Coarse activity bucket for CEO time-costing (inferred from route / page). */
export type CrmActivityClass =
  | 'sales'
  | 'meetings'
  | 'design'
  | 'admin'
  | 'site_visits'
  | 'unknown';

/** Whether a unified CRM session overlaps an active project/lead timer (POC). */
export type CrmActivityAttributedTo = 'project' | 'lead' | 'none';

export interface CrmActivitySession {
  id?: string;
  userId: string;
  userName: string;
  userEmail?: string;
  type: CrmActivitySessionType;
  /** Strict active minutes (input ≤30s). Comparable with historical `minutes`. */
  minutes: number;
  /** Presence minutes (visible+focused, input ≤180s, mousemove counts). */
  presenceMinutes?: number;
  activeSeconds?: number;
  presenceSeconds?: number;
  startedAt: Date;
  endedAt: Date;
  path: string;
  pageKey: string;
  pageLabel: string;
  activityClass?: CrmActivityClass;
  /** Project Hub job id when entity is a project. */
  jobId?: string;
  dealId?: string;
  entityType?: CrmActivityEntityType;
  entityId?: string;
  entityName?: string;
  createdAt: Date;
  /** In-page module/tab (Project Hub ?tab=, Finances sub-page). */
  moduleKey?: string;
  moduleLabel?: string;
  /** Spec alias for moduleKey. */
  viewKey?: string;
  viewLabel?: string;
  search?: string;
  /** Browser tab id for multi-tab dedupe. */
  tabId?: string;
  /** Stable session id joining edits / heartbeats. */
  sessionId?: string;
  /** Whether project/lead timers were also writing for this segment. */
  attributedTo?: CrmActivityAttributedTo;
  projectId?: string;
  leadId?: string;
  focused?: boolean;
  visible?: boolean;
}

/**
 * Lightweight CRM session beacon ping (authenticated staff only).
 * Written ~every 60s while the tab is visible/focused, plus on route change.
 * Aggregated into CEO time-cost sessions at report time — not Google Calendar.
 */
export type CrmHeartbeatIdleState = 'active' | 'reading' | 'idle';

export interface CrmSessionHeartbeat {
  id?: string;
  userId: string;
  userEmail: string;
  userName: string;
  timestamp: Date;
  path: string;
  pageKey: string;
  pageLabel: string;
  activityClass: CrmActivityClass;
  jobId?: string;
  dealId?: string;
  entityType?: CrmActivityEntityType;
  entityId?: string;
  entityName?: string;
  moduleKey?: string;
  moduleLabel?: string;
  viewKey?: string;
  viewLabel?: string;
  sessionId?: string;
  /** Seconds since last input (strict or presence, whichever is fresher). */
  lastInputAgeSec?: number;
  /** active ≤30s strict; reading ≤180s presence; else idle. */
  idleState?: CrmHeartbeatIdleState;
  /** Tab visible and (when available) window focused. */
  focused: boolean;
  visible: boolean;
  createdAt: Date;
}

// ── 1099 Contractor Timesheets & Payables ──

export type ContractorW9Status = 'missing' | 'on_file' | 'expired';

export type ContractorInvoiceBillingMode = 'hourly' | 'day_rate_required';

export type ContractorBillingUnit = 'hour' | 'day';

export interface ContractorProfile {
  userId: string;
  businessName?: string;
  defaultHourlyRate: number;
  /** Staff-set day rate; pre-loaded when invoiceBillingMode requires day-rate invoicing. */
  defaultDayRate?: number;
  /** When day_rate_required, contractor must submit invoices using day billing. */
  invoiceBillingMode?: ContractorInvoiceBillingMode;
  /** Hint for Juliana when entering payment in QuickBooks. */
  qbVendorName?: string;
  w9Status: ContractorW9Status;
  w9ReceivedAt?: Date;
  w9StoragePath?: string;
  agreementSignedAt?: Date;
  /** When set, contractor may only log hours on these project ids. */
  allowedProjectIds?: string[];
  isActive: boolean;
  updatedAt: Date;
}

export type ContractorTimeEntryStatus = 'draft' | 'submitted' | 'invoiced' | 'paid';

export interface ContractorTimeEntry {
  id?: string;
  contractorUserId: string;
  projectId: string;
  taskId?: string;
  workDate: Date;
  minutes: number;
  description: string;
  hourlyRate?: number;
  /** Rate stamped at invoicing (hour or day unit). */
  billingRate?: number;
  billingUnit?: ContractorBillingUnit;
  timesheetId?: string;
  status: ContractorTimeEntryStatus;
  /** When true, this entry predates the daily hour cap and is excluded from cap checks. */
  dailyCapGrandfathered?: boolean;
  enteredByUserId?: string;
  createdAt: Date;
  updatedAt?: Date;
}

export type ContractorTimesheetStatus = 'draft' | 'submitted' | 'invoiced' | 'paid';

export interface ContractorTimesheet {
  id?: string;
  contractorUserId: string;
  periodStart: Date;
  periodEnd: Date;
  status: ContractorTimesheetStatus;
  totalMinutes: number;
  totalAmount: number;
  projectIds: string[];
  submittedAt?: Date;
  submittedByUserId?: string;
  invoiceId?: string;
  createdAt: Date;
  updatedAt?: Date;
}

export type ContractorInvoiceStatus = 'pending_payment' | 'paid' | 'void';

export interface ContractorInvoiceLineItem {
  projectId: string;
  projectName: string;
  projectNumber?: string;
  minutes: number;
  rate: number;
  amount: number;
  billingUnit?: ContractorBillingUnit;
  days?: number;
}

export interface ContractorInvoice {
  id?: string;
  contractorUserId: string;
  contractorName: string;
  timesheetId: string;
  invoiceNumber: string;
  lineItems: ContractorInvoiceLineItem[];
  totalAmount: number;
  billingUnit?: ContractorBillingUnit;
  appliedRate?: number;
  periodStart: Date;
  periodEnd: Date;
  status: ContractorInvoiceStatus;
  paidAt?: Date;
  paidByUserId?: string;
  qbReferenceNote?: string;
  createdAt: Date;
  updatedAt?: Date;
}


// ── Project Onboarding Checklist ──

export interface OnboardingTemplateItem {
  id: string;
  title: string;
  description?: string;
  category: string;
  /** Drives task-table grouping for mirrored under-contract checklist rows */
  department?: ProjectDepartment;
  defaultAssigneeId?: string;
  order: number;
  required: boolean;
  /** When true, mirrored project checklist row is part of the visual critical-path chain on the Gantt. */
  onCriticalPath?: boolean;
}

export interface OnboardingChecklistTemplate {
  items: OnboardingTemplateItem[];
  updatedAt: Date;
}

export interface OnboardingItem {
  id: string;
  title: string;
  description?: string;
  category: string;
  /** Mirrors template; defaults to design when missing */
  department?: ProjectDepartment;
  /** When set, checklist row appears under this project task group (Firestore taskGroups id) */
  taskGroupId?: string;
  /** Full assignee list (mirrors ProjectTask.assignedTo). When missing, `assigneeId` is used. */
  assignedTo?: string[];
  assigneeId?: string;
  assigneeName?: string;
  required: boolean;
  order: number;
  completed: boolean;
  completedAt?: Date;
  completedBy?: string;
  completedByName?: string;
  /** When set, drives Status in the task panel; otherwise derived from `completed` */
  taskStatus?: TaskStatus;
  priority?: TaskPriority;
  startDate?: Date;
  endDate?: Date;
  progress?: number;
  dependencyEdges?: TaskDependencyEdge[];
  subtasks?: Subtask[];
  actionRequired?: string;
  actionRequiredNote?: string;
  /** Visual-only Gantt chain; not the same as schedule-critical (CPM). */
  onCriticalPath?: boolean;
  /** Hard-pin: scheduler skips this checklist row; drag is disabled. Mirrors ProjectTask. */
  scheduleLocked?: boolean;
  lockedAt?: Date;
  lockedBy?: string;
  /** Captured when item is marked completed; mirrored into startDate/endDate. */
  actualStartDate?: Date;
  actualEndDate?: Date;
  supplierOrgIds?: string[];
  supplierManual?: boolean;
  /** True when dates were generated at contract / template time and still need the assigned PM. */
  scheduleNeedsPmReview?: boolean;
  /** When the assigned project manager confirmed generated dates. */
  scheduleReviewedByPmAt?: Date;
  scheduleReviewedByPmUid?: string;
}

export interface ProjectOnboarding {
  id?: string;
  projectId: string;
  projectName: string;
  templateVersion: Date;
  status: 'active' | 'completed';
  completedAt?: Date;
  items: OnboardingItem[];
  createdAt: Date;
}

/** Optional tag for project hub uploads (employee / architect / engineer). */
export type ProjectFileKindTag = 'timber_list' | 'hardware_list' | 'html' | 'none';

/** Quote category when uploaded to the Quotes section in Project Hub files. */
export type ProjectQuoteType = 'timber' | 'cnc' | 'timber_and_cnc' | 'hardware_and_fasteners';

export type ProjectQuoteStatus = 'pending' | 'approved';

/** Section discriminator for flat project file uploads (not version streams). */
export type ProjectFileSection = 'quote';

/** Version-control lineage kind for primary project deliverables (HTML, native 3D, timber/hardware CSV, 2D PDF). */
export type ProjectFileStreamKind =
  | 'three_d_html'
  | 'three_d_raw'
  | 'two_d_pdf'
  | 'timber_list_csv'
  | 'hardware_list_csv'
  | 'general';

export interface ProjectFileStream {
  id?: string;
  projectId: string;
  title: string;
  streamKind: ProjectFileStreamKind;
  description?: string;
  createdAt: Date;
  createdBy: string;
  updatedAt: Date;
  /** Monotonic counter; latest blob version number. */
  lastVersionIndex?: number;
  /** projectFiles doc id for current head version. */
  headFileId?: string;
  /** User id who uploaded the current head version (mirrors head `projectFiles.uploadedBy`). */
  lastSubmittedBy?: string;
  /** Display name for {@link lastSubmittedBy}. */
  lastSubmittedByName?: string;
  /** Primary deliverable track (default) vs optional supplementary client deliverable. */
  streamRole?: 'primary' | 'supplementary';
  /** Client portal section title when {@link streamRole} is supplementary. */
  portalDisplayName?: string;
  /**
   * Proposal structures (nodeSet ids) this deliverable track covers. Absent / empty =
   * whole project. Optional so legacy tracks render unchanged.
   */
  structureIds?: string[];
  /** Denormalized display names for {@link structureIds}. */
  structureNames?: string[];
  /**
   * This track was the whole-project root before the structure split: its versions
   * were copied as the opening versions of {@link forkedToStreamIds}. Kept read-only
   * (no new uploads) so the shared history stays truthful.
   */
  wholeProjectRoot?: boolean;
  /** Structure tracks forked from this whole-project root at the split. */
  forkedToStreamIds?: string[];
  /** Set on a structure track that was forked from a whole-project root. */
  forkedFromStreamId?: string;
  /**
   * Set when this track was added after the original split. Root files uploaded
   * before this instant stay off this track; later whole-project uploads still appear.
   */
  inheritVersionsAfter?: Date;
  /**
   * Custom display name for the structure *group* this track sits in (the rail
   * section heading). Written to every track in the group; falls back to the
   * structure names ("Barn + Porch") when unset.
   */
  structureGroupLabel?: string;
  /**
   * Written by Files → "Set up structures" when this timber-list track was tied to a
   * proposal structure (created or filled from its CSV). Drives the Deliverables
   * history card alongside splits.
   */
  structureSetup?: ProjectFileStructureSetupRecord;
}

/** How a timber-list track came to belong to a structure via "Set up structures". */
export interface ProjectFileStructureSetupRecord {
  at: Date;
  byUid: string;
  byName?: string;
  /** Set when the version was moved out of a shared whole-project track onto this one. */
  fromStreamId?: string;
  fromStreamTitle?: string;
  /** CSV that fed the structure's timber lines. */
  fileName?: string;
  /** True when the wizard created the proposal structure (vs. filled an existing one). */
  createdStructure: boolean;
}

/** One hop in a version's move history (Deliverables → Move to another track). */
export interface ProjectFileMoveRecord {
  fromStreamId: string;
  fromStreamTitle?: string;
  fromVersionIndex: number;
  toStreamId: string;
  toVersionIndex: number;
  movedBy: string;
  movedByName: string;
  movedAt: Date;
}

/** Designer Standards library subfolders (Settings → Project Defaults). */
export type DesignerStandardSubfolder = 'sops' | 'template_2d' | 'template_3d' | 'timber_list_export';

/** One file in the global Designer Standards library (`settings/designerStandards`). */
export interface DesignerStandardItem {
  id: string;
  name: string;
  url: string;
  storagePath: string;
  mimeType: string;
  size: number;
  subfolder: DesignerStandardSubfolder;
  uploadedAt: Date;
  uploadedBy: string;
  uploadedByName: string;
  order: number;
}

export interface ProjectFile {
  id?: string;
  projectId: string;
  name: string;
  url: string;
  storagePath: string;
  mimeType: string;
  size: number;
  uploadedAt: Date;
  uploadedBy: string;
  uploadedByName: string;
  /** User-selected tag at upload; omitted on legacy/synced rows (treat as none). */
  kindTag?: ProjectFileKindTag;
  /** Latest CSV tagged as timber list for this project; older timber lists are false when superseded. */
  isCurrentTimberList?: boolean;
  /** Latest CSV tagged as hardware list for this project; older hardware lists are false when superseded. */
  isCurrentHardwareList?: boolean;
  /** Version-history lineage (`projectFileStreams` doc id); omitted for legacy flat uploads. */
  streamId?: string;
  versionIndex?: number;
  versionLabel?: string;
  versionNote?: string;
  /** Conceptual, engineer review, joinery, or supplementary details — set when the version is uploaded. */
  detailStage?: 'estimating' | 'conceptual' | 'joinery' | 'supplementary_details';
  /**
   * Timber CSV only. When set, height and width stay as written (7.5 stays 7.5).
   * Absent rounds those inches to the nearest inch. Length is unchanged either way.
   */
  trueDimensions?: boolean;
  /** This 3D version is the CNC model for {@link cncStructureIds}. */
  forCnc?: boolean;
  cncStructureIds?: string[];
  cncStructureNames?: string[];
  /** Latest blob for this stream (mutually exclusive per stream). */
  isHeadVersion?: boolean;
  /**
   * This version is a whole-project file copied into a structure track at the split:
   * same bytes as `fileId` in the original (root) track. Rendered with a "Whole project" chip.
   */
  wholeProjectOrigin?: { streamId: string; fileId: string };
  /**
   * Uploaded with Whole project selected after the structure split. The same
   * release was added to every structure group's track, and each copy keeps the chip.
   */
  uploadedAsWholeProject?: boolean;
  /** Ties copies of one version that were placed on more than one structure group. */
  structureReleaseId?: string;
  /**
   * Other structure tracks that show this same document. The file lives on
   * {@link streamId}; these tracks list it without a second copy.
   */
  sharedOnStreamIds?: string[];
  /** Audit trail when staff move a version between tracks (newest last). */
  moveHistory?: ProjectFileMoveRecord[];
  /** Search keywords for the global HTML model catalog (media management). */
  htmlModelDescriptors?: string[];
  /** Optional static screenshot URL for the HTML model catalog (Firebase Storage or CDN). */
  htmlPreviewImageUrl?: string;
  /** When `'quote'`, file lives in the Quotes section (flat upload, not a version stream). */
  fileSection?: ProjectFileSection;
  quoteType?: ProjectQuoteType;
  quoteStatus?: ProjectQuoteStatus;
  quoteApprovedAt?: Date;
  quoteApprovedBy?: string;
  quoteApprovedByName?: string;
  quoteBillingNotifyUserId?: string;
  quoteBillingNotifyUserName?: string;
  emailSource?: ExecEmailSource;
  /** When synced from `leadDocuments`, the source doc id (delete cascades to lead). */
  sourceLeadDocumentId?: string;
  /**
   * Project folder for unversioned files, e.g. `admin/project-brief` (see
   * `projectFileFolders.ts`). Absent = Unsorted. Never set on versioned rows.
   */
  folderPath?: string;
  /**
   * Employee-only Financials file. Stored in `projectFinancialFiles`, not `projectFiles`.
   * Client-only marker so move/delete hit the right collection.
   */
  employeeOnly?: boolean;
}

export interface EORRecord {
  firmId: string;
  firmName: string;
  engineerId: string;
  engineerName: string;
}

export interface TaskGroup {
  id?: string;
  projectId: string;
  name: string;
  color: string;
  position: number;
  collapsed?: boolean;
  eorRecord?: EORRecord;
}

export interface Subtask {
  id: string;
  title: string;
  completed: boolean;
  assignedTo?: string;
  position: number;
  /** CRM uid or external sentinel — who must act on this checklist line (ops AR). */
  actionRequired?: string;
  actionRequiredNote?: string;
}

/**
 * FS: successor start from predecessor finish.
 * SS: successor start from predecessor start.
 * FF: successor finish from predecessor finish.
 * SF: successor finish from predecessor start.
 * Lag (weekdays): Mon–Fri count; not raw calendar span. Negative = overlap / lead.
 */
export type DependencyEdgeType = 'FS' | 'SS' | 'FF' | 'SF';

export interface TaskDependencyEdge {
  predecessorId: string;
  type: DependencyEdgeType;
  /** Weekday count (Mon–Fri); excludes weekends. Not calendar-day span. */
  lagBusinessDays: number;
}

/** Payload when applying lag + link type from the Gantt or task panel. */
export interface UpdateDependencyPayload {
  lagBusinessDays: number;
  type: DependencyEdgeType;
}

/** One contiguous period a task waited on a particular action-required holder. */
export interface ActionRequiredWaitSegment {
  /** CRM uid, `ACTION_REQUIRED_EXTERNAL_SENTINEL`, or '' for unassigned periods */
  holderKey: string;
  /** External party label when holderKey is sentinel */
  holderNote?: string;
  startedAt: Date;
  endedAt?: Date;
}

export interface ProjectTask {
  id?: string;
  projectId: string;
  groupId?: string;
  title: string;
  description?: string;
  department: ProjectDepartment;
  assignedTo: string[];
  status: TaskStatus;
  priority: TaskPriority;
  startDate: Date;
  endDate: Date;
  dependencies: string[];
  /** Structured predecessor edges (FS/SS/FF/SF); when set, drives scheduling. `dependencies` is kept in sync as predecessor ids. */
  dependencyEdges?: TaskDependencyEdge[];
  progress: number;
  position: number;
  notes?: string;
  /** CRM user uid, or `ACTION_REQUIRED_EXTERNAL_SENTINEL` when waiting on an external party (name in `actionRequiredNote`). */
  actionRequired?: string;
  /** When `actionRequired` is external sentinel: who we are waiting on. Otherwise optional extra context for internal action required. */
  actionRequiredNote?: string;
  /** Audit trail of who held action required and for how long (maintained on AR changes). */
  actionRequiredWaitSegments?: ActionRequiredWaitSegment[];
  /** When set, this task is a subtask of the referenced parent task. */
  parentTaskId?: string;
  /** Primary proposal structure (first of {@link structureIds}) — kept for backward compatibility. */
  structureId?: string;
  /** Denormalized name for primary structure. */
  structureName?: string;
  /** One or more proposal structures this subtask belongs to. */
  structureIds?: string[];
  /** Denormalized names aligned with {@link structureIds}. */
  structureNames?: string[];
  /** Staff-set supplier orgs for this row (group timeline + quote override). */
  supplierOrgIds?: string[];
  /** True when {@link supplierOrgIds} was set in the task panel (do not clobber on later quote autofill). */
  supplierManual?: boolean;
  subtasks?: Subtask[];
  estimatedHours?: number;
  loggedHours?: number;
  tags?: string[];
  lastCommentAt?: Date;
  lastCommentBy?: string;
  /** Visual-only Gantt overlay; not schedule-critical (CPM). */
  onCriticalPath?: boolean;
  /** Hard-pin: scheduler skips this task; drag is disabled. Set on completion or via lock toggle. */
  scheduleLocked?: boolean;
  lockedAt?: Date;
  lockedBy?: string;
  /** Captured when task is marked completed; mirrored into startDate/endDate. */
  actualStartDate?: Date;
  actualEndDate?: Date;
  completedAt?: Date;
  /** True when dates were generated at contract / template time and still need the assigned PM. */
  scheduleNeedsPmReview?: boolean;
  /** When the assigned project manager confirmed generated dates. */
  scheduleReviewedByPmAt?: Date;
  scheduleReviewedByPmUid?: string;
  createdAt: Date;
  updatedAt: Date;
}

export type TaskReminderStatus = 'pending' | 'sent' | 'cancelled';
export type TaskReminderKind = 'once' | 'recurring';

export interface TaskReminder {
  id?: string;
  taskId: string;
  projectId: string;
  /** Self-only reminders: always equals createdByUserId. */
  recipientUserId: string;
  createdByUserId: string;
  remindAt: Date;
  message?: string;
  kind: TaskReminderKind;
  /** Required when kind is recurring — minutes between deliveries. */
  intervalMinutes?: number;
  status: TaskReminderStatus;
  sentAt?: Date;
  lastSentAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

/** Architect/engineer email successfully sent by `onTaskCommentCreated` (SMTP + prefs enabled). */
export interface TaskCommentExternalEmailDelivery {
  userId: string;
  recipientName: string;
  role: 'architect' | 'engineer';
  eventType: 'comment' | 'mention';
  emailSubject: string;
  sentAt: Date;
}

/** Per-recipient SMTP log written by `onTaskCommentCreated` (success and failure). */
export interface TaskCommentEmailDelivery {
  recipientUserId: string;
  recipientEmail: string;
  recipientName: string;
  recipientRole: string;
  eventType: 'comment' | 'mention';
  status: 'sent' | 'failed';
  emailSubject?: string;
  error?: string;
  sentAt: Date;
  triggeredByUserId: string;
  triggeredByName: string;
}

/** Optional categorization for richer task discussions; rendered as a colored chip in the meta row. */
export type TaskCommentKind = 'decision' | 'question' | 'action' | 'note';

export interface TaskComment {
  id?: string;
  taskId: string;
  /** Set on new comments so Cloud Functions can resolve onboarding checklist tasks (`onboarding-task:…`). */
  projectId?: string | null;
  /**
   * Consolidated group thread key (`${dealId||projectId}::${normalizedGroupName}`).
   * Present on comments posted from the group-level comments popup; lets comments aggregate
   * across the deal's Preconstruction and Under Contract projects without a per-task id.
   */
  groupCommentKey?: string;
  /**
   * Design Comments visibility. Missing = working notes (LCTF only).
   * `architect` = Design decision the assigned architect can see and reply to.
   */
  audience?: 'internal' | 'architect';
  /** Hub deal id when posted from a group thread (helps supplier Production ACL). */
  dealId?: string;
  /**
   * Precomputed notification targets for group comments (union of assignees + action-required
   * across the group's tasks). Used by the Cloud Function instead of resolving a single task.
   */
  notifyUserIds?: string[] | null;
  authorId: string;
  authorName: string;
  authorInitials?: string;
  /** Plain-text body with `@FirstName ` mention tokens. Preserved for the Slack/email mirror Cloud Function (onTaskCommentCreated). */
  body: string;
  /** Sanitized rich HTML rendition. When present, the renderer prefers this over `body`. */
  bodyHtml?: string;
  /** Marker for the body format ('html' = rich, 'plain' = legacy). Renderer falls back to plain when absent. */
  bodyFormat?: 'html' | 'plain';
  attachments?: CommentAttachment[];
  mentions?: CommentMention[];
  /** True when the comment includes an @team channel broadcast. */
  teamMention?: boolean;
  /** Full SMTP log (success + failure) — preferred over externalEmailDeliveries */
  emailDeliveries?: TaskCommentEmailDelivery[];
  /** @deprecated Legacy: successful architect/engineer sends only */
  externalEmailDeliveries?: TaskCommentExternalEmailDelivery[];
  source?: string;
  /** When set, this comment is a Design-group card for a completed plan review. */
  planReviewId?: string;
  planReviewFileName?: string;
  /** When set, the thread shows a card that opens this project's inspiration board. */
  inspirationBoard?: {
    leadId: string;
    dealId: string;
  };
  /** Client-only; reaction updates do not trigger Slack/email. */
  reactions?: CommentReactionsMap;
  /** Last-edit metadata (set by inline edit). Triggers an "(edited)" pill in the UI. */
  editedAt?: Date;
  editedById?: string;
  /** Pin metadata. Pinned comments render in a "Pinned" section above the chronological feed. */
  pinnedAt?: Date | null;
  pinnedById?: string | null;
  /** Optional discussion-kind tag — Decision / Question / Action / Note. */
  kind?: TaskCommentKind;
  /** When set, this comment is a reply to the comment with this id; rendered indented under its parent. */
  parentId?: string | null;
  createdAt: Date;
  updatedAt?: Date;
}

export type TaskActivityDelayCause =
  | 'supplier_delay'
  | 'supplier_advance'
  | 'cnc_delay'
  | 'cnc_advance'
  | 'weather_delay'
  | 'internal_delay'
  | 'internal_advance'
  | 'other';

export interface TaskActivityLog {
  id?: string;
  taskId: string;
  actorId: string;
  actorName: string;
  action: string;
  field?: string;
  oldValue?: string;
  newValue?: string;
  /** UID or external sentinel for actionRequired old value */
  oldValueKey?: string;
  /** UID or external sentinel for actionRequired new value */
  newValueKey?: string;
  projectId?: string;
  delayCause?: TaskActivityDelayCause;
  moveId?: string;
  oldStartMs?: number;
  oldEndMs?: number;
  newStartMs?: number;
  newEndMs?: number;
  createdAt: Date;
}

export interface Milestone {
  id?: string;
  projectId: string;
  title: string;
  description?: string;
  targetDate: Date;
  completedDate?: Date;
  status: MilestoneStatus;
  /** FS predecessors (task ids or `milestone:<docId>`); drives scheduling with tasks. */
  dependencyEdges?: TaskDependencyEdge[];
  /** Hard-pin: scheduler skips this milestone; drag is disabled. Mirrors ProjectTask. */
  scheduleLocked?: boolean;
  lockedAt?: Date;
  lockedBy?: string;
  createdAt: Date;
  updatedAt: Date;
}

// ==========================================
// OPERATIONS HUB
// ==========================================

export type OpsCategory = 'admin' | 'maintenance' | 'ordering' | 'requests' | 'other';
export type RecurFrequency = 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'quarterly';

export type OpsTaskOriginKind = 'meeting' | 'email' | 'agent' | 'manual';

/** Provenance for agent- or meeting-sourced OPS tasks. */
export interface OpsTaskOrigin {
  kind: OpsTaskOriginKind;
  label?: string;
  meetingId?: string;
  meetingTitle?: string;
  meetingDate?: string;
  emailThreadId?: string;
  agentName?: string;
}

export interface OpsTask {
  id?: string;
  title: string;
  description?: string;
  category: OpsCategory;
  assignedTo: string[];
  collaborators?: string[];
  status: TaskStatus;
  priority: TaskPriority;
  startDate?: Date;
  dueDate?: Date;
  estimatedHours?: number;
  recurring?: { frequency: RecurFrequency; nextDue?: Date };
  requestedBy?: string;
  /** CRM user uid, or `ACTION_REQUIRED_EXTERNAL_SENTINEL` when waiting on an external party (name in `actionRequiredNote`). */
  actionRequired?: string;
  /** When `actionRequired` is external sentinel: who we are waiting on. */
  actionRequiredNote?: string;
  /** Checklist items (id, title, completed, position). Same Subtask shape as PM. */
  subtasks?: Subtask[];
  tags?: string[];
  /** Where this task came from (agent API / meeting pipeline). */
  origin?: OpsTaskOrigin;
  projectId?: string;
  dealId?: string;
  /** Meeting note id when created via createOpsTaskFromMeeting. */
  sourceMeetingNoteId?: string;
  /** Action item id on that note (part of the idempotency key). */
  sourceMeetingActionItemId?: string;
  /** `${meetingNoteId}::${actionItemId}` — prevents duplicate creates. */
  meetingActionIdempotencyKey?: string;
  /** Granola or Fathom when created from a meeting action item. */
  meetingSource?: 'fathom' | 'granola';
  createdAt: Date;
  updatedAt: Date;
  completedAt?: Date;
  createdBy: string;
  /** Set when this doc was spawned by completing a recurring ops task (not a user-initiated create). */
  createdReason?: 'recurring_next';
}

export interface OpsComment {
  id?: string;
  taskId: string;
  userId: string;
  userName: string;
  userInitials?: string;
  /** Plain-text body (legacy + notification mirror). */
  text: string;
  bodyHtml?: string;
  bodyFormat?: 'html' | 'plain';
  mentions?: CommentMention[];
  attachments?: CommentAttachment[];
  source?: string;
  emailSource?: ExecEmailSource;
  kind?: TaskCommentKind;
  parentId?: string | null;
  /** Client-only; reaction updates do not trigger Slack/email. */
  reactions?: CommentReactionsMap;
  createdAt: Date;
  updatedAt?: Date;
}

export type InviteStatus = 'pending' | 'accepted' | 'revoked';

export interface ArchitectInvite {
  id?: string;
  email: string;
  name: string;
  invitedBy: string;
  invitedByName: string;
  status: InviteStatus;
  /** Missing on legacy designer invites. */
  inviteKind?: 'designer' | 'architect';
  acceptedAt?: Date;
  acceptedUid?: string;
  /** Firebase Auth uid created when the invite email is sent, before they accept. */
  authUid?: string;
  createdAt: Date;
}

export interface EngineerInvite {
  id?: string;
  email: string;
  name: string;
  invitedBy: string;
  invitedByName: string;
  status: InviteStatus;
  acceptedAt?: Date;
  acceptedUid?: string;
  createdAt: Date;
}

/** Internal employee / admin / staff email invite (Firestore: `employeeInvites`). */
export type EmployeeInviteRole = Extract<
  UserRole,
  'employee' | 'employee_shop' | 'employee_office' | 'admin' | 'staff'
>;

export interface EmployeeInvite {
  id?: string;
  email: string;
  name: string;
  role: EmployeeInviteRole;
  designations?: EmployeeDesignation[];
  isWorkstation?: boolean;
  invitedBy: string;
  invitedByName: string;
  status: InviteStatus;
  acceptedAt?: Date;
  acceptedUid?: string;
  createdAt: Date;
}

/** Invite flow mirrors `architectInvites` (Firestore: `cpaInvites`). */
export interface CpaInvite {
  id?: string;
  email: string;
  name: string;
  invitedBy: string;
  invitedByName: string;
  status: InviteStatus;
  acceptedAt?: Date;
  acceptedUid?: string;
  createdAt: Date;
}

/** Vendor capability flags (timber RFQ, CNC quote/production). */
export type VendorCapability = 'timber' | 'cnc';

/** Timber/CNC vendor company (Firestore: `supplierOrgs` — name kept for backward compatibility). */
export interface SupplierOrg {
  id?: string;
  name: string;
  defaultContactEmail?: string;
  /** First name used in RFQ publish email greeting (RFQ notifications section). */
  defaultContactFirstName?: string;
  notes?: string;
  /** Defaults to `['timber']` when absent on legacy docs. */
  capabilities?: VendorCapability[];
  cncDefaultFlatFee?: number;
  cncDefaultRatePerBdft?: number;
  leadTimeWeeks?: number;
  serviceRegion?: string;
  createdByUid?: string;
  createdByName?: string;
  createdAt: Date;
  updatedAt?: Date;
}

/** Invite flow mirrors `architectInvites` (Firestore: `supplierInvites`). */
export interface SupplierInvite {
  id?: string;
  email: string;
  name: string;
  supplierOrgId: string;
  /** Copied onto `users/{uid}` when the invite creates the account. */
  supplierDesignations?: SupplierWorkFlag[];
  /** Copied onto `users/{uid}` when the invite creates the account. Missing → Owner. */
  supplierPortalRole?: SupplierPortalRole;
  invitedBy: string;
  invitedByName: string;
  status: InviteStatus;
  acceptedAt?: Date;
  acceptedUid?: string;
  createdAt: Date;
}

/**
 * Denormalized award ACL for Production group comments.
 * Doc id is `${dealId}::production` (`productionCommentAccess/{id}`).
 */
export interface ProductionCommentAccess {
  id?: string;
  dealId: string;
  timberOrgIds: string[];
  cncOrgIds: string[];
  /** True when any Hub project for this deal is `phase === 'contracted'`. */
  underContract?: boolean;
  /** Hub timber project to attach new comments to (prefer contracted). */
  hubProjectId?: string;
  updatedAt?: Date;
}

export type RfqProjectStatus = 'invited' | 'active' | 'quoted' | 'closed';

/** Work package type — timber RFQ or CNC quote request. */
export type VendorWorkType = 'timber_rfq' | 'cnc_quote' | 'other_quote';

/** Dryness requirement shown in timber RFQ emails and the supplier portal. */
/** Timber RFQ moisture requirement for a species or quote line. `both` is legacy stored data only. */
export type TimberRfqDrynessSpec = 'green' | 'kiln_dried' | 'both';

/** Where timber / CNC packages should be delivered for a supplier invite. */
export type SupplierShipToPreset = 'aspers' | 'job_site' | 'custom';

export interface SupplierShipToAddress {
  recipient?: string;
  line1: string;
  line2?: string;
  city: string;
  state: string;
  zip: string;
}

/** Proposal structure a supplier is scoped to quote (id + name; name is the fallback match for hub-CSV releases). */
export interface TimberSupplierStructureRef {
  id: string;
  name: string;
}

/** Who provides the raw stock for a CNC-assigned species. */
export type CncMaterialSourceKind = 'supplier_provided' | 'lctf_shipped';

/** One species a CNC shop is asked to cut, with board feet and material flow. */
export interface CncSpeciesAssignment {
  species: string;
  /** Board feet for this species — prefilled from the proposal timber list; staff-editable. */
  boardFeet?: number;
  materialSource: CncMaterialSourceKind;
  /** Timber supplier org providing the raw stock when LCTF ships it to the shop. */
  sourceSupplierOrgId?: string;
  /** Denormalized source supplier name for email/portal display. */
  sourceSupplierName?: string;
  note?: string;
}

/** Per-supplier timber species requested on a timber RFQ. */
export interface TimberSupplierInvite {
  supplierOrgId: string;
  requestedSpecies: string[];
  /** Dryness requirement per species name (falls back to {@link drynessSpec}, then project default). */
  speciesDryness?: Record<string, TimberRfqDrynessSpec>;
  /** @deprecated Legacy supplier-wide dryness; use {@link speciesDryness} per species. */
  drynessSpec?: TimberRfqDrynessSpec;
  /** Delivery destination preset — resolved against project site address when `job_site`. */
  shipToPreset?: SupplierShipToPreset;
  /** Custom delivery address when {@link shipToPreset} is `custom`. */
  shipToCustom?: SupplierShipToAddress;
  /** 'selected' limits this supplier's quote request to {@link structures}. Absent/'all' = every structure. */
  structureScope?: 'all' | 'selected';
  /** Structures this supplier quotes when {@link structureScope} is 'selected'. */
  structures?: TimberSupplierStructureRef[];
  /**
   * Species to price per structure (structure id → subset of {@link requestedSpecies}).
   * Absent, or a structure with no entry, means every requested species applies to it.
   * Drives the per-structure CSV / PDF / email list and the portal.
   */
  structureSpecies?: Record<string, string[]>;
  /**
   * Who at this supplier receives RFQ emails for this project (lowercase emails chosen
   * from the org's RFQ contact, portal users, and pending invites). Absent = everyone.
   */
  notifyEmails?: string[];
  /** CNC quote scope — species this shop is asked to cut, with material flow (cnc_quote projects only). */
  cncSpeciesAssignments?: CncSpeciesAssignment[];
  /** Internal CRM: PM/staff edited {@link cncSpeciesAssignments} board feet / notes. */
  pmCncInputAdjustedAtIso?: string;
  pmCncInputAdjustedByName?: string;
}

/** Links a vendor org to a deal / scope (Firestore: `rfqProjects`). */
export interface RfqProject {
  id?: string;
  supplierOrgId: string;
  supplierOrgName?: string;
  /** All orgs that may receive packages for this RFQ (includes {@link supplierOrgId} when backfilled). */
  supplierOrgIds?: string[];
  /** Timber species requested from each invited supplier. */
  timberSupplierInvites?: TimberSupplierInvite[];
  /** @deprecated Legacy project-wide dryness; use {@link TimberSupplierInvite.speciesDryness} per species. */
  timberDrynessSpec?: TimberRfqDrynessSpec;
  dealId: string;
  dealTitle?: string;
  timberProjectId?: string;
  timberProjectName?: string;
  /** Defaults to `timber_rfq` on legacy docs. */
  workType?: VendorWorkType;
  /** Board feet for CNC quote packages. */
  boardFeet?: number;
  /** Species or timber notes for CNC context. */
  speciesNotes?: string;
  /** Quote deadline shown to vendors. */
  quoteDeadline?: Date;
  status: RfqProjectStatus;
  /** When set, this org is the approved/awarded supplier for this work type on the deal. */
  approvedSupplierOrgId?: string;
  /** Timber RFQ — individual quote submissions staff have approved. */
  approvedSubmissionIds?: string[];
  /** Timber RFQ — staff initials recorded when each submission was approved. */
  approvedSubmissionApproverInitials?: Record<string, string>;
  /** Timber RFQ — when each submission was approved. */
  approvedSubmissionApprovedAt?: Record<string, Date>;
  /** Timber RFQ — approvals scoped per release (pre-portal id or hubFileReleaseId). */
  approvedSubmissionIdsByRelease?: Record<string, string[]>;
  /**
   * CNC RFQ — awarded supplier scoped per release (pre-portal id or hubFileReleaseId).
   * @deprecated Superseded by {@link approvedSupplierOrgIdsByRelease} (multi-award);
   * kept mirrored to the first award for older readers.
   */
  approvedSupplierOrgIdByRelease?: Record<string, string>;
  /**
   * CNC RFQ — awarded suppliers per release. Multiple suppliers can be awarded the
   * same release when structure-split quotes each cover part of the order.
   */
  approvedSupplierOrgIdsByRelease?: Record<string, string[]>;
  /** CNC RFQ — staff initials when each supplier org was approved (releaseId → orgId → initials). */
  approvedSupplierApproverInitialsByRelease?: Record<string, Record<string, string>>;
  /** CNC RFQ — when each supplier org was approved (releaseId → orgId → date). */
  approvedSupplierApprovedAtByRelease?: Record<string, Record<string, Date>>;
  createdAt: Date;
  updatedAt?: Date;
  createdByUid?: string;
  createdByName?: string;
}

/** CNC-specific payload on a published package. */
export interface CncPackagePayload {
  /** Board feet this shop quotes — sum of {@link speciesAssignments} board feet when split by species. */
  boardFeet: number;
  speciesNotes?: string;
  /** IDs of vendorFileReleases attached to this package. */
  releasedFileIds?: string[];
  /** Species split + material flow snapshot at publish time (per supplier). */
  speciesAssignments?: CncSpeciesAssignment[];
  /** Structures this shop quotes, snapshotted at publish; absent = all structures. */
  structures?: TimberSupplierStructureRef[];
}

/** Immutable published vendor package (Firestore: `rfqPackages`). */
/**
 * Why a staged release / published package exists.
 * `quote` (default) asks suppliers to price it. `reference` is a refined model or
 * list shared after a quote was already approved — suppliers cut from it, they do
 * not requote it, and it never counts toward quote coverage.
 */
export type RfqReleasePurpose = 'quote' | 'reference';

export interface RfqPackage {
  id?: string;
  rfqProjectId: string;
  supplierOrgId: string;
  dealId: string;
  dealTitle?: string;
  /** Defaults to `timber_rfq` on legacy docs. */
  workType?: VendorWorkType;
  /** Copied from the staged release at publish. Absent = `quote`. */
  releasePurpose?: RfqReleasePurpose;
  proposalVersionId?: string | null;
  /** Present when published from live builder without a saved version. */
  proposalSnapshot?: { nodeSets: ProposalNodeSet[] };
  label?: string;
  /** Staged release display name shown to suppliers (e.g. "Timber List with joinery"). */
  releaseDisplayLabel?: string;
  /** Where the published timber list snapshot came from (for staff context). */
  timberListSource?: 'proposal_live' | 'proposal_saved_version' | 'hub_csv' | 'hub_staged_release';
  /** When timberListSource is hub_staged_release, the staged hub release doc id. */
  hubFileReleaseId?: string;
  /** Staged hub release version (v1, v2, …) shown to suppliers. */
  releaseVersionIndex?: number;
  /** Proposal version snapshot linked at stage/publish time (denormalized). */
  linkedProposalVersionId?: string | null;
  linkedProposalVersionLabel?: string;
  linkedProposalVersionSource?: ProposalVersionSource;
  linkedProposalVersionSavedAt?: Date;
  linkedFrom?: 'proposal_push' | 'hub_file_stage';
  /** CNC quote context when workType is cnc_quote. */
  cncPackagePayload?: CncPackagePayload;
  createdAt: Date;
  createdByUid?: string;
  createdByName?: string;
  /** Optional staff-edited message included in supplier notification email. */
  customEmailMessage?: string;
  /** Resolved delivery address snapshot at publish time (shown in email, PDF, portal). */
  shipToAddress?: SupplierShipToAddress;
  /** Preset used when published — for staff context in the hub. */
  shipToPreset?: SupplierShipToPreset;
  /** Synthetic package created by staff legacy import (pre-portal quotes). */
  legacyImport?: boolean;
  /**
   * Package-only CNC version created when staff split a combined timber+CNC quote
   * before any `.3D` was staged. Not a hubFileReleases row; not {@link legacyImport}.
   */
  quotePlaceholder?: boolean;
  /**
   * Structure refs this package covers, snapshotted from the supplier's invite at
   * publish or scope-revision time; absent = all structures. Enables exact drift
   * detection against the invite's current selection.
   */
  scopeStructures?: TimberSupplierStructureRef[];
  /**
   * Structure ids dropped from this package after a later RFQ covered them.
   * Hidden from the supplier portal so the mill is not asked to quote them twice.
   */
  archivedStructureIds?: string[];
  /**
   * Species per structure snapshotted from the supplier's invite at publish or
   * scope-revision time (structure id → species). Absent = every requested species
   * on every structure. Compared against the invite to flag species drift.
   */
  structureSpecies?: Record<string, string[]>;
  /** Number of scope revisions resent for this package (absent = never revised). */
  scopeRevisionCount?: number;
  /** When staff last resent this package with a changed structure scope. */
  scopeRevisedAt?: Date;
  scopeRevisedByName?: string;
  /** When staff dismissed a scope-drift reminder without emailing the supplier. */
  scopeDriftAcknowledgedAt?: Date;
  scopeDriftAcknowledgedByName?: string;
  /**
   * Every staged release this package carries. A Files → RFQs round publishes one
   * package per supplier covering all of their structures, so a package can span
   * several releases; `hubFileReleaseId` stays the first one for older readers.
   */
  hubFileReleaseIds?: string[];
  // ── Request-round envelope (set when published from Files → RFQs "Request quotes"). ──
  /** `rfqRequests/{id}` this package was sent under. Absent on packages published from the Suppliers tab. */
  rfqRequestId?: string;
  /** Which line (structure lane) of the request this package carries (first line when it spans several). */
  rfqRequestLineKey?: string;
  /** Every request line (structure lane) this package covers. */
  rfqRequestLineKeys?: string[];
  /** Denormalized request header for the supplier portal (suppliers cannot read `rfqRequests`). */
  requestLabel?: string;
  requestStage?: RfqRequestStage;
  requestMessage?: string;
  /** Quote due date shown to the supplier. */
  quoteDeadline?: Date;
  /** Previous package for the same supplier + structure lane that this newer version replaces. */
  supersedesPackageId?: string;
}

/**
 * What a request round is pricing: a concept-level list (budgetary) or the
 * production list suppliers will actually cut to. Older rounds stored the
 * project-file LOD (`estimating`, `conceptual`, `joinery`, `supplementary_details`);
 * `rfqRequestStageLabel` maps those for display.
 */
export type RfqRequestStage = 'concept' | 'production';

/** Request-round header fields copied onto each package at publish time. */
export type RfqPackageRequestEnvelope = Required<Pick<RfqPackage, 'rfqRequestId' | 'rfqRequestLineKey' | 'requestLabel'>> &
  Pick<RfqPackage, 'rfqRequestLineKeys' | 'requestStage' | 'requestMessage' | 'quoteDeadline'>;

/**
 * One structure lane of a request round: which timber-list version each supplier
 * received for these structures, and the package it produced per supplier org.
 */
export interface RfqRequestLine {
  /** Stable key within the request (sorted structure ids joined, or `whole_project`). */
  key: string;
  /** Structures this lane quotes; empty = whole project. */
  structures: TimberSupplierStructureRef[];
  sourceStreamId: string;
  sourceFileId: string;
  versionIndex?: number;
  versionLabel?: string;
  /** Staged `rfqProjects/{rfqProjectId}/hubFileReleases/{id}` reused or created for this lane. */
  hubFileReleaseId: string;
  displayLabel: string;
  /** Species on this structure's list at send time (proposal), shown per structure in the email. */
  species?: string[];
  /** Cross-section inches stay as written in the source CSV. */
  trueDimensions?: boolean;
  /** supplierOrgId → rfqPackages doc id. */
  packageIdByOrg: Record<string, string>;
}

/**
 * A request round staff sent from Files → RFQs (Firestore: `rfqRequests`, staff-only).
 * The envelope around N packages: one label / stage / due date / message, one email per
 * supplier, and the record of which version of each structure's list was requested.
 */
export interface RfqRequest {
  id?: string;
  dealId: string;
  projectId: string;
  rfqProjectId: string;
  workType: 'timber_rfq';
  label: string;
  stage?: RfqRequestStage;
  message?: string;
  quoteDeadline?: Date;
  sentAt: Date;
  sentByUid: string;
  sentByName?: string;
  supplierOrgIds: string[];
  lines: RfqRequestLine[];
}

/** Structured CNC quote fields (optional alongside file upload). */
export interface CncQuoteFields {
  flatFee?: number;
  ratePerBdft?: number;
  leadTimeWeeks?: number;
  notes?: string;
  /** Computed total when flat + variable provided. */
  totalEstimate?: number;
}

/** Vendor quote upload metadata (Firestore: `rfqSubmissions`). */
export interface RfqSubmission {
  id?: string;
  rfqPackageId: string;
  rfqProjectId: string;
  supplierOrgId: string;
  dealId: string;
  workType?: VendorWorkType;
  fileName: string;
  /** Empty when {@link notBidding} is true (no file stored). */
  storagePath: string;
  /** When true, supplier declined to quote (no PDF/CSV upload). */
  notBidding?: boolean;
  /** Structured CNC quote when workType is cnc_quote. */
  cncQuote?: CncQuoteFields;
  /** Canonical dollar total for staff reporting (required on new submissions). */
  quoteTotal?: number;
  /**
   * Original combined timber+CNC total before staff split. Historical only —
   * {@link quoteTotal} is timber-only (or CNC-only on the linked CNC submission) after split.
   */
  combinedQuoteTotal?: number;
  /** CNC dollar slice carved from {@link combinedQuoteTotal} at split time. */
  cncSplitAmount?: number;
  /** When staff committed a combined → timber/CNC split. */
  splitAt?: Date;
  splitByUid?: string;
  splitByName?: string;
  /** How the CNC slice was entered in the split modal. */
  splitMethod?: 'rate_x_bf' | 'manual';
  /** Timber submission → CNC submission created by the split. */
  linkedCncSubmissionId?: string;
  /** CNC submission → source timber submission. */
  linkedTimberSubmissionId?: string;
  /**
   * CNC submission (and package) created from a combined quote before any `.3D`
   * existed. Distinct from {@link legacyImport}.
   */
  quotePlaceholder?: boolean;
  /** Staff backfill marker (historical pre-portal quotes). */
  legacyImport?: boolean;
  /** PM/EA uploaded quote PDF on behalf of a non-portal supplier. */
  staffManualUpload?: boolean;
  /** Timber RFQ: species this submission quotes. */
  quotedSpecies?: string;
  /**
   * Structures this quote covers (subset of the supplier’s assigned scope).
   * Used for timber and CNC RFQs. Absent / empty = full assigned scope for that package.
   */
  quotedStructures?: TimberSupplierStructureRef[];
  /** Timber RFQ: dryness quoted on this submission (independent per quote row). */
  quotedDryness?: TimberRfqDrynessSpec;
  /** Timber pre-portal / staff quotes: delivery lead time range in whole weeks. */
  leadTimeWeeksMin?: number;
  leadTimeWeeksMax?: number;
  /** @deprecated Early backfill — use {@link leadTimeWeeksMin} / {@link leadTimeWeeksMax}. */
  leadTimeWeeks?: number;
  /** Timber RFQ: optional notes from the supplier on this quote submission. */
  notes?: string;
  /** Other quotes: free-text scope label (e.g. steel connectors). */
  quoteLabel?: string;
  /** Other quotes: supplier quote validity / response due date. */
  quoteValidUntil?: Date;
  /** Other quotes: free-text vendor name when not a portal supplier org. */
  supplierOrgName?: string;
  /** Optional payment terms draft from staff upload — prefills approval/AP modal. */
  paymentTermsDraft?: import('../utils/supplierBills').SupplierPaymentTermsSpec;
  /** Job PO created from this Other Quote upload. */
  purchaseRequisitionId?: string;
  /** Issued PO number stamped when the linked job PO is approved. */
  poNumber?: string;
  /** Proposal node copied onto the linked job PO. */
  plBucket?: import('./purchasing').PurchaseJobPlBucket;
  /** `company_card` or `ach_office` for Other Quotes; other ids from Purchasing. */
  paymentMethodId?: string;
  paymentMethod?: string;
  /** Staff marked this quote rejected (after awarding another quote). */
  rejectedAt?: Date;
  rejectedByUid?: string;
  rejectedByName?: string;
  rejectionReason?: string;
  rejectionNoticeSentAt?: Date;
  downloadUrl?: string;
  contentType?: string;
  sizeBytes?: number;
  uploadedByUid: string;
  uploadedByName?: string;
  createdAt: Date;
  /** Internal CRM: PM/staff corrected quote fields after supplier submit. */
  pmQuoteInputAdjustedAt?: Date;
  pmQuoteInputAdjustedByUid?: string;
  pmQuoteInputAdjustedByName?: string;
}

/** Staff-staged hub file release (subcollection: `rfqProjects/{id}/hubFileReleases`). */
export interface RfqHubFileRelease {
  id?: string;
  rfqProjectId: string;
  dealId: string;
  workType: VendorWorkType;
  sourceProjectId: string;
  sourceFileId: string;
  sourceStreamId: string;
  sourceStreamKind: ProjectFileStreamKind;
  sourceStreamTitle: string;
  /** Copied from the source timber CSV at stage time. Live file flag wins when it can be read. */
  trueDimensions?: boolean;
  /**
   * Structures the source track covered when staged (snapshot of the stream's
   * tags; empty = whole project). Drives supplier-side grouping and the publish
   * scope fallback without re-reading hub data the supplier cannot see.
   */
  structureIds?: string[];
  structureNames?: string[];
  /** Quoting release (default) or reference release for an already-approved quote. */
  releasePurpose?: RfqReleasePurpose;
  versionIndex?: number;
  versionLabel?: string;
  displayLabel: string;
  releaseVersionIndex: number;
  storagePath: string;
  releasedUrl: string;
  fileName: string;
  mimeType?: string;
  sizeBytes?: number;
  releasedByUid: string;
  releasedByName?: string;
  createdAt: Date;
  /** Proposal version snapshot this RFQ release corresponds to. */
  linkedProposalVersionId?: string | null;
  linkedProposalVersionLabel?: string;
  linkedProposalVersionSource?: ProposalVersionSource;
  linkedProposalVersionSavedAt?: Date;
  linkedFrom?: 'proposal_push' | 'hub_file_stage';
}

/** Immutable file release to a vendor (subcollection: `rfqProjects/{id}/vendorFileReleases`). */
export interface VendorFileRelease {
  id?: string;
  rfqProjectId: string;
  supplierOrgId: string;
  dealId: string;
  /** Source `projectFiles/{id}` doc this release was copied from. */
  sourceFileId: string;
  sourceStreamId: string;
  sourceStreamTitle: string;
  sourceProjectId: string;
  versionIndex?: number;
  versionLabel?: string;
  displayLabel: string;
  releaseVersionIndex: number;
  /** Storage path under `vendor-releases/...`. */
  storagePath: string;
  releasedUrl: string;
  fileName: string;
  mimeType?: string;
  sizeBytes?: number;
  releasedByUid: string;
  releasedByName?: string;
  createdAt: Date;
}

export type VendorPortalActivityType =
  | 'page_view'
  | 'download'
  | 'upload'
  | 'acknowledge'
  | 'heartbeat';

/** Vendor portal activity log (Firestore: `vendorPortalActivity`). */
export interface VendorPortalActivity {
  id?: string;
  supplierOrgId: string;
  rfqProjectId?: string | null;
  rfqPackageId?: string | null;
  sessionId?: string;
  type: VendorPortalActivityType;
  action: string;
  metadata?: Record<string, unknown> | null;
  userAgent?: string;
  source: 'vendor' | 'employee';
  timestamp: Date;
}

/** Per-user vendor portal UI preferences (Firestore: `vendorPortalUserPrefs/{uid}`). */
export interface VendorPortalUserPrefs {
  tourCompletedByOrg?: Record<string, Date>;
  tourSkippedByOrg?: Record<string, Date>;
  updatedAt?: Date;
}

/** Fabrication routing decision on a timber project. */
export type FabricationMethod = 'undecided' | 'hand' | 'cnc' | 'comparing';

/** Persisted hand-cut vs CNC calculator inputs for a project. */
export interface ProjectHandCutVsCncState {
  boardFeet: number;
  handCutMode: 'labor' | 'directBdft';
  crewCount: number;
  cutDays: number;
  hoursPerDay: number;
  laborRatePerHour: number;
  handCutDollarPerBdft: number;
  cncDollarPerBdft: number;
  cncFlatFee: number;
  handOverhead: number;
  handConsumables: number;
  handDieselGallons: number;
  handDieselPricePerGal: number;
  handShipping: number;
  cncOverhead: number;
  cncConsumables: number;
  cncDieselGallons: number;
  cncDieselPricePerGal: number;
  cncShipping: number;
  updatedAt?: Date;
  updatedByUid?: string;
}

// Email Matching Types
export interface EmailMatch {
  entityType: 'lead' | 'contact' | 'deal';
  entityId: string;
  entityName: string;
  matchScore: number;
  matchReasons: string[];
}

// Task types
export interface Task {
  id?: string;
  title: string;
  description?: string;
  assignedTo?: string;
  relatedTo: {
    entityType: 'lead' | 'contact' | 'deal';
    entityId: string;
  };
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'in-progress' | 'completed' | 'cancelled';
  dueDate?: Date;
  createdAt?: Date;
}

// Payment types
export interface Payment {
  id?: string;
  dealId?: string;
  leadId?: string;
  amount: number;
  currency: string;
  description: string;
  paymentMethod: 'cash' | 'check' | 'bank-transfer' | 'credit-card' | 'other' | 'quickbooks';
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  createdAt?: Date;
  paidAt?: Date;
  refundedAt?: Date;

  // QuickBooks integration
  quickbooksPaymentId?: string | null;
  quickbooksRealmId?: string;
  /**
   * Customer prepayment booked in QBO as a bank Deposit to "Customer
   * prepayments" (later netted off an invoice), not as a QBO Payment.
   * `kind` is 'prepayment_deposit' and the doc id is `qbdep_{deposit}_{line}`.
   */
  kind?: 'prepayment_deposit';
  quickbooksDepositId?: string;
  quickbooksDepositLineId?: string;
  prepaymentAccountName?: string;
  /** QBO invoice whose negative "Customer Deposits" line consumed this prepayment. */
  appliedToQuickbooksInvoiceId?: string | null;
  appliedToInvoiceNumber?: string | null;
  customerName?: string;
  customerEmail?: string;
  matchedLeadId?: string;
  matchedLeadName?: string;
  matchedDealId?: string;
  matchedDealTitle?: string;
  paymentDate?: string;
  source?: 'manual' | 'quickbooks-webhook' | 'quickbooks-sync';
  /** Excluded from cash flow / P&L aggregations (admin opted to suppress, e.g. duplicate / out-of-scope). */
  excluded?: boolean;
  /** QB DepositToAccountRef.name — the bank account where the payment was deposited. */
  bankAccountName?: string;
  bankAccountId?: string;
  /**
   * Optional manual split — present when a single QB transaction needs to be
   * spread across multiple projects (most common with design invoices that
   * cover several deals). When `splits` is populated, Project P&L aggregation
   * uses these allocations and ignores `matchedDealId`.
   * The sum of `splits[i].amount` should equal the transaction's total amount.
   */
  splits?: TransactionSplit[];
  splitsBy?: string;
  splitsAt?: Date;
}

// QuickBooks Invoice (synced from QB)
export interface QBInvoice {
  id?: string;
  quickbooksInvoiceId: string;
  quickbooksRealmId?: string;
  invoiceNumber?: string;
  customerName: string;
  customerEmail?: string;
  totalAmount: number;
  balance: number;
  dueDate?: string;
  txnDate: string;
  status: 'open' | 'paid' | 'overdue' | 'voided' | 'partial' | 'removed_in_qb' | 'credited_for_consolidation';
  matchedLeadId?: string;
  matchedLeadName?: string;
  matchedDealId?: string;
  matchedDealTitle?: string;
  linkedPaymentIds?: string[];
  lineItems?: QBInvoiceLineItem[];
  syncedAt: Date;
  createdAt: Date;
}

export interface QBInvoiceLineItem {
  description: string;
  amount: number;
  quantity?: number;
  unitPrice?: number;
}

// QuickBooks Expense (synced from QB — Purchase + Bill entities)
export interface QBExpense {
  id?: string;
  quickbooksId: string;
  quickbooksRealmId: string;
  entityType: 'purchase' | 'bill';
  vendorName: string;
  customerName?: string;
  totalAmount: number;
  /** QuickBooks Bill.Balance when entityType is `bill`. */
  balance?: number;
  /** QuickBooks Bill.DueDate (YYYY-MM-DD) when entityType is `bill`. */
  dueDate?: string;
  /** QuickBooks DocNumber when present on the mirrored Bill/Purchase. */
  docNumber?: string;
  txnDate: string;
  accountName: string;
  accountId: string;
  description?: string;
  paymentType?: string;
  lineItems: QBExpenseLineItem[];
  matchedDealId?: string;
  matchedDealTitle?: string;
  category: 'direct' | 'overhead' | 'unmatched';
  /**
   * Bank/credit-card account the funds came from (Purchase.AccountRef.name).
   * Bills don't have a bank source until they're paid, so this stays empty
   * until a BillPayment links them.
   */
  bankAccountName?: string;
  bankAccountId?: string;
  /** Last four digits parsed from `bankAccountName` on sync. */
  cardLastFour?: string;
  /** Card owner from `financeSettings/debitCards` registry. */
  assignedUserId?: string;
  assignedUserName?: string;
  /** Sequence card transaction link (issuer-side identity). */
  sequenceTransactionId?: string;
  /** Purchased card PO this charge settled. */
  purchaseRequisitionId?: string;
  poNumber?: string;
  categorizedVia?: 'purchase_order' | 'user';
  /** Sequence DebitCard or OmniCard id from registry / matcher. */
  sequenceCardId?: string;
  /** Sequence pod name (e.g. Short Term Ops) when linked via card sync. */
  sequencePodName?: string;
  /** When true, only CRM admins may view or categorize this expense. */
  adminOnlyExpense?: boolean;
  /** Cardholder workflow — pending charges are excluded from P&L until categorized. */
  categorizationStatus?: 'pending' | 'categorized' | 'not_applicable';
  categorizedAt?: Date;
  categorizedBy?: string;
  /** Suppressed from P&L / card inbox (personal charge, duplicate, out of scope). */
  excluded?: boolean;
  excludedReason?: string;
  excludedAt?: Date;
  excludedBy?: string;
  /** When true, QB auto-matcher must not overwrite deal link. */
  matchedDealIdOverridden?: boolean;
  /** Manual split across multiple projects — see Payment.splits. */
  splits?: TransactionSplit[];
  splitsBy?: string;
  splitsAt?: Date;
  /** P&L bucket chosen at categorize time (single source of truth). */
  plBucket?: ProjectPLBucket;
  plBucketLegendId?: string;
  plBucketSource?: 'auto' | 'user' | 'pm_override';
  /** Specific QB account picked within the legend group (QB write-back target). */
  qbAccountChoiceId?: string;
  qbAccountChoiceName?: string;
  qbSyncToken?: string;
  qbWriteBackStatus?: 'pending' | 'synced' | 'failed' | 'skipped' | 'paused';
  qbWriteBackError?: string;
  qbWriteBackAt?: Date;
  syncedAt: Date;
  createdAt: Date;
}

/**
 * One slice of a split QuickBooks transaction. The aggregator credits this
 * slice's `amount` to `dealId`'s P&L instead of the whole transaction.
 *
 * `dealId === null` represents an explicitly unallocated portion (e.g. tax
 * or fees that shouldn't be attributed to any project).
 */
export interface TransactionSplit {
  dealId: string | null;
  /** Snapshot of the deal title at the time of split (for fast UI / audit). */
  dealTitle?: string;
  /** When set, distinguishes precon vs under-contract work on the same deal. */
  projectPhase?: ProjectPhase;
  /** Dollar amount in this slice. Sum across slices must equal the txn total. */
  amount: number;
  /** Optional PM note (e.g. "Design hours for foundation review"). */
  note?: string;
}

export interface QBExpenseLineItem {
  description: string;
  amount: number;
  accountName: string;
  accountId: string;
  className?: string;
  customerName?: string;
  billableStatus?: string;
}

/** Admin registry: company debit card last-four → CRM user for expense categorization. */
export interface DebitCardAssignment {
  id: string;
  lastFour: string;
  assignedUserId: string;
  assignedUserName: string;
  cardLabel?: string;
  bankAccountName?: string;
  bankAccountId?: string;
  /** Primary match key from Sequence.io import (Card ID in Sequence app). */
  sequenceCardId?: string;
  /** Platform API UUID used to match card-transactions when using sk_ keys. */
  sequencePlatformCardId?: string;
  sequenceCardType?: 'debit' | 'omni';
  sequenceHolderName?: string;
  sequencePodId?: string;
  sequencePodName?: string;
  sampleMerchants?: string[];
  /** When true, re-import will not overwrite lastFour (manual correction). */
  lastFourLocked?: boolean;
  isActive: boolean;
  updatedAt: Date;
  updatedBy?: string;
}

/** Firestore doc `financeSettings/debitCards`. */
export interface DebitCardsSettings {
  cards: DebitCardAssignment[];
  updatedAt?: Date;
  updatedBy?: string;
}

/**
 * Project P&L bucket used by the Finances → Project P&L view.
 * Mirrors the proposal cost taxonomy so PM Budget vs Actual lines up
 * with how the proposal was scoped. `unassigned` is a fallback for
 * QB expenses that aren't yet mapped (PM should override).
 */
export type ProjectPLBucket =
  | 'design'
  | 'engineering'
  | 'timber'
  | 'production'
  | 'freight'
  | 'install'
  | 'travel'
  | 'overhead'
  | 'unassigned';

export const PROJECT_PL_BUCKETS: ProjectPLBucket[] = [
  'design',
  'engineering',
  'timber',
  'production',
  'freight',
  'install',
  'travel',
  'overhead',
  'unassigned',
];

/**
 * Per-deal PM overrides for the Project P&L view, stored at
 * `dealPLOverrides/{dealId}` to keep `enhancedDeals` lean. Overrides apply
 * on top of the auto bucket mapping (account-name substring + heuristics).
 */
/**
 * P&L-only revenue adjustment. Does not create a change order and does not
 * change `enhancedDeals.value`, billing, or the payment forecast.
 */
export interface PlReconciliationAdjustment {
  id: string;
  /** Signed whole dollars (negative = reduce recognized P&L contract revenue). */
  amount: number;
  title: string;
  reason: string;
  source: 'finance_agent' | 'crm';
  createdAt: Date;
  createdBy: string;
  idempotencyKey?: string;
  /** When true, this ghost also moves Progress Billing contract value on a job that has a proposal. */
  appliesToProgressBilling?: boolean;
  voidedAt?: Date;
  voidedBy?: string;
}

export interface DealPLOverrides {
  id?: string;                       // matches dealId
  dealId: string;
  /** Per-QB-expense override: expense id → bucket. */
  expenseBuckets?: Record<string, ProjectPLBucket>;
  /** Optional PM note shown on the project row (e.g. context for big variance). */
  notes?: string;
  /**
   * Ghost P&L true-ups. Active (non-voided) amounts are added to Project P&L
   * contract revenue only.
   */
  plReconciliations?: PlReconciliationAdjustment[];
  /**
   * When true the project is dropped from KPI rollups, default table view, and
   * overhead allocation. PMs use this to hide legacy / unsalvageable projects
   * whose QB data can't be reasonably matched.
   */
  excluded?: boolean;
  excludedReason?: string;
  excludedAt?: Date;
  updatedAt?: Date;
  updatedBy?: string;
}

// Legacy Project types (keeping for backward compatibility)
export interface Project {
  name: string;
  description: string;
  status: 'planning' | 'in-progress' | 'completed' | 'on-hold';
  startDate: string;
  endDate?: string;
  clientIds?: string[];
  clientName?: string;
  employeeId?: string;
  location: string;
  timberType: string;
  squareFootage: number;
  budget: number;
  progress: number;
}

export interface ProjectWithId extends Project {
  id: string;
}

export interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  signInWithGoogle: () => Promise<boolean>;
  /** Firebase password-reset email (self-service). Caller should show a generic success message. */
  requestPasswordReset: (email: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  loading: boolean;
  /** Upload a profile image; updates Auth, Firestore `users/{uid}`, and local `user` state. */
  updateProfilePhoto: (file: File) => Promise<void>;
  /** Remove profile image (Firestore + Auth). */
  clearProfilePhoto: () => Promise<void>;
}

// Strategic Map Markers (for wealth indicators, competitor locations, etc.)
export interface StrategicMarkerLayer {
  id?: string;
  name: string;           // e.g., "Porsche Dealerships", "Golf Courses", "Yacht Clubs"
  description?: string;
  icon: string;           // Emoji or icon identifier
  color: string;          // Hex color for markers
  enabled: boolean;       // Whether layer is visible on map
  createdAt: Date;
  updatedAt: Date;
}

export interface StrategicMarker {
  id?: string;
  layerId: string;        // Reference to StrategicMarkerLayer
  name: string;           // e.g., "Porsche of Nashville"
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  coordinates: Coordinates;
  website?: string;
  phone?: string;
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
}

// CSV format for importing strategic markers:
// name,address,city,state,zipCode,latitude,longitude,website,phone,notes
// "Porsche Nashville","1580 Mallory Ln","Brentwood","TN","37027",36.0312,-86.7828,"https://...","+1-615-..."

// ==========================================
// MARKETING SPEND TRACKING
// ==========================================

export type MarketingChannel =
  | 'google-ads'
  | 'facebook-ads'
  | 'instagram-ads'
  | 'linkedin-ads'
  | 'tiktok-ads'
  | 'email-marketing'
  | 'seo'
  | 'content-marketing'
  | 'trade-shows'
  | 'print-advertising'
  | 'direct-mail'
  | 'referral-program'
  | 'other';

export interface MarketingSpend {
  id?: string;
  channel: MarketingChannel;
  campaignName?: string;        // Optional campaign identifier
  utmSource?: string;           // Link to UTM source for attribution
  utmCampaign?: string;         // Link to UTM campaign
  amount: number;               // Spend amount
  period: {
    start: Date;
    end: Date;
  };
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface MarketingBudget {
  id?: string;
  channel: MarketingChannel;
  monthlyBudget: number;
  quarterlyBudget?: number;
  annualBudget?: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface MarketingKPIs {
  // Core financial metrics
  totalSpend: number;
  totalLeads: number;
  totalCustomers: number;       // Won deals
  totalRevenue: number;         // Revenue from won deals

  // Calculated KPIs
  cac: number;                  // Customer Acquisition Cost
  cpl: number;                  // Cost Per Lead
  romi: number;                 // Return on Marketing Investment (%)
  ltvCacRatio: number;          // LTV:CAC ratio

  // Attribution
  marketingSourcedRevenue: number;      // Revenue from marketing-sourced leads
  marketingContributionPercent: number; // % of total revenue from marketing

  // By channel breakdown
  byChannel: ChannelMarketingMetrics[];
}

export interface ChannelMarketingMetrics {
  channel: MarketingChannel;
  channelName: string;          // Display name
  spend: number;
  leads: number;
  customers: number;
  revenue: number;
  cac: number;
  cpl: number;
  romi: number;
  conversionRate: number;
}

// ==========================================
// GOOGLE CALENDAR INTEGRATION
// ==========================================

export interface GoogleCalendarConfig {
  connected: boolean;
  accessToken?: string;
  refreshToken?: string;
  tokenExpiry?: Date;
  selectedCalendars: string[]; // Calendar IDs to sync
  email?: string;              // Google account email
  lastSyncAt?: Date;           // Last time events were synced
}

/** Google calendar row from Executive Calendar delegated list API. */
export interface DelegateCalendarSummary {
  id: string;
  summary?: string;
  primary?: boolean;
  backgroundColor?: string;
  accessRole?: string;
}

/** Saved place for Executive Calendar trip builder (stored on executive user doc). */
export interface SavedCalendarPlace {
  id: string;
  label: string;
  address: string;
  lat?: number;
  lng?: number;
}

/** Per-executive settings for Executive Calendar trip builder. */
export interface ExecutiveCalendarSettings {
  homePlaceId?: string;
  defaultTravelCalendarId?: string;
  savedPlaces: SavedCalendarPlace[];
}

export type CalendarEventGroupAnchorMode = 'activity_start' | 'leave_home';
export type CalendarEventSegmentType = 'travel' | 'activity';

export interface CalendarEventGroupSegment {
  segmentId: string;
  type: CalendarEventSegmentType;
  googleEventId?: string;
  calendarId: string;
  title: string;
  location?: string;
  fromPlaceId?: string;
  toPlaceId?: string;
  durationMinutes: number;
  travelDurationSeconds?: number;
  start?: string;
  end?: string;
}

/** Firestore doc: executiveCalendarEventGroups/{groupId} */
export interface CalendarEventGroup {
  id?: string;
  executiveUid: string;
  anchorMode: CalendarEventGroupAnchorMode;
  anchorSegmentIndex: number;
  segments: CalendarEventGroupSegment[];
  /** Email addresses invited to every Google event in the trip. */
  invitees?: string[];
  createdAt?: Date;
  updatedAt?: Date;
}

export interface GoogleCalendarEvent {
  id: string;
  calendarId: string;
  title: string;
  description?: string;
  start: Date;
  end: Date;
  location?: string;
  htmlLink?: string;           // Link to open in Google Calendar
  isAllDay: boolean;
  colorId?: string;            // Google Calendar color
  status?: 'confirmed' | 'tentative' | 'cancelled';
  // Attendees (optional)
  attendees?: Array<{
    email: string;
    displayName?: string;
    responseStatus?: 'needsAction' | 'declined' | 'tentative' | 'accepted';
  }>;
}

export interface GoogleCalendarList {
  id: string;
  summary: string;             // Calendar name
  description?: string;
  primary?: boolean;           // Is this the user's primary calendar
  backgroundColor?: string;
  foregroundColor?: string;
  accessRole: 'freeBusyReader' | 'reader' | 'writer' | 'owner';
}

// =============================================================================
// Media Library Types
// =============================================================================

export type MediaType = 'image' | 'video' | 'document' | 'model';

export interface MediaItem {
  id?: string;
  name: string;
  url: string;                    // Firebase Storage URL
  /** Storage object path (e.g. media-library/123-file.png); set on new uploads for deletes / debugging */
  storagePath?: string;
  thumbnailUrl?: string;          // For images/videos
  type: MediaType;
  mimeType: string;
  size: number;                   // bytes
  width?: number;                 // For images/videos
  height?: number;                // For images/videos
  duration?: number;              // For videos (seconds)
  uploadedBy: string;             // User ID
  uploadedByName: string;
  tags: string[];                 // User-defined tags for organization
  userTags: string[];             // @mentioned user IDs
  labels: string[];               // Category labels (e.g., 'product', 'brochure')
  description?: string;
  /** Search keywords for the global HTML model catalog (media management). */
  htmlModelDescriptors?: string[];
  /** Optional static screenshot URL for the HTML model catalog (same as project files). */
  htmlPreviewImageUrl?: string;
  usageCount: number;             // Track how often used
  lastUsedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface MediaFolder {
  id?: string;
  name: string;
  parentId?: string;              // For nested folders
  color?: string;
  itemCount: number;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

// =============================================================================
// Gmail Integration Types
// =============================================================================

export interface LinkedEmail {
  id?: string;
  gmailMessageId: string;
  threadId: string;
  userId: string;                 // Which user's inbox
  from: string;
  fromName?: string;
  to: string[];
  cc?: string[];
  subject: string;
  snippet: string;
  bodyHtml?: string;
  bodyText?: string;
  date: Date;
  linkedTo?: {
    type: 'lead' | 'deal' | 'project';
    id: string;
    name: string;
    confidence: 'high' | 'medium' | 'manual';
  };
  needsReview: boolean;           // For low-confidence matches
  isRead: boolean;
  hasAttachments: boolean;
  attachments?: EmailAttachment[];
  labels?: string[];              // Gmail labels
  createdAt: Date;
  updatedAt: Date;
}

export interface EmailAttachment {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  url?: string;                   // If downloaded to storage
}

export interface GmailConnection {
  userId: string;
  email: string;
  accessToken: string;
  refreshToken: string;
  tokenExpiry: Date;
  lastSyncAt?: Date;
  syncEnabled: boolean;
  autoLinkEnabled: boolean;
  connectedAt: Date;
}

// =============================================================================
// Executive Suite — Email Funnel ("Exec Inbox")
// =============================================================================

/**
 * Per-stage agent capability, in increasing order of authority.
 *  observe   – read only
 *  classify  – may move threads into/out of this stage
 *  draft     – may also propose replies (await approval)
 *  autosend  – may also send replies automatically (within guardrails)
 *  autofile  – may also auto-file/auto-archive (e.g. newsletters, receipts)
 */
export type ExecAgentPolicy = 'observe' | 'classify' | 'draft' | 'autosend' | 'autofile';

/** Mailbox-wide ceiling on agent authority (effective capability = min(level, stage policy)). */
export type ExecInboxAutonomyLevel = 0 | 1 | 2 | 3 | 4;

/** Implicit intake stage for INBOX mail with no funnel label yet. */
export const EXEC_INBOX_UNSORTED_STAGE_ID = 'unsorted';

/**
 * A funnel stage. `id` is immutable; `label` and `gmailLabelName` are editable
 * so renaming a stage (or its Gmail label) never breaks threads/policies/history.
 */
export interface ExecInboxStage {
  id: string;
  label: string;
  gmailLabelName: string;
  order: number;
  accent: string;            // hex color used for the pill/dot
  icon: string;              // lucide icon name
  /** Agent may move threads INTO this stage but never auto-respond from it. */
  escalateOnly?: boolean;
  agentPolicy: ExecAgentPolicy;
  /** Soft-delete: hidden from active funnel but still resolvable for old threads. */
  archived?: boolean;
}

/** Seeded default funnel — the current 8 Gmail labels, highest priority first. */
export const DEFAULT_EXEC_INBOX_STAGES: ExecInboxStage[] = [
  { id: 'matt', label: '! Matt', gmailLabelName: '! Matt', order: 0, accent: '#DC2626', icon: 'AlertOctagon', escalateOnly: true, agentPolicy: 'classify' },
  { id: 'to-respond', label: '1 To Respond', gmailLabelName: '1 To Respond', order: 1, accent: '#2563EB', icon: 'Reply', agentPolicy: 'draft' },
  { id: 'review', label: '2 Review', gmailLabelName: '2 Review', order: 2, accent: '#F59E0B', icon: 'Eye', escalateOnly: true, agentPolicy: 'classify' },
  { id: 'responded', label: '3 Responded', gmailLabelName: '3 Responded', order: 3, accent: '#22C55E', icon: 'Check', agentPolicy: 'classify' },
  { id: 'waiting-on', label: '4 Waiting On', gmailLabelName: '4 Waiting On', order: 4, accent: '#7C3AED', icon: 'Clock', agentPolicy: 'classify' },
  { id: 'receipts', label: '5 Receipts/Financials', gmailLabelName: '5 Receipts/Financials', order: 5, accent: '#64748B', icon: 'Receipt', agentPolicy: 'classify' },
  { id: 'newsletters', label: '6 Newsletters', gmailLabelName: '6 Newsletters', order: 6, accent: '#9C9889', icon: 'Newspaper', agentPolicy: 'autofile' },
  { id: 'archive', label: '7 Archive', gmailLabelName: '7 Archive', order: 7, accent: '#94A3B8', icon: 'Archive', agentPolicy: 'autofile' },
];

/** Safety rails applied to every agent mutation. */
export interface ExecInboxGuardrails {
  /** Stage ids the agent may never auto-send from (defaults include matt + review). */
  neverAutosendStages: string[];
  senderAllowList: string[];
  senderDenyList: string[];
  /** 0 disables auto-send entirely. */
  maxAutoSendsPerDay: number;
  /** Local "HH:mm" window during which the agent must not send. */
  quietHours?: { start: string; end: string; tz?: string };
  /** Minimum classifier confidence (0-1) required to act. */
  minConfidence: number;
  /** When true, agent actions are logged but not executed. */
  dryRun: boolean;
}

export const DEFAULT_EXEC_INBOX_GUARDRAILS: ExecInboxGuardrails = {
  neverAutosendStages: ['matt', 'review'],
  senderAllowList: [],
  senderDenyList: [],
  maxAutoSendsPerDay: 0,
  minConfidence: 0.8,
  dryRun: false,
};

/** Stored on the mailbox owner's `users/{uid}` doc under `execInbox`. */
export interface ExecInboxGmailWatch {
  topicName?: string;
  historyId?: string;
  expiration?: Date;
  registeredAt?: Date;
  lastNotificationAt?: Date;
  /** Who registered Gmail users.watch — desktop agent or CRM. */
  watchOwner?: 'crm' | 'agent';
}

/** Stored on the mailbox owner's `users/{uid}` doc under `execInbox`. */
export interface ExecInboxMailboxConfig {
  enabled: boolean;
  /** UIDs (assistants) allowed to operate this mailbox in the suite. */
  delegates: string[];
  /** stageId -> Gmail labelId, cached so renames re-resolve cleanly. */
  labelMap?: Record<string, string>;
  /** Editable stage definitions; seeded from DEFAULT_EXEC_INBOX_STAGES when absent. */
  stages?: ExecInboxStage[];
  /** Master kill switch for agent actions. */
  agentEnabled: boolean;
  autonomyLevel: ExecInboxAutonomyLevel;
  /** Delegate UID whose Google credentials the agent sends through (e.g. Juliana). */
  agentOperatorUid?: string;
  guardrails?: ExecInboxGuardrails;
  /** Bearer key the local Mac-mini agent uses to call the Exec Inbox Agent API. */
  agentApiKey?: string;
  lastSyncAt?: Date;
  /** Gmail push watch cursor (shared with desktop agent Pub/Sub topic). */
  gmailWatch?: ExecInboxGmailWatch;
}

export type ExecSendKind = 'owner' | 'delegate' | 'agent';

/** Who actually performed a send (drives the native "sent by …" attribution). */
export interface ExecSendIdentity {
  email: string;
  displayName: string;
  kind: ExecSendKind;
}

export interface ExecInboxDraft {
  bodyHtml?: string;
  bodyText?: string;
  /** uid of the human, or 'agent'. */
  createdBy: string;
  status: 'pending' | 'approved' | 'discarded';
  /** Agent's one-line rationale for the proposed reply. */
  note?: string;
  confidence?: number;
  createdAt: Date;
}

/** One thread per mailbox; id = `${mailboxUserId}_${threadId}`. */
export interface ExecInboxThread {
  id?: string;
  mailboxUserId: string;
  threadId: string;
  /** Owner + delegates; powers Firestore rules. */
  allowedUids: string[];
  subject: string;
  lastFrom: string;
  lastFromName?: string;
  /** True when the latest message is from the mailbox owner (they replied last). */
  lastFromIsOwner?: boolean;
  snippet: string;
  /** Agent one-sentence "latest developments" summary shown in place of the snippet. */
  agentSummary?: {
    text: string;
    /** messageCount at summarize time — stale when the thread has grown past it. */
    forMessageCount?: number;
    confidence?: number;
    model?: string;
    updatedAt?: Date;
  };
  participants?: string[];
  /** Thread participants excluding the mailbox owner — who the conversation is with. */
  counterparts?: { email: string; name?: string }[];
  /** Latest-message To recipients (excl. owner). */
  primaryRecipients?: { email: string; name?: string }[];
  /** Latest-message Cc recipients (excl. owner). */
  ccRecipients?: { email: string; name?: string }[];
  lastMessageAt: Date;
  messageCount: number;
  unread: boolean;
  hasAttachments: boolean;
  /** Funnel stage id (or EXEC_INBOX_UNSORTED_STAGE_ID). */
  stage: string;
  gmailLabelIds: string[];
  assignedTo?: string;
  linkedTo?: { type: 'lead' | 'deal' | 'project'; id: string; name: string };
  /** Agent link suggestion awaiting confirmation (confidence below auto-link threshold). */
  proposedLink?: {
    target: { type: 'lead' | 'deal' | 'project'; id: string; name: string };
    confidence?: number;
    rationale?: string;
    status: 'pending';
    createdBy?: string;
    createdAt?: Date;
  };
  proposedDraft?: ExecInboxDraft;
  /** Per-thread agent provenance for trust + override. */
  agent?: { classifiedBy: 'agent' | string; confidence?: number; rationale?: string; lastActionAt?: Date };
  lastSentBy?: ExecSendIdentity;
  /** Monotonic per-mailbox sequence powering the agent changes feed. */
  cursorSeq: number;
  lastClassifiedBy?: string;
  createdAt: Date;
  updatedAt: Date;
}

export type ExecAgentAction = 'classify' | 'draft' | 'send' | 'markRead' | 'link' | 'summarize' | 'sync' | 'denied';

/** Audit + feedback row in `execInboxAgentLog`. */
export interface ExecInboxAgentLogEntry {
  id?: string;
  mailboxUserId: string;
  allowedUids: string[];
  action: ExecAgentAction;
  threadId?: string;
  fromStage?: string;
  toStage?: string;
  draftId?: string;
  /** 'agent' or a human uid. */
  actor: string;
  model?: string;
  confidence?: number;
  rationale?: string;
  dryRun: boolean;
  result: 'applied' | 'dryRun' | 'denied' | 'error';
  denyReason?: string;
  revertToken?: string;
  /** Captured when a human edits/overrides an agent action (tuning signal). */
  feedback?: { proposed?: string; final?: string; accepted?: boolean };
  createdAt: Date;
}

// =============================================================================
// EXECUTIVE DAY BRIEFING
// =============================================================================

export type ExecutiveBriefingProfile = 'ceo' | 'coo';

/** Stored on `users/{uid}.executiveBriefing`. */
export interface ExecutiveBriefingConfig {
  enabled: boolean;
  profile: ExecutiveBriefingProfile;
  /** UIDs allowed to read this executive's briefing (e.g. CEO). Admins also allowed. */
  viewers?: string[];
}

export type BriefingPrioritySource = 'email' | 'calendar' | 'projectHub' | 'ops' | 'production';

export interface BriefingPriority {
  rank: number;
  title: string;
  rationale: string;
  href: string;
  source: BriefingPrioritySource;
  entityId?: string;
}

export interface BriefingSection {
  summary?: string;
  itemCount?: number;
  items?: Record<string, unknown>[];
}

/** Stored in `executiveDayBriefings/{ownerUid}_{YYYY-MM-DD}`. */
export interface ExecutiveDayBriefing {
  id?: string;
  ownerUid: string;
  /** YYYY-MM-DD in the executive's local timezone. */
  dateKey: string;
  allowedUids: string[];
  generatedAt: Date;
  generatedBy: 'agent' | 'crm';
  /** Markdown — main AI summary. */
  narrative: string;
  priorities: BriefingPriority[];
  /** Keys from {@link briefingPriorityKey} manually dismissed for today. */
  dismissedPriorityKeys?: string[];
  sections?: {
    email?: BriefingSection;
    calendar?: BriefingSection;
    projectHub?: BriefingSection;
    ops?: BriefingSection;
    production?: BriefingSection;
  };
  contextSnapshot?: Record<string, unknown>;
}

export function executiveDayBriefingDocId(ownerUid: string, dateKey: string): string {
  return `${ownerUid}_${dateKey}`;
}

export function todayBriefingDateKey(timeZone = 'America/New_York'): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}

// ==========================================
// PROPOSAL BUILDER SYSTEM
// ==========================================

export type ProposalBuilderStatus = 'draft' | 'sent' | 'opened' | 'approved' | 'rejected';

/**
 * How freight and overhead are entered on a custom proposal.
 * Missing / `'legacy'` = per-structure freight node + overhead days (all existing proposals).
 * `'global'` = freight on Global Costs dollar lines; overhead is the project % (new custom proposals).
 */
export type ProposalCostingModel = 'legacy' | 'global';

/**
 * Per-proposal promotional discount — a flat dollar amount off the post-margin
 * billable total, offered in exchange for a benefit (e.g. filming rights).
 */
export interface ProposalDiscount {
  enabled: boolean;
  /** Flat dollars off the post-margin billable total. */
  amount: number;
  /** Client-facing label, e.g. "Feature Film Partnership". */
  label: string;
  /** Short client-facing description of the exchange. */
  clientDescription?: string;
  /** Staff-only context. */
  internalNotes?: string;
}

export interface ProposalBuilderData {
  id?: string;
  dealId: string;
  clientName?: string;              // Client name for T&C
  leadCompany?: string;             // Company name from lead
  projectName?: string;             // Project name from deal title
  /** Denormalized from linked deal — canonical project number (e.g. 26-014). */
  projectNumber?: string;
  /** Denormalized from linked deal — client label (typically last name or company). */
  clientLabel?: string;
  /** Denormalized from linked deal — project descriptor (e.g. Pavilion). */
  descriptor?: string;
  location?: string;                // Location/address from deal
  slug: string;                    // For public URL (lastname-projectname)
  /**
   * Locks the public proposal URL (`slug`) from regeneration. Flips to `true`
   * the first time the proposal is sent OR signed. Once locked, renaming a
   * deal updates `projectName` (display label) but never changes the slug —
   * so already-shared client URLs keep working.
   */
  slugLocked?: boolean;
  accessPin: string;               // 4-digit PIN for public access
  nodeSets: ProposalNodeSet[];     // Multiple sets (barn, bridge, etc.)
  profitMargin: number;            // Default 40%
  installOptional?: boolean;       // Allow client to opt out of install
  /**
   * Roll design cost into timber kit on public display.
   * New creates default to true; missing/undefined on legacy docs must resolve to false
   * (see resolveDesignIncludedInKit) so saved totals are not silently flipped.
   */
  designIncludedInKit?: boolean;
  isRomPricing?: boolean;          // Rough Order of Magnitude - pricing needs refinement after client input
  /** Optional whole-project square footage for the public $/sf badge. */
  projectSquareFootage?: number;
  /**
   * Per-deal toggles for what the client sees on the public proposal page
   * (`/proposal/:slug`). Distinct from the client-portal timeline overrides,
   * which live on `clientPortals/{portalId}.projects.{dealId}` because that
   * surface is rendered by the portal, not the proposal page. Undefined =
   * all toggles default to OFF.
   */
  publicSettings?: {
    /**
     * When true, each enabled structure on the public proposal exposes a
     * compact "View timber sizes" view (Name, Grade, Dryness, cross-section
     * only — no lengths, no quantities, no costs). OFF by default to keep
     * pricing-relevant detail private.
     */
    showTimberSizes?: boolean;
    /**
     * When true, the public proposal shows cost per square foot next to the
     * total. Requires {@link projectSquareFootage}. Off by default.
     */
    showCostPerSqFt?: boolean;
  };
  /**
   * Phased Project mode (beta, off by default). When true, structures are
   * grouped into `phases` and the client commits phases incrementally —
   * initial phase(s) at signing, later phases via builder-mode change orders.
   */
  phasingEnabled?: boolean;
  /** Ordered phase definitions when `phasingEnabled`. */
  phases?: ProposalPhase[];
  status: ProposalBuilderStatus;
  /** Client sign flow: whether installation was included in the signed commercial terms */
  installSelection?: boolean;
  /** Client's per-structure inclusion at sign time. structureId -> included (true). Omitted/false = excluded. */
  structureSelections?: Record<string, boolean>;
  /** Denormalized signed total (kept in sync with speciesPricing.billableTotal on sign / admin amend) */
  grandTotal?: number;
  /** Sign-time surety bonding premium included in `grandTotal` (0 / absent when no bonding). */
  bondingTotal?: number;

  // Pre-proposal checklist
  checklist: ProposalChecklist;

  // Summary line items (calculated)
  designTotal: number;
  timberKitTotal: number;          // Production + Materials + Freight
  installTotal: number;            // Install + Travel + Overhead
  rawCostTotal: number;
  billableTotal: number;
  grossProfit: number;
  /** Structure-only raw sum before global costs and project-overhead % (when saved from builder). */
  structureRawCostTotal?: number;
  /** Extra overhead from global rate setting: % of (structure raw + global costs). */
  projectOverheadAmount?: number;
  /** Promotional discount in exchange for a benefit (e.g. filming rights). */
  discount?: ProposalDiscount;
  /**
   * Project-level pre-sign estimating / modeling (CRM time + separately invoiced
   * designer time). Edited on Global Costs under Project overhead. Added once
   * to global costs (not per-structure Design). Omitted on existing proposals.
   */
  designEstimating?: DesignEstimatingData;

  createdAt: Date;
  updatedAt: Date;
  sentAt?: Date;
  approvedAt?: Date;
  createdBy: string;

  // Multi-species selection (client selects and saved on signature)
  selectedSpecies?: string;        // Client's selected species name
  speciesPricing?: {               // Snapshot of pricing for selected species
    speciesName: string;
    timberTotal: number;
    billableTotal: number;
    selections?: Record<string, string>;
    installTotal?: number;
    structureSelections?: Record<string, boolean>;
    /** Billable design frozen at signing. Later saves must not overwrite this with raw cost. */
    designTotal?: number;
    /** Promotional discount frozen at signing (already removed from billableTotal). */
    discountAmount?: number;
    /** Bonding premium frozen at signing (included in billableTotal). */
    bondingTotal?: number;
  };

  // Signature data (when proposal is signed)
  signature?: {
    dataUrl: string;
    signedBy: string;
    signedAt: Date | { toDate: () => Date };
  };
  signedPdfUrl?: string;           // Firebase Storage URL for signed PDF
  /**
   * Included structure ids (sorted, `|`-joined) rendered into `signedPdfUrl`.
   * When this matches the sign-time selection, the stored file stays the download
   * even if structures are added to the proposal later.
   */
  signedPdfLayoutKey?: string;
  /** Admin-only audit log when signed commercial terms are amended without re-signing */
  signedPricingAmendments?: Array<{
    at: Date | { toDate: () => Date };
    byUid: string;
    action: string;
    previousBillable: number;
    newBillable: number;
  }>;

  // Optional fields
  name?: string;                   // Proposal title/name
  clientEmail?: string;            // Client email

  // Kit proposal tracking
  isKitProposal?: boolean;         // True if created from kit wizard
  sourceKitId?: string;            // Reference to source TimberKit
  sourceKitName?: string;          // Kit name for display
  isCustomized?: boolean;          // True if user converted to custom (unlocks all editing)
  kitAvailableSpecies?: string[];  // All species available in the kit
  offeredSpecies?: string[];       // Species options offered to client (subset of kitAvailableSpecies)
  activeSpecies?: string;          // Currently selected species for pricing preview
  kitPricing?: Array<{             // Species pricing from kit
    speciesName: string;
    totalCost: number;
    structurePricing?: Array<{     // Per-structure breakdown for droppable elements
      nodeSetId: string;
      nodeSetName: string;
      cost: number;                // Cost for this structure (with profit margin applied)
    }>;
  }>;

  // Pricing lock - prevents material price changes from affecting sent proposals
  pricingLocked?: boolean;         // Whether pricing is locked
  pricingLockedAt?: Date;          // When pricing was locked
  lockedPricing?: {                // Snapshot of pricing at lock time
    grandTotal: number;
    installTotal?: number;
    kitPricing?: Array<{ speciesName: string; totalCost: number }>;
    nodeSetTotals?: Array<{ nodeSetId: string; name: string; total: number }>;
  };

  // Global project-level costs (optional, for large/complex projects)
  globalCosts?: GlobalCosts;
  /**
   * Missing / `'legacy'` keeps per-structure freight and overhead (existing proposals).
   * `'global'` is set only on newly created custom proposals.
   */
  costingModel?: ProposalCostingModel;

  /** Latest project-file timber list CSV (uploaded from project hub); drives builder reminder banner. */
  latestTimberListPrompt?: {
    fileId: string;
    fileName: string;
    fileUrl: string;
    uploadedAt: Date;
    uploadedByName?: string;
    uploadedBy?: string;
  };
  /** Matches `latestTimberListPrompt.fileId` when the user dismisses the banner (hide until a newer timber list is uploaded). */
  timberListBannerDismissedForFileId?: string | null;
  /** Project `projectFiles` doc id whose CSV was merged into this proposal (auto or manual import). */
  timberListAppliedToProposalFileId?: string | null;

  /** Latest project-file hardware list CSV; drives the builder reminder banner. */
  latestHardwareListPrompt?: {
    fileId: string;
    fileName: string;
    fileUrl: string;
    uploadedAt: Date;
    uploadedByName?: string;
    uploadedBy?: string;
  };
  hardwareListBannerDismissedForFileId?: string | null;
  hardwareListAppliedToProposalFileId?: string | null;
}

/** OPS review workflow — stored at `proposalBuilders/{dealId}/opsReview/meta`. */
export type ProposalOpsReviewStatus = 'none' | 'pending' | 'approved';

export interface ProposalOpsReviewMeta {
  status: ProposalOpsReviewStatus;
  requestedAt?: Date;
  requestedBy?: string;
  requestedByName?: string;
  reviewerUserId?: string;
  reviewerName?: string;
  salesRepUserId?: string;
  salesRepName?: string;
  approvedAt?: Date;
  approvedBy?: string;
  approvedByName?: string;
  /** Proposal version snapshot created at OPS approval (`versions/{id}`). */
  approvedVersionId?: string;
  requestMessage?: string;
  withdrawnAt?: Date;
  withdrawnBy?: string;
  withdrawnByName?: string;
}

/** Cost bucket within a structure — maps to proposal node sections in the builder. */
export type ProposalOpsReviewNodeType =
  | 'structure'
  | 'design'
  | 'timber'
  | 'materials'
  | 'production'
  | 'freight'
  | 'overhead'
  | 'install'
  | 'travel';

/** Per-structure OPS review comment — `proposalBuilders/{dealId}/opsReviewComments/{commentId}`. */
export interface ProposalOpsReviewComment {
  id?: string;
  nodeSetId: string;
  nodeSetName: string;
  /** Cost section; omitted on legacy docs → treated as `structure`. */
  nodeType?: ProposalOpsReviewNodeType;
  /** Display label for the section (e.g. Design, Timbers). */
  nodeLabel?: string;
  message: string;
  authorId: string;
  authorName: string;
  createdAt: Date;
}

/** Stored at `proposalBuilders/{dealId}/versions/{versionId}` — frozen proposal for history (view-only). */
export type ProposalVersionSource = 'manual' | 'sent' | 'ops_approved' | 'rfq_staging';

/** Per-structure timber/materials totals at version save (denormalized for history list). */
export interface TimberNodeCostSnapshot {
  nodeSetId: string;
  name: string;
  /** Raw sum from materials timber list + timber additions for this structure. */
  timberMaterialsTotal: number;
  /** Billable timber kit with margin when present on the node set. */
  billableTimberKit?: number;
}

/** Stored at `proposalBuilders/{dealId}/timberVersions/{versionId}` — per-structure timber list snapshot (all species). */
export interface StructureTimberListVersion {
  id?: string;
  nodeSetId: string;
  nodeSetName: string;
  label?: string;
  createdAt: Date | { toDate: () => Date };
  createdBy: string;
  createdByName?: string;
  quoteMultipleSpecies: boolean;
  timberList: TimberListItem[];
  alternateSpecies: SpeciesTimberConfig[];
  timberTotal: number;
  speciesSummary: string;
}

export interface ProposalBuilderVersionDoc {
  id?: string;
  createdAt: Date | { toDate: () => Date };
  createdBy: string;
  label?: string;
  source: ProposalVersionSource;
  /** Denormalized client-facing total for list rows (matches builder display total when saved). */
  billableTotalSnapshot?: number;
  /** Per-structure timber totals at save (full line items remain in snapshot). */
  timberNodeSnapshots?: TimberNodeCostSnapshot[];
  /** RFQ hub releases staged from this proposal version. */
  linkedRfqReleaseIds?: string[];
  snapshot: ProposalBuilderData;
}

// Proposal notes — shared between employees and clients on the public proposal page
export interface ProposalNote {
  id?: string;
  proposalId: string;              // proposalBuilders doc ID
  authorType: 'employee' | 'client';
  authorName: string;
  message: string;
  createdAt: Date;
}

// Portal notes — feedback on 3D model / other steps in the public client portal
export interface PortalNote {
  id?: string;
  portalId: string;                // clientPortals doc ID
  step: PortalStage;              // which timeline step this note is about
  authorType: 'employee' | 'client';
  authorName: string;
  message: string;
  createdAt: Date;
}

// Global project cost item
export interface GlobalCostItem {
  enabled: boolean;
  amount: number;
  notes?: string;
}

// Global project-level costs for large/complex projects
export interface GlobalCosts {
  baselineTravel: GlobalCostItem;              // general travel expenses for the whole project
  additionalTravel: GlobalCostItem;
  globalFreightInbound: GlobalCostItem;      // inbound freight (materials to shop/jobsite)
  globalFreightOutbound: GlobalCostItem;     // outbound freight (finished product to jobsite)
  equipmentRental: GlobalCostItem;
  additionalGear: GlobalCostItem;
  ppeBudget: GlobalCostItem;
  jobsiteIncidentals: GlobalCostItem;    // bracing lumber, food, drinks, popup tents, etc.
  additionalInsurance: GlobalCostItem;
  damagedTimberBudget: GlobalCostItem;   // auto-calculates 5% of timber cost, editable
  jobsiteSecurity: GlobalCostItem;
  additionalVehicles: GlobalCostItem;
  jobSpecificTooling: GlobalCostItem;
  totalGlobalCosts: number;              // Sum of all enabled items
  /** This proposal only: add overhead as % of (structure raw + line items above). */
  projectOverheadPercentEnabled?: boolean;
  /** 0–100; used when projectOverheadPercentEnabled is true. */
  projectOverheadPercent?: number;
  /**
   * Surety bonding passed through to the client as its own line below
   * Installation (percent of the pre-bonding Total Investment, no margin).
   * Not part of `totalGlobalCosts`; omitted on proposals saved before bonding existed.
   */
  bonding?: ProposalBonding;
}

export type ProposalBondType = 'performance' | 'payment' | 'bid' | 'maintenance' | 'other';

export interface ProposalBondingLine {
  id: string;
  type: ProposalBondType;
  /** Shown when type is 'other'. */
  label?: string;
  /** Percent of the pre-bonding client total, 0–100. */
  percent: number;
  enabled: boolean;
}

export interface ProposalBonding {
  enabled: boolean;
  lines: ProposalBondingLine[];
  /** Optional sub-copy on the client line; default describes the bond types. */
  clientDescription?: string;
}

// Track PDF proposal downloads
export interface ProposalDownload {
  id?: string;
  proposalId: string;
  dealId?: string;           // Link to deal for easy lookup
  clientName: string;
  downloadedAt: Date;
  downloadedBy?: 'client' | 'employee' | 'admin' | 'stakeholder';  // Who initiated the download
  includeInstall: boolean;
  selectedSpecies?: Record<string, string>;
  kitSelectedSpecies?: string;  // For kit proposals
  structureSelections?: Record<string, boolean>;
  totalAmount: number;
  pdfStorageUrl?: string;    // Firebase Storage URL for the PDF
  pdfFileName?: string;      // Original filename
  isSigned?: boolean;        // Whether this is a signed version
  isKitProposal?: boolean;   // Whether this is from a kit proposal
  userAgent?: string;
}

// Sales Order - Created when proposal is signed
/** Contract payment installment identifiers (50% deposit / 40% pre-shipment / 10% final). */
export type SalesOrderInstallmentId = 'deposit' | 'pre_shipment' | 'final';

/**
 * A QuickBooks invoice created for a single contract installment under progress
 * billing. `balance`/`status`/`paidAt` are backfilled by the QuickBooks payment
 * sync (webhook + bulk history) once the client pays.
 */
export interface SalesOrderInstallmentInvoice {
  installmentId: SalesOrderInstallmentId;
  quickbooksInvoiceId: string;
  quickbooksInvoiceNum: string;
  amount: number;
  createdAt: Date;
  balance?: number;
  status?: 'open' | 'partial' | 'paid' | 'voided' | 'removed_in_qb';
  paidAt?: Date;
  /**
   * Set when this invoice covers only some structure groups of a 40% or 10%
   * milestone. Absent on a whole-milestone invoice (including every deposit).
   */
  partialId?: string;
  /** Proposal structure ids billed on this invoice. Empty means the whole milestone. */
  structureIds?: string[];
  /** File structure-group keys at invoice time. */
  structureGroupKeys?: string[];
  /** Group names at invoice time, so a later rename does not rewrite the bill. */
  structureGroupLabels?: string[];
  /** Contract value of the selected structures (before the milestone percent). */
  basisAmount?: number;
  /** Structure-line dollars, excluding change orders folded onto this invoice. */
  baseBilled?: number;
  /** Change orders folded onto this invoice. Each is the full amount for this milestone. */
  foldedChangeOrderRefs?: string[];
  /** True on the invoice that finishes the milestone (the full bill, or the last slice). */
  closesMilestone?: boolean;
}

export interface SalesOrder {
  id?: string;
  proposalId: string;
  dealId: string;
  clientName: string;
  signedAt: Date;
  signedBy: string;
  signatureDataUrl: string;

  // Selections
  includeInstall: boolean;
  selectedSpecies: Record<string, string>;  // structureId -> speciesName
  structureSelections?: Record<string, boolean>;

  // Pricing snapshot
  designTotal: number;
  timberKitTotal: number;
  installTotal: number;
  grandTotal: number;
  profitMargin: number;

  // Line items
  lineItems: SalesOrderLineItem[];

  // QuickBooks invoice linkage
  quickbooksInvoiceId?: string;
  quickbooksInvoiceNum?: string;
  quickbooksInvoiceCreatedAt?: Date;

  // Progress billing (50/40/10). `progress` orders bill QuickBooks invoices per
  // installment. The 40% and 10% may have several invoices, one per structure
  // group. Unset/`full` = legacy single-invoice behavior.
  billingStyle?: 'full' | 'progress';
  /** Billed slices. Deposit has one. Pre-shipment and final may have several. */
  installmentInvoices?: SalesOrderInstallmentInvoice[];
  /** Flat list of the QB invoice Ids in `installmentInvoices[]` for array-contains lookups from payment sync. */
  installmentInvoiceIds?: string[];

  // Change-order linkage — when set, this sales order represents an approved
  // change order rather than the original signed proposal.
  changeOrderId?: string;
  isChangeOrder?: boolean;
  /** When a CO was bundled onto a progress installment invoice. */
  bundledOnInstallmentInvoice?: 'deposit' | 'pre_shipment' | 'final';
  bundledOnSalesOrderId?: string;
  quickbooksInvoiceVoidedForConsolidation?: string;
  quickbooksInvoiceVoidedNumForConsolidation?: string;
  quickbooksInvoiceVoidedAt?: Date;

  createdAt: Date;
}

export interface SalesOrderLineItem {
  structureId: string;
  structureName: string;
  quantity: number;
  species: string;
  designCost: number;
  timberKitCost: number;
  installCost: number;
  subtotal: number;
}

// ── Change Orders ───────────────────────────────────────────────────────────
// A change order is an independent, immutable amendment layered on top of a
// signed proposal. Approved change orders adjust the project's "true total
// value" (the deal value) WITHOUT ever mutating the original proposal document.
// contractValue(deal) = signedProposalBase + sum(approved change order deltas).
// ── Phased Project Structures ──
// Off-by-default mode where proposal structures are grouped into phases the
// client commits to incrementally. Later-phase commits post through the
// change-order ledger (builder-mode CO), keeping contract value / sales
// orders / QB invoicing untouched.
export type ProposalPhaseStatus = 'future' | 'offered' | 'committed';

export interface ProposalPhase {
  id: string;
  name: string;                      // "Phase 1 — Main Barn"
  order: number;                     // 0-based display/sequence order
  status: ProposalPhaseStatus;
  /** Set when the client (or staff internally) commits this phase. */
  committedAt?: Date | { toDate: () => Date };
  committedBy?: string;
  signature?: {
    dataUrl: string;
    signedBy: string;
    signedAt: Date | { toDate: () => Date };
  };
  /** Builder-mode change order that carried this phase's commitment (later phases only). */
  changeOrderId?: string;
  /** Staff released this phase for client commitment. */
  offeredAt?: Date | { toDate: () => Date };
  /** Last staff reprice of this phase's structures. */
  repricedAt?: Date | { toDate: () => Date };
  repriceNote?: string;
}

export type ChangeOrderStatus = 'draft' | 'sent' | 'approved' | 'rejected' | 'void';
export type ChangeOrderPricingMode = 'lump_sum' | 'line_item' | 'builder';
/**
 * How an approved change order is billed:
 * - 'immediate': own sales order + QB invoice at approval (legacy default).
 * - 'contract_terms': no sales order/invoice — delta folds into the remaining
 *   contract payment installments (always used for credits/negative deltas).
 */
export type ChangeOrderBillingMode = 'immediate' | 'contract_terms';

export interface ChangeOrderLineItem {
  id: string;
  description: string;
  bucket: ProjectPLBucket;       // P&L bucket this line contributes to
  quantity: number;
  unitCost?: number;             // input cost per unit (drives pricing; negative allowed for credits)
  margin?: number;               // profit margin % applied to cost (true margin: price = cost / (1 - m/100))
  unitPrice: number;             // billable per-unit price (derived from unitCost + margin)
  amount: number;                // quantity * unitPrice (billable delta)
  costEstimate?: number;         // raw cost for P&L budget (derived: quantity * unitCost)
}

export interface ChangeOrderSignature {
  dataUrl: string;
  signedBy: string;
  signedAt: Date;
}

export interface ChangeOrderAuditEntry {
  at: Date;
  byUid: string;
  byName?: string;
  action: 'created' | 'edited' | 'sent' | 'signed' | 'internal_approved' | 'rejected' | 'voided' | 'reopened' | 'retracted';
  fromStatus?: ChangeOrderStatus;
  toStatus?: ChangeOrderStatus;
  amount?: number;
}

export interface ChangeOrder {
  id?: string;
  // Identity / linkage
  dealId: string;                // canonical commercial id
  projectId?: string;            // timberProjects id (hub context)
  proposalId: string;            // = dealId (reference to the signed proposal)
  number: number;                // sequential per deal (1, 2, 3 -> "CO-001")
  /** Searchable reference code, e.g. "26-014-CO-001" (projectNumber + CO number). */
  reference?: string;
  title: string;
  scopeDescription?: string;

  status: ChangeOrderStatus;

  // Pricing
  pricingMode: ChangeOrderPricingMode;
  lumpSumAmount?: number;                  // lump_sum mode
  lineItems?: ChangeOrderLineItem[];       // line_item mode
  builderNodeSets?: ProposalNodeSet[];     // builder mode
  builderProfitMargin?: number;            // builder mode margin
  builderSelectedSpecies?: Record<string, string>;

  // Net effect on the contract
  deltaAmount: number;                     // signed billable delta (negative = credit), includes bondingAdjustment
  budgetCostByBucket?: Partial<Record<ProjectPLBucket, number>>; // raw cost for P&L budget
  /** Proposal bonding percent captured when the CO was priced; absent on COs without bonding. */
  bondingPercent?: number;
  /** Bonding premium change rolled into deltaAmount (negative on credits). */
  bondingAdjustment?: number;
  /** Billing treatment — missing = 'immediate' (legacy). Credits are always 'contract_terms'. */
  billingMode?: ChangeOrderBillingMode;

  // Approval snapshot — stamped once when the CO is approved so later COs can
  // never shift what this CO displayed (client page, PDF, review modal).
  /** Contract value immediately before this CO applied (signed base + COs approved earlier). */
  contractValueBefore?: number;
  /** contractValueBefore + deltaAmount. */
  contractValueAfter?: number;
  /** Raw P&L bucket budget before this CO (proposal + earlier approved CO budgets). */
  budgetByBucketBefore?: Partial<Record<ProjectPLBucket, number>>;
  /** budgetByBucketBefore + this CO's budgetCostByBucket. */
  budgetByBucketAfter?: Partial<Record<ProjectPLBucket, number>>;
  snapshotAt?: Date;

  // Approval / audit
  signature?: ChangeOrderSignature;
  /** Staff recorded client approval without a portal signature (offline consent). */
  internalApproval?: boolean;
  internalApprovedBy?: string;
  internalApprovedByUid?: string;
  approvedAt?: Date;
  rejectedAt?: Date;
  signedPdfUrl?: string;
  slug: string;                            // public link slug
  accessToken?: string;
  auditLog?: ChangeOrderAuditEntry[];

  // Customer context (snapshot for portal display)
  clientName?: string;
  clientEmail?: string;            // default signing-link recipient
  projectName?: string;
  projectNumber?: string;

  // Links
  salesOrderId?: string;
  rfqReleaseIds?: string[];
  procurementTaskGroupId?: string;

  // Bookkeeping
  createdAt: Date;
  createdBy: string;
  updatedAt?: Date;
  sentAt?: Date;
}

/** Building plan size in feet — used for loaded $/sf. Not canvas node size. */
export interface StructureBuildingFootprint {
  lengthFt: number;
  widthFt: number;
}

export interface ProposalNodeSet {
  id: string;
  name: string;                    // "Main Barn", "Covered Bridge"
  position: { x: number; y: number }; // React Flow position
  dimensions?: { width: number; height: number }; // Dimensions of the group node
  /**
   * Building plan size in feet for loaded $/sf on the structure total.
   * Not the React Flow group `dimensions`.
   */
  buildingFootprint?: StructureBuildingFootprint;
  childPositions?: Record<string, { x: number; y: number }>; // Positions of child nodes
  images?: Array<{ url: string; name: string }>;  // Structure photos
  notes?: string;                    // Optional notes for this structure
  quantity?: number;                 // Number of identical structures (default: 1)
  enabled?: boolean;                 // Whether structure is included in proposal (default: true)
  /** When true, this structure receives no share of Global Project Costs (split among other enabled structures). */
  excludeFromGlobalPrices?: boolean;
  /** When true, install and travel are not included in client billable pricing for this structure. */
  excludeInstall?: boolean;
  /** When true, client may opt out of this structure on the public proposal. Builder-only. */
  clientOptional?: boolean;
  /**
   * When true, this structure is in the proposal’s single choose-one set.
   * The client can purchase only one structure that has this flag. Cannot also be `clientOptional`.
   */
  clientExclusive?: boolean;
  /** @deprecated Use `clientExclusive`. Non-empty values still count as exclusive. */
  clientExclusiveGroup?: string;
  /** Phased projects: id of the ProposalPhase this structure belongs to. Unassigned = base (first) phase. */
  phaseId?: string;
  design: DesignNodeData;
  production: ProductionNodeData;
  freight: FreightNodeData;
  overhead: OverheadNodeData;
  install: InstallNodeData;
  travel: TravelNodeData;
  materials: MaterialsNodeData;
  // Pre-calculated billable prices (with profit margin already applied) - hides margin from clients
  billablePrices?: BillablePrices;
}

// Pre-calculated billable prices for public display (hides profit margin)
export interface BillablePrices {
  design: number;                    // Pre-calculated design cost with margin
  timberKit: number;                 // Pre-calculated timber kit cost with margin (default species)
  install: number;                   // Pre-calculated install cost with margin
  travel: number;                    // Pre-calculated travel cost with margin
  subtotal: number;                  // Total for this structure
  // Species options with pre-calculated prices
  speciesOptions?: Record<string, number>; // { "Douglas Fir": 25000, "White Oak": 27500 }
}

export type EngineeringBillingMode = 'hourly' | 'lumpSum';

/**
 * Marked region on a designer invoice PDF (0–100 page space, same as leadAgentWorkspace).
 * Used by Global Costs hover+Space Quick Look to show which invoice dollars apply to this proposal.
 */
export interface DesignerInvoiceMarkupRegion {
  pageNumber: number;
  x: number;
  y: number;
  w: number;
  h: number;
  label?: string;
  amount?: number;
}

/** How a designer invoice allocation reached designEstimating. */
export type DesignerInvoiceSource = 'manual' | 'agent' | 'email';

/**
 * Proposal-level estimating / modeling before the proposal is signed.
 * Added once to Design totals (not multiplied by structure quantity).
 */
export interface DesignEstimatingData {
  /** Recorded CRM / staff time. */
  crmHours?: number;
  /** Designer time invoiced separately from joinery. */
  designerHours?: number;
  /** Optional lump designer invoice in addition to (or instead of) hours. */
  designerInvoiceAmount?: number;
  /** Override CRM $/hr; omitted = proposal `pmRate`. Prefer loaded/burdened rate for precon staff time. */
  crmHourlyRate?: number;
  /** Override designer $/hr; omitted = proposal `leadDesignerRate`. */
  designerHourlyRate?: number;
  /**
   * Hosted PDF (often pre-marked for this project). Global Costs: hover the invoice $
   * and press Space for Quick Look.
   */
  designerInvoiceFileUrl?: string;
  designerInvoiceStoragePath?: string;
  designerInvoiceFileName?: string;
  /** 1-based page hint for Quick Look / future highlight scroll. */
  designerInvoicePage?: number;
  /** Optional marked-up cost boxes in 0–100 page space. */
  designerInvoiceMarkupRegions?: DesignerInvoiceMarkupRegion[];
  /** Provenance. `email` reserved for invoices@lctimberframes.com ingest (not wired yet). */
  designerInvoiceSource?: DesignerInvoiceSource;
  designerInvoiceNote?: string;
  /** Last computed live raw total (so readers don't need rates). */
  total?: number;
  /** Explicit lock after send. Staff may set `unlocked` to revise. */
  locked?: boolean;
  /** Staff override so estimating can be edited after send. */
  unlocked?: boolean;
  lockedAt?: Date | string | { toDate?: () => Date };
  lockedCrmHours?: number;
  lockedDesignerHours?: number;
  lockedDesignerInvoiceAmount?: number;
  lockedCrmHourlyRate?: number;
  lockedDesignerHourlyRate?: number;
  /** Frozen raw dollars used while locked. */
  lockedTotal?: number;
}

// Individual node data structures
export interface DesignNodeData {
  pmHours: number;                 // Joinery PM hours (also the legacy four-hour PM field)
  /** Joinery APM hours. Omitted on old proposals = 0. */
  apmHours?: number;
  leadDesignerHours: number;       // Lead designer hours (joinery)
  supportDesignerHours: number;    // Support designer hours (joinery)
  /** Linked CRM user on each joinery line. */
  pmUserId?: string;
  apmUserId?: string;
  leadDesignerUserId?: string;
  supportDesignerUserId?: string;
  /** Per-structure $/hr when a person is linked or the rate is edited here. */
  pmRate?: number;
  apmRate?: number;
  leadDesignerRate?: number;
  supportDesignerRate?: number;
  engineeringHours: number;        // Engineering hours
  /** Missing = hourly (legacy). */
  engineeringMode?: EngineeringBillingMode;
  engineeringLumpSum?: number;
  total: number;                   // Calculated structure-only total
}

export interface ProductionNodeData {
  boardFeet: number;               // Active BF for production costing
  total: number;                   // boardFeet * rate
  /**
   * When true, `boardFeet` is a manual override and should not be replaced by
   * the materials timber list total. When false/omitted, production BF tracks
   * the sum of timber list line BF × qty.
   */
  boardFeetOverridden?: boolean;
  /**
   * Per-structure $/bdft override. When set, this is the source of truth
   * for the rate; `total` is derived as `boardFeet * ratePerBdft`.
   * Persisting it directly avoids the float-precision drift that happens
   * when the rate is reverse-derived from `total / boardFeet` (e.g. a
   * user-entered 1.9 reading back as 1.899 after a roundtrip).
   * Omitted on legacy data — fall back to the global rate or the implied
   * rate in that case.
   */
  ratePerBdft?: number;
}

export interface FreightNodeData {
  milesToAspers: number;           // Miles to production facility
  milesToJobSite: number;          // Miles from Aspers to job site
  costPerMileToAspers: number;     // Rate for first leg
  costPerMileToJobSite: number;    // Rate for second leg
  flatFeeToAspers: number;         // Flat fee for first leg
  flatFeeToJobSite: number;        // Flat fee for second leg
  total: number;
}

export interface OverheadNodeData {
  days: number;                    // Supports 0.5 increments
  total: number;
}

export type InstallEquipmentKind = 'crane' | 'telehandler' | 'scissorLift' | 'basketLift';
export type InstallEquipmentPeriod = 'hour' | 'day' | 'week' | 'month';

/** One stackable rental period on an install machine (crane week + OT hours, etc.). */
export interface InstallEquipmentLine {
  id: string;
  kind: InstallEquipmentKind;
  period: InstallEquipmentPeriod;
  /** How many machines. Omitted on older lines — treated as 1. */
  units?: number;
  /** How many of `period` (days, weeks, hours). */
  quantity: number;
  rate: number;
  overtime?: boolean;              // crane only; period is hour
}

/** Role breakdown that rolls up into `teamLaborPerDay`. Does not change install math. */
export interface InstallLaborLine {
  id: string;
  role: string;
  quantity: number;
  ratePerDay: number;
  /** CRM user id when the line was picked from the employee list. */
  employeeUserId?: string;
}

export interface InstallNodeData {
  days: number;
  teamLaborPerDay: number;
  /** Optional crew breakdown. Sum of qty × $/day is written to `teamLaborPerDay`. */
  laborLines?: InstallLaborLine[];
  craneRequired: boolean;
  craneDaysNeeded: number;
  craneRatePerDay: number;
  telehandlerRequired: boolean;
  telehandlerDaysNeeded: number;
  telehandlerRatePerDay: number;
  scissorLiftRequired: boolean;
  scissorLiftDaysNeeded: number;
  scissorLiftRatePerDay: number;
  basketLiftRequired?: boolean;
  equipmentLines?: InstallEquipmentLine[];
  /** Jobsite fuel for this structure. Separate from travel.fuelBudget. */
  fuelBudget?: number;
  overheadTotal: number;           // days * overhead rate
  total: number;
}

export interface TravelNodeData {
  milesToJobSite: number;          // From Franklin, TN (round trip)
  hotelNights: number;
  /** Rooms booked. Omitted on older proposals; treated as 2. */
  hotelRooms?: number;
  hotelRatePerNight: number;
  crewSize: number;
  perDiemPerPersonPerDay: number;
  fuelBudget: number;              // Default $500 minimum
  travelDays: number;              // Paid travel days; billed at listed labor rates when present
  total: number;
}

// A single decking size/product on a structure. A structure can carry several
// (e.g. 2x6 T&G plus 3x8 T&G), each priced per linear ft via board-width conversion.
export interface DeckingItem {
  id: string;
  productName?: string;            // Selected product name for display
  widthInches: number;             // Board width in inches (dressed; drives sq ft → lin ft)
  sqFt: number;
  pricePerLinFt: number;           // $/linear ft
  total: number;                   // ((sqFt × 12) / widthInches) × pricePerLinFt, rounded
}

export interface PostBasesItem {
  id: string;
  productName?: string;
  quantity: number;
  unitCost: number;
  total: number;
}

export type HardwareListGroup = 'steel' | 'fasteners' | 'pegs' | 'other';

export interface HardwareListItem {
  id: string;
  group: HardwareListGroup;
  name: string;
  subGroup?: string;
  material?: string;
  section?: string;
  length?: string;
  lengthRough?: string;
  quantity: number;
  weightLb?: number;
  unitCost: number;
  total: number;
  storey?: string;
  sourceProductionNumber?: string;
  sourceProjectFileId?: string;
}

export interface MaterialsNodeData {
  timberList: TimberListItem[];    // Can be imported from CSV (default species)
  timberTotal: number;
  /** Steel / fasteners / pegs line items. Pegs and fastener lump fields stay as rollups of these groups. */
  hardwareList?: HardwareListItem[];
  // Multi-species support
  quoteMultipleSpecies?: boolean;           // Toggle for multi-species mode
  alternateSpecies?: SpeciesTimberConfig[]; // Additional species options
  pegsCost: number;
  /** Current model: one or more catalog post-base products. Legacy qty/unit/total stay in sync. */
  postBasesItems?: PostBasesItem[];
  postBasesProductName?: string;
  postBasesQty: number;
  postBasesUnitCost: number;       // Default $80
  postBasesTotal: number;
  fastenerPackageCost: number;
  // Decking — entered in sq ft, priced per linear ft with board-width conversion.
  // `deckingItems` is the current model (multiple sizes per structure). The legacy
  // single-decking fields below are kept in sync (aggregate/first item) for backward
  // compatibility with older proposals and read-sites that predate `deckingItems`.
  deckingItems?: DeckingItem[];
  deckingSqFt: number;
  deckingWidthInches: number;      // Board width in inches (from database)
  deckingPricePerLinFt: number;    // $/linear ft (from database)
  deckingTotal: number;            // Auto-calculated: sum of decking item totals
  deckingProductName?: string;     // Selected product name for display
  total: number;
  totalBoardFeet: number;          // For production calculation
}

export interface TimberListItem {
  id: string;
  structureId?: string;            // Optional: which structure this item belongs to
  structureName?: string;          // Display name for structure
  name: string;                    // e.g., "Main Beam", "Rafter"
  species: string;
  grade: string;                   // e.g., "#1", "Select", "Construction"
  dimensions: string;              // e.g., "8x8x12"
  /** Green vs kiln dried; omit or empty = unspecified (matches legacy DB rows) */
  moistureContent?: '' | 'green' | 'kiln_dried';
  quantity: number;
  boardFeet: number;               // per-piece board feet
  unitCost: number;                // piecePrice
  total: number;
  /** Set when item originated from a PDF measurement take-off */
  sourceMeasurementId?: string;
  sourceDocumentId?: string;
  /** App-assigned line# from hub CSV version (primary compare link). */
  sourceLineNumber?: number;
  /** Cadwork production# when present on import. */
  sourceProductionNumber?: string;
  /** Hub project file version this line was imported from. */
  sourceProjectFileId?: string;
  /**
   * Set when the source CSV was marked true dimensions. Client sizes then keep
   * decimal cross-sections. Absent rounds them to the nearest inch.
   */
  trueDimensions?: boolean;
}

// A complete timber configuration for a specific species
export interface SpeciesTimberConfig {
  speciesName: string;             // e.g., "White Oak", "Douglas Fir"
  isDefault?: boolean;             // Primary option shown first
  timberList: TimberListItem[];    // Full timber list for this species
  timberTotal: number;             // Calculated total cost
  totalBoardFeet: number;          // Total board feet
  /**
   * Optional production $/bdft for this species. Omitted = inherit the
   * structure’s default `production.ratePerBdft`. Only used when the
   * structure is quoting more than one species.
   */
  ratePerBdft?: number;
}

// Pre-proposal data gathering checklist
export interface ProposalChecklist {
  // Client Information
  clientAddressConfirmed: boolean;
  primaryContactVerified: boolean;
  siteAccessDetailsNoted: boolean;

  // Project Scope
  projectTypeConfirmed: boolean;   // Kit mod vs custom
  timberSpeciesSelected: boolean;
  scopeDiscussed: boolean;

  // Design Requirements
  designMeetingCompleted: boolean;
  modelDrawingsReceived: boolean;
  engineeringRequirementsIdentified: boolean;

  // Install Logistics
  siteVisitCompleted: boolean;
  equipmentRequirementsAssessed: boolean;
  installDurationDiscussed: boolean;

  // Materials
  timberListAvailable: boolean;
  fastenerRequirementsNoted: boolean;
}

// Default empty checklist
export const DEFAULT_PROPOSAL_CHECKLIST: ProposalChecklist = {
  clientAddressConfirmed: false,
  primaryContactVerified: false,
  siteAccessDetailsNoted: false,
  projectTypeConfirmed: false,
  timberSpeciesSelected: false,
  scopeDiscussed: false,
  designMeetingCompleted: false,
  modelDrawingsReceived: false,
  engineeringRequirementsIdentified: false,
  siteVisitCompleted: false,
  equipmentRequirementsAssessed: false,
  installDurationDiscussed: false,
  timberListAvailable: false,
  fastenerRequirementsNoted: false,
};

// Default empty node set
export const createDefaultNodeSet = (name: string, id?: string): ProposalNodeSet => ({
  id: id || `nodeset-${Date.now()}`,
  name,
  position: { x: 0, y: 0 },
  dimensions: { width: 650, height: 240 }, // Default dimensions
  childPositions: {},
  images: [],
  quantity: 1,
  enabled: true,
  excludeFromGlobalPrices: false,
  excludeInstall: false,
  clientOptional: false,
  design: { pmHours: 0, leadDesignerHours: 0, supportDesignerHours: 0, engineeringHours: 0, total: 0 },
  production: { boardFeet: 0, total: 0, boardFeetOverridden: false },
  freight: { milesToAspers: 0, milesToJobSite: 0, costPerMileToAspers: 0, costPerMileToJobSite: 0, flatFeeToAspers: 0, flatFeeToJobSite: 0, total: 0 },
  overhead: { days: 0, total: 0 },
  install: { days: 0, teamLaborPerDay: 950, craneRequired: false, craneDaysNeeded: 0, craneRatePerDay: 1600, telehandlerRequired: false, telehandlerDaysNeeded: 0, telehandlerRatePerDay: 750, scissorLiftRequired: false, scissorLiftDaysNeeded: 0, scissorLiftRatePerDay: 350, basketLiftRequired: false, equipmentLines: [], fuelBudget: 0, overheadTotal: 0, total: 0 },
  travel: { milesToJobSite: 0, hotelNights: 0, hotelRooms: 2, hotelRatePerNight: 150, crewSize: 4, perDiemPerPersonPerDay: 60, fuelBudget: 500, travelDays: 0, total: 0 },
  materials: { timberList: [], timberTotal: 0, hardwareList: [], pegsCost: 0, postBasesQty: 0, postBasesUnitCost: 80, postBasesTotal: 0, fastenerPackageCost: 0, deckingSqFt: 0, deckingWidthInches: 0, deckingPricePerLinFt: 0, deckingTotal: 0, total: 0, totalBoardFeet: 0 },
});

// ==========================================
// KIT MANAGEMENT SYSTEM
// ==========================================

export type TimberKitStatus = 'draft' | 'active' | 'archived';

// ==========================================
// CLIENT PORTAL SYSTEM
// ==========================================

export type PortalDeliverableApprovalStatus = 'pending' | 'approved';

export interface PortalDeliverableApproval {
  status: PortalDeliverableApprovalStatus;
  approvedAt?: Date;
  approvedByName: string;
  approvedByEmail?: string;
  signature: string;
  releaseId?: string;
  bundleId?: string;
  releaseVersionIndex?: number;
  /** Static image URL this approval is bound to (concept preview image). */
  assetUrl?: string;
}

/** Where a portal file release surfaces in the client portal UI. */
export type PortalReleaseTarget = 'engineered_drawings' | 'concept_elevations' | 'model';

/** Design phase a portal release belongs to on the client approval trail. */
export type PortalDesignPhase = 'concept' | 'engineering';

/**
 * Fields shared by `portalFileReleases` and `portalReleaseBundles` that power the
 * per-release approval trail. All optional — legacy rows derive them at read time.
 */
export interface PortalReleaseTrailFields {
  designPhase?: PortalDesignPhase;
  /** Structure scope snapshot from the source track at release time (empty = whole project). */
  structureIds?: string[];
  structureNames?: string[];
  /** Staff asked the client to approve this release. */
  approvalRequested?: boolean;
  approvalRequestedAt?: Date;
  /** Client approval recorded against this exact release / bundle (CF-written). */
  approval?: PortalDeliverableApproval;
  /** A newer unapproved release of the same track + phase replaced this one. */
  supersededByReleaseId?: string;
}

export type PortalStage =
  | 'design-fee'        // Step 1: Concept Design Fee
  | '3d-model'          // Step 2: 3D Model delivery
  | 'quote'             // Step 3: Quote/Proposal sent
  | 'quote-signed'      // Step 4: Quote signed
  | '2d-plans'          // Step 5: 2D Plans (construction drawings, post-signing)
  | 'in-production'     // Step 6: In Production
  | 'delivery';         // Step 7: Delivery & Install

export interface PortalStageHistoryEntry {
  stage: PortalStage;
  enteredAt: Date;
  completedAt?: Date;
}

/** One line on the customer portal statement of account. Dates are ISO strings. */
export interface PortalStatementEntry {
  id: string;
  date: string;
  kind: 'invoice' | 'payment' | 'design_fee';
  label: string;
  detail?: string;
  structureGroupLabels?: string[];
  charge?: number;
  payment?: number;
  /** Contract balance after this line. Design-fee payments do not change it. */
  balanceAfter: number;
  quickbooksInvoiceNum?: string;
}

/** Client-safe payment installment row mirrored onto the portal for public display. */
export interface PortalInstallmentPayment {
  installmentId: string;
  label: string;
  amount: number;
  status: 'not_billed' | 'open' | 'partial' | 'paid' | 'voided';
  paidAt?: Date;
  /** When the milestone was fully invoiced. A partial slice does not set this. */
  invoicedAt?: Date;
  /** Dollars invoiced so far across partial and full invoices. */
  amountBilled?: number;
  /** Dollars collected on those invoices. */
  amountPaid?: number;
  /** Full milestone minus amount billed. */
  amountUnbilled?: number;
  /** True when invoices cover only some structure groups. */
  partiallyBilled?: boolean;
  /** Groups named on the open invoices for this installment. */
  structureGroupLabels?: string[];
}

/**
 * Client-safe surety bond status mirrored onto the portal from the (staff-only)
 * `suretyBondRequests` doc, so the client sees bonding progress before the bond
 * is issued. Dates are strings (YYYY-MM-DD / ISO) so no Timestamp mapping is needed.
 */
export interface PortalProjectBond {
  requestId: string;
  status: 'draft' | 'sent' | 'issued' | 'declined';
  bondType: 'bid' | 'performance_payment';
  /** Penal sum of each bond. */
  bondAmount: number;
  suretyName?: string;
  bondNumber?: string;
  /** YYYY-MM-DD the request letter is dated. */
  letterDate: string;
  /** YYYY-MM-DD the bond was issued, when status is 'issued'. */
  issuedAt?: string | null;
  /** ISO timestamp of the last mirror write. */
  updatedAt: string;
}

export interface PortalProject {
  dealId: string;
  /** Canonical legacy name (concatenated `#number client descriptor`). Kept for back-compat. */
  projectName: string;
  /** Structured project descriptor (e.g. "Leiper's Creek Rd"). Set by CRM project-naming tool. */
  descriptor?: string;
  /** Structured client label (e.g. "Trace Construction"). Set by CRM project-naming tool. */
  clientLabel?: string;
  /** Structured project number (e.g. "#0065"). Set by CRM project-naming tool. */
  projectNumber?: string;
  proposalSlug?: string;
  engagementLetterId?: string;
  engagementLetterSlug?: string;
  currentStage: PortalStage;
  stageHistory: PortalStageHistoryEntry[];
  /** Set true by a manual stage retreat — suppresses auto-bumps until the next advance. */
  stageManualOverride?: boolean;
  designFeeSkipped: boolean;
  /** When true, embed inspiration board on public portal design-fee step. */
  inspirationBoardPortalEnabled?: boolean;
  designFeePaid?: boolean;
  designFeePaidAt?: Date;
  amountPaid?: number;
  /**
   * Client-safe mirror of the contract payment installments and their paid
   * status, written by a Cloud Function from the (staff-only) sales order so the
   * public portal can show Paid/Invoiced badges without reading finance docs.
   */
  installmentPayments?: PortalInstallmentPayment[];
  /** Deposits, bills, and payments for this project, tied to structure groups. */
  statement?: PortalStatementEntry[];
  /** Client-safe surety bond status (null once cleared). */
  bond?: PortalProjectBond | null;
  selectedServices?: string[];
  agreementSigned?: boolean;
  agreementSignedAt?: Date;
  agreementSignedBy?: string;
  agreementSignature?: string;
  agreementServices?: { id: string; name: string; price: number }[];
  agreementTotal?: number;
  stripeSessionId?: string;
  stripeReceiptUrl?: string;
  modelUrl?: string;
  modelImageUrl?: string;
  /** Optional Loom share URL (staff-pasted); shown embedded on the public portal 3D step */
  modelLoomUrl?: string;
  modelNotes?: string;
  modelStepSkipped?: boolean;
  /**
   * Staff override: keep concept-model review and approve off even after a quote is sent.
   * Absent or false follows the default — the flow stays off until a quote is client-visible.
   */
  conceptApprovalFlowDisabled?: boolean;
  plansUrl?: string;
  /** Provenance: when `plansUrl` was set by a Project Hub release, the release id is recorded here. Cleared on manual one-off upload. */
  plansSourceReleaseId?: string;
  plansSourceProjectId?: string;
  plansSourceVersionLabel?: string;
  /** Provenance: when `modelUrl` was set by a Project Hub release. Cleared on manual one-off upload. */
  modelSourceReleaseId?: string;
  modelSourceProjectId?: string;
  modelSourceVersionLabel?: string;
  /** 2D elevation PDFs released during the 3D concept phase (not engineered drawings). */
  conceptElevationUrl?: string;
  conceptElevationSourceReleaseId?: string;
  conceptElevationSourceProjectId?: string;
  conceptElevationSourceVersionLabel?: string;
  /** Client sign-off on engineered drawings (record only — staff advances stages manually). */
  plansApproval?: PortalDeliverableApproval;
  plansApprovalHistory?: PortalDeliverableApproval[];
  /** Client sign-off on 3D model deliverable. */
  modelApproval?: PortalDeliverableApproval;
  modelApprovalHistory?: PortalDeliverableApproval[];
  /** Client sign-off on 2D concept elevations (3D phase). */
  conceptApproval?: PortalDeliverableApproval;
  conceptApprovalHistory?: PortalDeliverableApproval[];
  productionStatus?: 'queued' | 'in-progress' | 'complete';
  estimatedCompletion?: Date;
  productionNotes?: string;
  deliveryDate?: Date;
  deliveryStatus?: 'scheduled' | 'in-transit' | 'delivered' | 'installed';
  completionNotes?: string;
  createdAt: Date;
  updatedAt: Date;
  /** When true, project is hidden from the public client portal */
  hiddenFromPortal?: boolean;

  questionnaireStatus?: 'not-started' | 'in-progress' | 'completed' | 'skipped';

  /** Per-project design fee service overrides (employee-set). When present, replaces the global defaults on the public portal. */
  customDesignFeeServices?: { id: string; name: string; price: number; description: string; required?: boolean }[];

  // Client-requested target completion date ("I need it done by X")
  targetCompletionDate?: Date;

  // Per-portal timeline duration overrides (employee-set before quote sent)
  timelineOverrides?: Array<{
    id: string;
    durationWeeks: number;
    bufferWeeks: number;
    subPhases?: Array<{
      id: string;
      label: string;
      durationWeeks: number;
    }>;
  }>;
  /**
   * Override global walkthrough default for this project.
   * undefined = inherit global default; true = force on; false = force off.
   */
  modelWalkthroughEnabled?: boolean;
  /** Denormalized effective flag for public portal reads (computed on staff save). */
  modelWalkthroughEffective?: boolean;

  /**
   * Design-approval trail grouping mirror (server-written). Labels + structure
   * ids/names only — enough for portal guests to match the Files-tab groups
   * without reading projectFileStreams / projectFiles.
   */
  approvalTrailStructures?: Array<{ id: string; name: string }>;
  approvalTrailStructureGroups?: Array<{
    label: string;
    structureIds: string[];
    structureNames?: string[];
  }>;
  /** When the grouping mirror was last written by release/sync/backfill. */
  approvalTrailGroupingSyncedAt?: Date;
}

export type PortalModelWalkthroughStatus = 'uploading' | 'processing' | 'ready' | 'failed';

export interface PortalModelWalkthrough {
  id: string;
  portalId: string;
  dealId: string;
  releaseId?: string;
  bundleId?: string;
  storagePath: string;
  downloadUrl: string;
  durationSeconds: number;
  status: PortalModelWalkthroughStatus;
  authorUid: string;
  authorName: string;
  createdAt: Date;
  transcript?: string;
  summary?: string;
  keyQuestions?: string[];
  keyConcerns?: string[];
  processedAt?: Date;
  error?: string;
}

export interface ClientPortal {
  id?: string;
  slug: string;                      // URL slug: lastName-4digitKey (e.g., potter-7291)
  accessPin: string;                 // 4-digit PIN (shared with engagement letter)

  // Client info
  clientName: string;
  clientEmail: string;
  clientCompany?: string;

  // Multi-project support: per-project data keyed by dealId
  projects?: { [dealId: string]: PortalProject };

  // Linked entities (legacy flat fields kept for backward compat)
  leadId: string;
  dealId?: string;
  engagementLetterId?: string;
  engagementLetterSlug?: string;
  proposalId?: string;
  proposalSlug?: string;

  // Stage tracking (legacy flat — use projects map for multi-project)
  currentStage: PortalStage;
  stageHistory: PortalStageHistoryEntry[];
  /** Set true by a manual stage retreat — suppresses auto-bumps until the next advance. */
  stageManualOverride?: boolean;

  // Step-specific data (legacy flat — use projects map for multi-project)
  designFeeSkipped: boolean;
  /** When true, embed inspiration board on public portal design-fee step. */
  inspirationBoardPortalEnabled?: boolean;
  modelStepSkipped?: boolean;
  /** Staff override: keep concept-model review and approve off after a quote is sent. */
  conceptApprovalFlowDisabled?: boolean;
  designFeePaid?: boolean;
  designFeePaidAt?: Date;
  amountPaid?: number;
  /** Client-safe contract installment paid mirror (legacy flat — use projects map for multi-project). */
  installmentPayments?: PortalInstallmentPayment[];
  /** Statement of account (legacy flat — use projects map for multi-project). */
  statement?: PortalStatementEntry[];
  /** Client-safe surety bond status (legacy flat — use projects map for multi-project). */
  bond?: PortalProjectBond | null;
  selectedServices?: string[];
  agreementSigned?: boolean;
  agreementSignedAt?: Date;
  agreementSignedBy?: string;
  agreementSignature?: string;
  agreementServices?: { id: string; name: string; price: number }[];
  agreementTotal?: number;
  stripeSessionId?: string;
  stripeReceiptUrl?: string;
  modelUrl?: string;
  modelImageUrl?: string;
  modelLoomUrl?: string;
  modelNotes?: string;
  plansUrl?: string;
  conceptElevationUrl?: string;
  plansApproval?: PortalDeliverableApproval;
  plansApprovalHistory?: PortalDeliverableApproval[];
  modelApproval?: PortalDeliverableApproval;
  modelApprovalHistory?: PortalDeliverableApproval[];
  conceptApproval?: PortalDeliverableApproval;
  conceptApprovalHistory?: PortalDeliverableApproval[];

  // Production & Delivery (legacy flat)
  productionStatus?: 'queued' | 'in-progress' | 'complete';
  estimatedCompletion?: Date;
  productionNotes?: string;
  deliveryDate?: Date;
  deliveryStatus?: 'scheduled' | 'in-transit' | 'delivered' | 'installed';
  completionNotes?: string;

  // Document uploads (future)
  documentUploads?: Array<{
    id: string;
    name: string;
    url: string;
    uploadedAt: Date;
  }>;

  // Client-uploaded files (images, docs, etc.)
  clientUploads?: Array<{
    id: string;
    name: string;
    url: string;
    type: string;       // MIME type
    step: PortalStage;  // which step the upload belongs to
    uploadedAt: Date;
    dealId?: string;     // which project this upload belongs to
  }>;

  // Portal appearance overrides (per-portal)
  portalBackgroundImage?: { url: string; name: string };
  portalOverlayColor?: string;
  portalOverlayOpacity?: number;

  // Timestamps
  createdAt: Date;
  updatedAt: Date;
  lastViewedAt?: Date;

  // Questionnaire gating
  questionnaireStatus?: 'not-started' | 'in-progress' | 'completed' | 'skipped';

  // Kick-off email tracking
  kickOffEmailSentAt?: any; // Firestore Timestamp
  kickOffEmailRecipients?: string[];
}

/**
 * One published release of a Project Hub file (2D PDF or 3D HTML model) to a client portal.
 * Stored as a subcollection on the parent portal so portal stakeholders can read their own
 * version history without broad collection-level access. Releases are normally
 * append-only; staff may withdraw a hub version via `unreleaseProjectFileFromPortal`,
 * which deletes release rows and reconciles portal pointers. Each release writes an immutable
 * Storage copy to `portal-files/...` so the client URL stays stable even if the source
 * `projectFiles` row is later deleted or its history rewound.
 */
export interface PortalFileRelease extends PortalReleaseTrailFields {
  id?: string;
  /** Which project on the (multi-project) portal this release belongs to. */
  dealId: string;
  /** Bundle this release was published in (set on releases created after the trail shipped). */
  bundleId?: string;
  streamKind: 'two_d_pdf' | 'three_d_html';
  /** Source `projectFiles/{id}` doc this release was copied from. */
  sourceFileId: string;
  /**
   * Copies of one structure-group upload share this id (`projectFiles.structureReleaseId`).
   * The public approval trail uses it to approve the same 3D model once across lanes.
   */
  sourceStructureReleaseId?: string;
  /** Source `projectFileStreams/{id}`. */
  sourceStreamId: string;
  sourceStreamTitle: string;
  /** Source project (Project Hub `timberProjects/{id}`). */
  sourceProjectId: string;
  /** Source `projectFiles.versionIndex` (1-based, monotonic per stream). */
  versionIndex?: number;
  versionLabel?: string;
  versionNote?: string;
  /**
   * Editable client-facing display label, e.g. "Engineered Drawings" or "Foundation Plans".
   * Defaults: "Engineered Drawings" for `two_d_pdf`, "3D Model" for `three_d_html`.
   */
  displayLabel: string;
  /**
   * Per-`(dealId, streamKind)` monotonic counter — drives the `v{N}` suffix in the
   * client-visible filename. Independent of `versionIndex` because the same source
   * version can be released more than once (e.g. after an internal review pass).
   */
  releaseVersionIndex: number;
  /** Final, sanitized client-facing filename (built from convention; what the client downloads). */
  name: string;
  mimeType: string;
  size: number;
  /** Stable Storage download URL pointing at `portal-files/.../v{N}-{slug}.{ext}`. */
  releasedUrl: string;
  releasedStoragePath: string;
  releasedAt: Date;
  releasedBy: string;
  releasedByName: string;
  /** Which portal step/section this release populates. Defaults: engineered_drawings for PDF, model for HTML. */
  portalTarget?: PortalReleaseTarget;
  /**
   * Optional Loom share URL attached to this 3D model version.
   * Clients only see a walkthrough when the *current* model release has one —
   * a newer release without a Loom hides the previous video.
   */
  loomUrl?: string;
}

/**
 * Groups one or more {@link PortalFileRelease} rows published together as a single
 * client-visible version (e.g. main model + garage + detail sheet). Stored under
 * `clientPortals/{portalId}/portalReleaseBundles`. Append-only like releases.
 * Single-file releases still create a bundle with `releaseIds.length === 1` so the
 * portal can treat bundles uniformly while preserving backward-compatible UX when
 * the bundle contains only one file.
 */
export interface PortalReleaseBundle extends PortalReleaseTrailFields {
  id?: string;
  dealId: string;
  streamKind: 'two_d_pdf' | 'three_d_html';
  /** Per-(dealId, streamKind) monotonic counter — shared with contained releases. */
  releaseVersionIndex: number;
  /** Client-facing package label (defaults to stream kind label when omitted). */
  displayLabel: string;
  /** Ordered list of `portalFileReleases` doc ids in this bundle. */
  releaseIds: string[];
  /** Which release drives `projects.{dealId}.modelUrl` / `plansUrl`. */
  primaryReleaseId: string;
  releasedAt: Date;
  releasedBy: string;
  releasedByName: string;
  /** Mirrors contained releases — used to split PDF bundles between portal steps. */
  portalTarget?: PortalReleaseTarget;
}

// Pre-built timber frame kit template with multi-species pricing
export interface TimberKit {
  id: string;
  name: string;                    // "16x20 Pavilion", "24x36 Barn"
  description?: string;
  thumbnailUrl?: string;
  coverImage?: string;             // Main kit image URL

  // Core pricing - reuses same structure as proposals
  nodeSets: ProposalNodeSet[];
  profitMargin: number;            // Default 40%

  // Multi-species pre-configured pricing
  speciesPricing: {
    speciesName: string;           // "Douglas Fir", "White Oak"
    timberTotal: number;           // Materials cost for this species
    billableTotal: number;         // Full price with margin
  }[];

  // Sale/discount
  isOnSale: boolean;
  saleDiscountType?: 'percentage' | 'fixed';  // Discount type
  saleDiscount?: number;           // Value (percentage: 15 = 15% off, fixed: 500 = $500 off)
  saleEndsAt?: Date;

  // Calculated totals (base, before species-specific adjustments)
  designTotal: number;
  timberKitTotal: number;          // Production + Materials + Freight
  installTotal: number;            // Install + Travel + Overhead
  rawCostTotal: number;
  billableTotal: number;

  status: TimberKitStatus;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
}

// Kit proposal assigned to a deal - editable copy from template
export interface KitProposal {
  id: string;
  kitId: string;                   // Reference to source TimberKit
  kitName: string;                 // Snapshot of kit name
  dealId: string;

  // Copied and editable - freight/install change per lead
  nodeSets: ProposalNodeSet[];
  profitMargin: number;
  selectedSpecies?: string;        // Client's selected species

  // Calculated totals
  designTotal: number;
  timberKitTotal: number;
  installTotal: number;
  rawCostTotal: number;
  billableTotal: number;

  // Sale pricing applied at time of assignment
  saleDiscountApplied?: number;

  // Proposal status
  status: ProposalBuilderStatus;
  slug: string;                    // For public URL
  accessPin: string;               // 4-digit PIN

  // Signature data
  signature?: {
    dataUrl: string;
    signedBy: string;
    signedAt: Date | { toDate: () => Date };
  };
  signedPdfUrl?: string;

  createdAt: Date;
  updatedAt: Date;
  sentAt?: Date;
}

// ═══════════════════════════════════════════
// DESIGN QUESTIONNAIRE
// ═══════════════════════════════════════════

export type QuestionnaireStatus = 'not-started' | 'in-progress' | 'completed' | 'skipped';

export interface QuestionnaireSelection {
  categoryId: string;
  optionId: string;
  liked: boolean;
  comment?: string;
  selectedAt: Date;
}

export interface QuestionnaireSpecs {
  footprint?: string;
  roofPitch?: string;
  ceilingHeight?: string;
  floors?: string;
  notes?: string;
}

export interface QuestionnaireVideo {
  id: string;
  url: string;
  durationSeconds: number;
  uploadedAt: Date;
}

export interface DesignQuestionnaire {
  id?: string;
  portalId: string;
  leadId: string;
  clientName: string;
  selections: QuestionnaireSelection[];
  specs?: QuestionnaireSpecs;
  videos?: QuestionnaireVideo[];
  generalNotes?: string;
  inspirationImages?: Array<{ id: string; url: string; name: string; uploadedAt: Date }>;
  completedAt?: Date;
  skipped?: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export type PortalActivityType = 'page_view' | 'section_view' | 'link_click' | 'action' | 'heartbeat';

export interface PortalActivity {
  id?: string;
  portalId: string;
  projectId?: string;
  sessionId: string;
  type: PortalActivityType;
  action: string;
  metadata?: Record<string, any>;
  userAgent?: string;
  /** 'agent' rows are written by AI agents (enrichment, bidding, proposal review). */
  source?: 'client' | 'employee' | 'stakeholder' | 'agent';
  timestamp: Date;
}

/** Staff-created magic link for view-only portal access (doc id = secret token). */
export interface PortalStakeholderLink {
  id?: string;
  portalId: string;
  portalSlug: string;
  displayName: string;
  /** Optional company shown on sample-portal invites. */
  companyName?: string;
  createdAt: Date | { toDate: () => Date };
  createdBy: string;
  revokedAt?: Date | { toDate: () => Date } | null;
  expiresAt?: Date | { toDate: () => Date } | null;
  lastUsedAt?: Date | { toDate: () => Date } | null;
  /** When set, this invite was created from a CRM lead (sample proposal / GC demo). */
  leadId?: string;
  /** Invite purpose — e.g. `sample_invite` for the shared sample GC portal. */
  purpose?: 'sample_invite' | string;
}

/** Trade category for subcontractor briefing links (finishing, millwork, electrical, roofing, etc.). */
export type TradeBriefingTradeType = 'finishing' | 'millwork' | 'electrical' | 'roofing' | 'general' | 'other';

/** Staff-managed magic link for subcontractor trade briefings (doc id = secret token). */
export interface TradeBriefingLink {
  id?: string;
  dealId: string;
  timberProjectId: string;
  companyName: string;
  contactName?: string;
  tradeType: TradeBriefingTradeType;
  displayName?: string;
  accessPin?: string;
  briefingNotes?: string;
  /**
   * Project Hub `projectFiles` doc ids to expose on this briefing (specific versions).
   * Empty array = no files. Omitted on older links that used portal releases.
   */
  includedProjectFileIds?: string[];
  /** Legacy: portal `portalFileReleases` ids. Used only when `includedProjectFileIds` is absent. */
  includedReleaseIds?: string[];
  showStructureScope?: boolean;
  /** When true, the briefing lists proposal timber sizes and lengths by structure. Default off except finishing. */
  showTimberList?: boolean;
  /** When true, the briefing shows the subcontractor quote-upload form. Default off for new links. */
  includeQuoteUpload?: boolean;
  inviteEmail?: string;
  invitedUid?: string;
  inviteStatus?: 'pending' | 'accepted';
  inviteId?: string;
  /** Public https URL for the trade/supplier partnership logo. */
  partnerLogoUrl?: string;
  /** Storage path used when the agent uploaded a logo file. */
  partnerLogoStoragePath?: string;
  partnerLogoName?: string;
  createdAt: Date | { toDate: () => Date };
  createdBy: string;
  revokedAt?: Date | { toDate: () => Date } | null;
  expiresAt?: Date | { toDate: () => Date } | null;
  lastViewedAt?: Date | { toDate: () => Date };
}

/** View log for trade briefing links — written by Cloud Functions only. */
export interface TradeBriefingActivity {
  id?: string;
  token: string;
  dealId: string;
  viewedAt: Date | { toDate: () => Date };
  userAgent?: string;
}

/** Decking line on a structure scope card — sq ft only, no pricing. */
export interface TradeBriefingDeckingItem {
  id: string;
  productName?: string;
  widthInches: number;
  sqFt: number;
}

/** Timber list row without commercial fields. */
export interface TradeBriefingTimberLine {
  name: string;
  species: string;
  grade: string;
  dimensions: string;
  quantity: number;
  boardFeet: number;
  dryness?: string;
}

/** Install / lift context without dollar amounts. */
export interface TradeBriefingInstallContext {
  days?: number;
  craneRequired?: boolean;
  craneDaysNeeded?: number;
  telehandlerRequired?: boolean;
  telehandlerDaysNeeded?: number;
  scissorLiftRequired?: boolean;
  scissorLiftDaysNeeded?: number;
  basketLiftRequired?: boolean;
}

export interface TradeBriefingTravelContext {
  travelDays?: number;
  hotelNights?: number;
  crewSize?: number;
}

/** Per-species timber spec for trade briefing structure cards. */
export interface TradeBriefingSpeciesSpec {
  species: string;
  grade?: string;
  dryness?: string;
}

/** Sanitized structure summary for subcontractor briefings. */
export interface TradeBriefingStructureScope {
  id: string;
  name: string;
  quantity?: number;
  enabled?: boolean;
  images?: Array<{ url: string; name: string }>;
  notes?: string;
  speciesLabels?: string[];
  gradeLabels?: string[];
  drynessLabels?: string[];
  /** Preferred display — species with paired grade/dryness when known. */
  speciesSpecs?: TradeBriefingSpeciesSpec[];
  deckingItems?: TradeBriefingDeckingItem[];
  timberLines?: TradeBriefingTimberLine[];
  installContext?: TradeBriefingInstallContext;
  travelContext?: TradeBriefingTravelContext;
}

/** Hub PDF comment pin included on a public trade briefing (no commercial fields). */
export interface TradeBriefingPdfAnnotation {
  id: string;
  pageNumber: number;
  x: number;
  y: number;
  content: string;
  userName: string;
  /** ISO-8601 */
  createdAt?: string;
  replies?: Array<{
    userName: string;
    content: string;
    /** ISO-8601 */
    createdAt?: string;
  }>;
}

/** Portal release row exposed on the public briefing page. */
export interface TradeBriefingRelease {
  id: string;
  streamKind: 'two_d_pdf' | 'three_d_html' | 'general';
  displayLabel: string;
  name: string;
  releasedUrl: string;
  mimeType: string;
  size: number;
  portalTarget?: PortalReleaseTarget;
  releaseVersionIndex: number;
  /** Hub `projectFiles.versionLabel` when this row came from Project Hub. */
  versionLabel?: string;
  /** ISO-8601 */
  releasedAt?: string;
  /** Staff PDF pins for this hub file (addendum / 2D). */
  annotations?: TradeBriefingPdfAnnotation[];
}

/** Release bundle metadata for version history (mirrors portalReleaseBundles). */
export interface TradeBriefingReleaseBundle {
  id?: string;
  streamKind: 'two_d_pdf' | 'three_d_html' | 'general';
  releaseVersionIndex: number;
  displayLabel?: string;
  releaseIds: string[];
  primaryReleaseId: string;
  /** ISO-8601 */
  releasedAt?: string;
  portalTarget?: PortalReleaseTarget;
}

/** Payload returned by `getTradeBriefingContent` — no commercial fields. */
export interface TradeBriefingContent {
  dealId: string;
  timberProjectId?: string;
  leadId?: string;
  projectNumber?: string;
  descriptor?: string;
  projectName?: string;
  clientLabel?: string;
  siteAddress?: string;
  tradeType: TradeBriefingTradeType;
  companyName: string;
  contactName?: string;
  displayName?: string;
  briefingNotes?: string;
  /** When true, show the subcontractor quote-upload form. */
  includeQuoteUpload?: boolean;
  /** When true, structure cards include a timber size / length table. */
  showTimberList?: boolean;
  /** Project-wide confidential notice (from timberProjects). */
  confidentialBanner?: string;
  structures: TradeBriefingStructureScope[];
  releases: TradeBriefingRelease[];
  releaseBundles: TradeBriefingReleaseBundle[];
  branding?: {
    companyName?: string;
    logoUrl?: string;
    backgroundUrl?: string;
    overlayColor?: string;
    overlayOpacity?: number;
  };
  /** Trade/supplier partnership lockup on the public briefing. */
  partner?: {
    companyName: string;
    logoUrl?: string;
  };
}

// ═══════════════════════════════════════════
// FINANCES MODULE
// ═══════════════════════════════════════════
export type {
  OverheadFrequency,
  OverheadCategory,
  OverheadScalesWith,
  OverheadExpense,
  PayrollDepartment,
  PayrollStatus,
  PayrollPosition,
  PayrollSettings,
  PayType,
  BalanceSheetCache,
  CashflowMethod,
  CashflowAccountType,
  CashflowAccountLine,
  CashflowMonthlyTotals,
  CashflowMethodData,
  CashflowHistoryCache,
  EntityTaxType,
  PlannedKind,
  BonusPlanMode,
  BonusPayoutSplit,
  BonusPositionOverride,
  BonusPayoutSlice,
  BonusPlan,
  TaxPlan,
  ObligationSettings,
  ObligationSlice,
  CockpitSettings,
  OwnerDistribution,
  OwnerDrawKind,
  DebtInstrument,
} from './finances';
export {
  OVERHEAD_CATEGORIES,
  OVERHEAD_CATEGORY_LABELS,
  OVERHEAD_FREQUENCIES,
  OVERHEAD_FREQUENCY_LABELS,
  OVERHEAD_SCALES_WITH_LABELS,
  PAYROLL_DEPARTMENTS,
  PAYROLL_DEPARTMENT_LABELS,
  PAYROLL_STATUSES,
  PAYROLL_STATUS_LABELS,
  PAY_TYPE_LABELS,
  DEFAULT_PAYROLL_BURDEN_RATE,
  DEFAULT_PTO_DAYS_PER_YEAR,
  DEFAULT_SEP_IRA_RATE,
  DEFAULT_EMPLOYER_TAX_RATE,
  DEFAULT_PAYROLL_SETTINGS,
  DEFAULT_HOURS_PER_WEEK,
  DEFAULT_HOURS_PER_YEAR,
  WEEKS_PER_YEAR,
  WORK_DAYS_PER_YEAR,
  BONUS_PLAN_MODES,
  BONUS_PLAN_MODE_LABELS,
  DEFAULT_TN_EXCISE_RATE,
  DEFAULT_TN_FRANCHISE_PER_HUNDRED,
  DEFAULT_TN_FRANCHISE_MIN,
  DEFAULT_QBI_RATE,
  DEFAULT_OWNER_FEDERAL_EFFECTIVE_RATE,
  DEFAULT_BONUS_PERCENT,
  DEFAULT_OBLIGATION_SETTINGS,
  createDefaultBonusPlan,
  createDefaultTaxPlan,
  OWNER_DRAW_KINDS,
  OWNER_DRAW_KIND_LABELS,
  DEFAULT_COCKPIT_SETTINGS,
} from './finances';

// ============================================================================
// Lead Inspiration Board (Figma-style canvas, one board per lead)
// ============================================================================

export type BoardNodeType = 'image' | 'link' | 'note' | 'text' | 'group' | 'timeline' | 'floorplan';

export type BoardDrawKind =
  | 'pen'
  | 'highlighter'
  | 'line'
  | 'arrow'
  /** Frame (user-facing name). Always the bottom layer, behind nodes. */
  | 'rect'
  | 'ellipse'
  | 'callout';

export type BoardDrawTool = 'select' | BoardDrawKind | 'eraser';

export interface BoardDrawPoint {
  x: number;
  y: number;
}

export interface BoardDrawing {
  id: string;
  kind: BoardDrawKind;
  color: string;
  /** Stroke width in flow px (canvas) or image-local px (image overlays). */
  width: number;
  /** Pen/highlighter polyline; two points for line/arrow/rect/ellipse/callout. */
  points: BoardDrawPoint[];
  /** Callout label */
  text?: string;
  /** Highlighter opacity (~0.35). */
  opacity?: number;
  /** Author initials shown on callout labels (e.g. "ML"). */
  authorInitials?: string;
  authorName?: string;
  authorId?: string;
  /** Callout box fill (defaults to engineering cyan). */
  fill?: string;
  /** Stacking order, shared with board nodes. */
  zIndex?: number;
}

export interface BoardImageData {
  url: string;
  /** Storage path for deletion */
  storagePath?: string;
  naturalWidth?: number;
  naturalHeight?: number;
  alt?: string;
  caption?: string;
  /** Image-local drawings in normalized 0–1 coordinates. */
  drawings?: BoardDrawing[];
  /** Clockwise rotation in 90° steps. */
  rotation?: 0 | 90 | 180 | 270;
  /** Photo opacity from 0.05 (ghost) to 1 (solid). Missing means 1. */
  opacity?: number;
}

export interface BoardLinkData {
  url: string;
  title?: string;
  /** User-editable label; wins over OG `title` in the UI */
  displayTitle?: string;
  description?: string;
  thumbnailUrl?: string;
  favicon?: string;
  domain?: string;
  /** True while the link preview is still being fetched */
  loading?: boolean;
}

export interface BoardNoteData {
  text: string;
  /** Rich HTML content (TipTap); plain `text` kept for legacy/search fallback */
  textHtml?: string;
  /** Sticky note background color */
  color?: string;
  fontSize?: number;
  mentions?: CommentMention[];
}

export type BoardTextStyleId = 'header' | 'title' | 'subtitle' | 'body' | 'caption';

export interface BoardTextData {
  text: string;
  /** Style preset (header/title/subtitle/…). Unset = legacy free-form text. */
  textStyle?: BoardTextStyleId;
  /** Explicit size override — wins over the preset's size when set. */
  fontSize?: number;
  color?: string;
}

export interface BoardGroupData {
  label?: string;
  color?: string;
}

export interface BoardTimelineEvent {
  id: string;
  label: string;
  duration: number;
  unit: 'days' | 'weeks';
}

export interface BoardTimelineData {
  events: BoardTimelineEvent[];
  title?: string;
}

export type FloorPlanOpeningKind = 'door' | 'window' | 'garage_door';

export interface FloorPlanFootprint {
  /** Working canvas width in inches (left → right). Not the building outline. */
  widthIn: number;
  /** Working canvas depth in inches (top → bottom). Not the building outline. */
  depthIn: number;
}

export type FloorPlanWallEnd = 'start' | 'end';
export type FloorPlanConstraintKind = 'coincident' | 'perpendicular';

export interface FloorPlanWall {
  id: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  thicknessIn: number;
}

export interface FloorPlanConstraint {
  id: string;
  kind: FloorPlanConstraintKind;
  aWallId: string;
  bWallId: string;
  /** Endpoint on wall A. Required for coincident. */
  aEnd?: FloorPlanWallEnd;
  /** Endpoint on wall B. Required for coincident. */
  bEnd?: FloorPlanWallEnd;
}

export interface FloorPlanOpening {
  id: string;
  kind: FloorPlanOpeningKind;
  wallId: string;
  /** Distance along the wall from (x1, y1) to the start of the opening, in inches. */
  offsetIn: number;
  widthIn: number;
  /** Door swing when facing along the wall direction. */
  swing?: 'left' | 'right';
  /** Mirror the symbol across the wall (swing / garage door to the other side). */
  flip?: boolean;
}

export type FloorPlanStairKind = 'straight' | 'l' | 'u' | 'spiral';

export interface FloorPlanStair {
  /** Treads on the first / only flight. */
  treads?: number;
  /** Treads on the second flight (L and U). */
  landTreads?: number;
  /** Walking width of a flight. L / U only; straight uses `widthIn`. */
  runWidthIn?: number;
  /** Right = default (CW / landing on the right). Left mirrors the run. */
  turn?: 'left' | 'right';
}

export interface FloorPlanItem {
  id: string;
  catalogId: string;
  /** Top-left of the axis-aligned box after rotation, in inches. */
  x: number;
  y: number;
  /** Degrees clockwise. Furniture is usually 0 / 90 / 180 / 270. */
  rotation: number;
  widthIn: number;
  depthIn: number;
  /** Present on stair catalog items. */
  stair?: FloorPlanStair;
}

/** Inset of the visible trace, as fractions of the full (rotated) AABB. */
export interface FloorPlanUnderlayCrop {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** PDF page or 3D-model snapshot placed under the walls for tracing. */
export interface FloorPlanUnderlay {
  id: string;
  url: string;
  storagePath?: string;
  label?: string;
  /** Top-left of the unrotated image, in inches. */
  x: number;
  y: number;
  widthIn: number;
  heightIn: number;
  /** 0.05–1. Missing means the default trace fade. */
  opacity: number;
  /** Degrees clockwise. Legacy plans use 0 / 90 / 180 / 270. */
  rotation?: number;
  naturalWidth?: number;
  naturalHeight?: number;
  crop?: FloorPlanUnderlayCrop;
  /** When true, the trace can be selected but not moved, cropped, or scaled. */
  locked?: boolean;
  /** Hidden layers stay in the plan but are not drawn or hit-tested. */
  hidden?: boolean;
}

/** Tape measure dropped on the plan (model, walls, or empty sheet). */
export interface FloorPlanMeasure {
  id: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface BoardFloorPlanData {
  title?: string;
  footprint: FloorPlanFootprint;
  walls: FloorPlanWall[];
  openings: FloorPlanOpening[];
  items: FloorPlanItem[];
  underlays?: FloorPlanUnderlay[];
  constraints?: FloorPlanConstraint[];
  measures?: FloorPlanMeasure[];
}

export type BoardNodeData =
  | BoardImageData
  | BoardLinkData
  | BoardNoteData
  | BoardTextData
  | BoardGroupData
  | BoardTimelineData
  | BoardFloorPlanData;

export interface BoardNode {
  id: string;
  type: BoardNodeType;
  x: number;
  y: number;
  width?: number;
  height?: number;
  zIndex?: number;
  /** When set, position is relative to the parent group node */
  parentId?: string;
  data: BoardNodeData;
}

export interface BoardViewport {
  x: number;
  y: number;
  zoom: number;
}

export interface LeadBoard {
  dealId: string;
  nodes: BoardNode[];
  /** Canvas-level drawings in flow coordinates. */
  drawings?: BoardDrawing[];
  viewport?: BoardViewport;
  updatedAt: Date;
  updatedBy?: string;
}

/** Reply on an inspiration board element comment thread */
export interface BoardCommentReply {
  userId: string;
  userName: string;
  content: string;
  mentions?: CommentMention[];
  createdAt: Date;
}

/** One conversation thread per board node (`portalProjectBoards/{dealId}/comments/{nodeId}`) */
export interface BoardCommentThread {
  id: string;
  nodeId: string;
  content: string;
  authorUid: string;
  authorName: string;
  authorKind: 'staff' | 'client';
  mentions?: CommentMention[];
  replies: BoardCommentReply[];
  createdAt: Date;
}

// =============================================================================
// People Hub — Travel Management
// =============================================================================

export type PeopleTravelTripStatus =
  | 'draft'
  | 'planning'
  | 'searching'
  | 'ready'
  | 'booked'
  | 'completed'
  | 'cancelled';

export type PeopleTravelOptionKind = 'flight' | 'hotel' | 'rental_car';

export type PeopleTravelEventKind = 'work' | 'meeting' | 'hotel' | 'meal' | 'other';

export type PeopleTravelLegMode = 'flight' | 'drive' | 'rental_car' | 'other';

export type PeopleTravelStopKind =
  | 'origin'
  | 'destination'
  | 'flight_departure'
  | 'flight_arrival'
  | 'hotel'
  | 'rental_pickup'
  | 'rental_dropoff'
  | 'activity'
  | 'other';

export type PeopleTravelTripPurpose =
  | 'business'
  | 'conference'
  | 'client_visit'
  | 'personal'
  | 'other';

export type PeopleTravelCalendarSyncStatus =
  | 'none'
  | 'pending'
  | 'syncing'
  | 'synced'
  | 'failed';

export interface PeopleTravelDestination {
  label: string;
  placeId?: string;
  lat?: number;
  lng?: number;
}

export interface PeopleTravelStop {
  id: string;
  kind: PeopleTravelStopKind;
  label: string;
  address?: string;
  lat?: number;
  lng?: number;
  startAt?: string;
  endAt?: string;
  sortOrder: number;
  linkedOptionId?: string;
  source?: 'manual' | 'agent' | 'plan_draft';
  boardKind?: 'location' | 'event' | 'leg';
  boardId?: string;
}

export interface PeopleTravelLocation {
  id: string;
  label: string;
  kind?: string;
  address?: string;
  placeId?: string;
  lat?: number;
  lng?: number;
  sortOrder: number;
  x: number;
  y: number;
  startDate?: string;
  endDate?: string;
}

export interface PeopleTravelEvent {
  id: string;
  locationId: string;
  title: string;
  kind: PeopleTravelEventKind;
  startAt?: string;
  endAt?: string;
  notes?: string;
  x: number;
  y: number;
}

export interface PeopleTravelLeg {
  id: string;
  fromLocationId: string;
  toLocationId: string;
  mode: PeopleTravelLegMode;
  travelerUserIds?: string[];
  notes?: string;
  selectedOptionIds?: string[];
}

export interface PeopleTravelPlannerMessage {
  id: string;
  role: 'user' | 'planner';
  text: string;
  imageCount?: number;
  createdAt: number;
  createdByUserId?: string;
}

export interface PeopleTravelBudget {
  cap?: number;
  currency: 'USD';
  estimatedTotal: number;
  selectedTotal: number;
  actualSpent?: number;
}

export interface PeopleTravelTrip {
  id: string;
  title: string;
  purpose?: PeopleTravelTripPurpose;
  ownerUserId: string;
  travelerUserIds: string[];
  startDate: string;
  endDate: string;
  destinations: PeopleTravelDestination[];
  status: PeopleTravelTripStatus;
  budget: PeopleTravelBudget;
  itineraryNotes?: string;
  calendarExecutiveUid?: string;
  calendarInvitees?: string[];
  calendarEventGroupId?: string;
  calendarSyncStatus?: PeopleTravelCalendarSyncStatus;
  calendarSyncedAt?: Date;
  calendarSyncError?: string;
  createdAt: Date;
  updatedAt: Date;
  createdByUserId: string;
}

export interface PeopleTravelOption {
  id: string;
  kind: PeopleTravelOptionKind;
  source: 'manual' | 'agent';
  label: string;
  vendor?: string;
  price: number;
  currency: 'USD';
  details: Record<string, unknown>;
  deepLink?: string;
  rank?: number;
  isSelected: boolean;
  isBooked: boolean;
  searchRequestId?: string;
  createdAt: Date;
  legId?: string;
  eventId?: string;
}

export interface PeopleTravelSearchRequest {
  tripId: string;
  status: 'pending' | 'processing' | 'complete' | 'failed';
  kinds?: PeopleTravelOptionKind[];
  preferences?: Record<string, unknown>;
  requestedAt?: Date;
  requestedBy?: string;
  startedAt?: Date;
  completedAt?: Date;
  error?: string;
}

export interface PeopleTravelCalendarSyncRequest {
  tripId: string;
  status: 'pending' | 'processing' | 'complete' | 'failed';
  executiveUid: string;
  invitees: string[];
  timeZone?: string;
  anchorMode?: CalendarEventGroupAnchorMode;
  segments?: Array<Record<string, unknown>>;
  requestedAt?: Date;
  requestedBy?: string;
  startedAt?: Date;
  completedAt?: Date;
  error?: string;
}
